import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabase";
import { MODES, getStripePrice, lsSet, safeRedirect, getMedal } from "./constants";

// --- Lazy imports for code splitting -------------------------------------------
const LazyAnalyzerApp = React.lazy(() => import("./AnalyzerApp"));
const LazyProfilePage = React.lazy(() => import("./ProfilePage"));
const LazyAuthPages = React.lazy(() => import("./AuthPages").then(m => ({ default: m.SignInPage })));
const LazySignUpPage = React.lazy(() => import("./AuthPages").then(m => ({ default: m.SignUpPage })));
const LazyLegalPages = React.lazy(() => import("./LegalPages").then(m => ({ default: m.PrivacyPage })));
const LazyTermsPage = React.lazy(() => import("./LegalPages").then(m => ({ default: m.TermsPage })));
const LazySharedDealPage = React.lazy(() => import("./LegalPages").then(m => ({ default: m.SharedDealPage })));
const LazyAffiliatePage = React.lazy(() => import("./LegalPages").then(m => ({ default: m.AffiliatePage })));

// --- Global Styles -------------------------------------------------------------
function GlobalStyles() {
  return <style>{`
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

    /* ── DEALSOURCE.AI BRAND SYSTEM ──────────────────────────────────────────
       Primary BG:   #172437 / #1d2d43
       Accent:       #2563eb → #22c55e  (blue-to-green gradient)
       Text:         #f8fafc (primary) / #94a3b8 (secondary)
       Glass panels: translucent dark navy with thin blue-tinted borders
       Metrics:      green glow on positive, amber caution, red (restrained) danger
    ────────────────────────────────────────────────────────────────────────── */

    :root {
      /* Backgrounds */
      --bg:            #172437;
      --bg-page:       #172437;
      --bg-deep:       #132033;
      --bg-panel:      #172437;
      --bg-panel-2:    #23344c;
      --bg-input:      rgba(255,255,255,0.04);
      --bg-tile:       rgba(255,255,255,0.04);
      --bg-card:       rgba(29,45,67,0.9);
      --bg-glass:      rgba(35,52,76,0.75);

      /* Borders — thin, blue-tinted */
      --border-soft:   rgba(37,99,235,0.10);
      --border-mid:    rgba(37,99,235,0.16);
      --border-strong: rgba(37,99,235,0.28);
      --border-white:  rgba(255,255,255,0.06);
      --border:        rgba(37,99,235,0.12);
      --border-light:  rgba(37,99,235,0.10);

      /* Accent gradient */
      --accent-from:   #2563eb;
      --accent-to:     #22c55e;
      --accent-grad:   linear-gradient(135deg, #2563eb, #22c55e);

      /* Text */
      --text-primary:   #f8fafc;
      --text-secondary: #94a3b8;
      --text-muted:     #a8b7ca;
      --label:          #cbd5e1;

      /* Semantic — green (success / positive metrics) */
      --green:          #22c55e;
      --green-bright:   #4ade80;
      --green-soft:     rgba(34,197,94,0.10);
      --green-glow:     0 2px 10px rgba(0,0,0,0.10);
      --green-dark:     #4ade80;

      /* Semantic — blue (info / accent) */
      --blue:           #3b82f6;
      --blue-bright:    #60a5fa;
      --blue-soft:      rgba(59,130,246,0.10);

      /* Semantic — amber (caution) */
      --amber:          #f59e0b;
      --amber-soft:     rgba(245,158,11,0.10);

      /* Semantic — red (danger — restrained) */
      --red:            #ef4444;
      --red-soft:       rgba(239,68,68,0.07);

      /* Shadows */
      --shadow:         0 4px 16px rgba(0,0,0,0.50);
      --shadow-soft:    0 8px 32px rgba(0,0,0,0.40);
      --shadow-md:      0 12px 40px rgba(0,0,0,0.55);
      --shadow-tile:    0 4px 14px rgba(0,0,0,0.35);
      --shadow-panel:   0 1px 0 rgba(255,255,255,0.04) inset, 0 16px 48px rgba(0,0,0,0.45);

      /* Layout */
      --radius:         12px;
      --radius-sm:      8px;
      --radius-lg:      18px;
      --radius-xl:      22px;
      --pad-sm:         12px;
      --pad-md:         18px;
      --pad-lg:         24px;
      --font:           'IBM Plex Sans', system-ui, sans-serif;
      --mono:           'IBM Plex Sans', system-ui, sans-serif;
    }

    /* ── RESET ── */
    *  { box-sizing:border-box; margin:0; padding:0; }
    html { scroll-behavior:smooth; background:var(--bg); }
    body { font-family:var(--font); background:var(--bg); color:var(--text-primary); -webkit-font-smoothing:antialiased; }

    /* ── GLASS PANEL SYSTEM ──────────────────────────────────────────────────
       Panels use backdrop-blur + subtle inner highlight + blue-tinted border
       This creates the premium finance dashboard depth                       */
    .ds-panel {
      background: var(--bg-panel);
      border: 1px solid var(--border-mid);
      border-radius: 20px;
      box-shadow: var(--shadow-panel);
      backdrop-filter: blur(12px);
    }
    .ds-tile {
      background: rgba(255,255,255,0.03);
      border: 1px solid var(--border-soft);
      border-radius: 16px;
      box-shadow: var(--shadow-tile);
      transition: background 0.15s, border-color 0.15s, transform 0.15s;
    }
    .ds-tile:hover {
      background: rgba(255,255,255,0.045);
      border-color: var(--border-mid);
      transform: translateY(-1px);
    }

    /* ── METRIC CARDS ───────────────────────────────────────────────────────
       Clean panel with optional green glow for positive metrics             */
    .metric-bubble {
      background: rgba(255,255,255,0.03);
      border: 1px solid var(--border-soft);
      border-radius: 16px;
      padding: 16px 16px 14px;
      box-shadow: var(--shadow-tile);
    }
    .kpi-card {
      background: rgba(255,255,255,0.025);
      border: 1px solid var(--border-soft);
      border-radius: 18px;
      padding: 18px 16px;
      min-height: 110px;
      box-shadow: var(--shadow-tile);
      transition: transform 0.15s, border-color 0.15s, background 0.15s;
    }
    .kpi-card:hover {
      transform: translateY(-1px);
      border-color: var(--border-mid);
      background: rgba(255,255,255,0.04);
    }
    /* Positive metric glow — add class .kpi-positive */
    .kpi-positive {
      border-color: rgba(34,197,94,0.18) !important;
      box-shadow: var(--green-glow), var(--shadow-tile) !important;
    }

    /* ── DEEP DIVE ROWS ─────────────────────────────────────────────────────*/
    .deep-dive-row {
      background: rgba(255,255,255,0.025);
      border: 1px solid var(--border-soft);
      border-radius: 18px;
      overflow: hidden;
      transition: all 0.18s;
      margin-bottom: 0;
    }
    .deep-dive-row:hover { background:rgba(255,255,255,0.035); border-color:var(--border-mid); }
    .deep-dive-row[data-open="true"] { background:rgba(255,255,255,0.035); border-color:var(--border-mid); }
    .deep-dive-row__header { padding:18px 22px; width:100%; display:flex; align-items:center; justify-content:space-between; background:none; border:none; cursor:pointer; text-align:left; }
    .deep-dive-row__content { border-top:1px solid var(--border-soft); padding:24px 22px 28px; background:rgba(0,0,0,0.15); }

    /* ── SECTION ANCHORS ────────────────────────────────────────────────────*/
    .section-anchor { display:flex; align-items:center; gap:10px; margin-bottom:16px; }
    .section-anchor::before { content:''; display:block; width:3px; height:16px; border-radius:2px; background:var(--accent-grad); flex-shrink:0; }
    .section-anchor-text { font-size:11px; font-weight:700; color:var(--text-secondary); text-transform:uppercase; letter-spacing:0.09em; }
    .section-divider { height:1px; background:var(--border-soft); margin:8px 0 20px; }

    /* ── RESULTS PANEL ──────────────────────────────────────────────────────*/
    .results-panel {
      background: linear-gradient(160deg, rgba(29,45,67,0.98), rgba(23,36,55,0.98));
      border: 1px solid var(--border-mid);
      border-radius: 24px;
      padding: 24px;
      box-shadow: var(--shadow-md);
    }
    .results-panel__divider { height:1px; background:var(--border-soft); margin:18px 0; }
    .results-panel__section {
      background: rgba(255,255,255,0.025);
      border: 1px solid var(--border-soft);
      border-radius: 16px;
      padding: var(--pad-md);
    }

    /* ── INPUTS ─────────────────────────────────────────────────────────────*/
    input[type=number], input[type=text], select {
      background: rgba(255,255,255,0.04) !important;
      border: 1px solid var(--border-mid) !important;
      color: var(--text-primary) !important;
      border-radius: 8px;
    }
    input[type=number]:focus, input[type=text]:focus, select:focus {
      border-color: rgba(34,197,94,0.5) !important;
      box-shadow: 0 0 0 2px rgba(34,197,94,0.14) !important;
      outline: none !important;
    }

    /* ── RANGE SLIDER ───────────────────────────────────────────────────────*/
    input[type=range] { -webkit-appearance:none; appearance:none; height:4px; border-radius:4px; background:rgba(255,255,255,0.08); }
    input[type=range]::-webkit-slider-thumb { -webkit-appearance:none; width:18px; height:18px; border-radius:50%; background: var(--green); cursor:pointer; box-shadow:0 0 0 4px rgba(34,197,94,0.15); transition:box-shadow 0.15s; }
    input[type=range]::-webkit-slider-thumb:hover { box-shadow:0 0 0 6px rgba(34,197,94,0.22); }
    input[type=number]::-webkit-inner-spin-button { -webkit-appearance:none; }
    input, select, textarea { color-scheme:dark; }

    /* ── SCROLLBAR ──────────────────────────────────────────────────────────*/
    ::-webkit-scrollbar { width:4px; }
    ::-webkit-scrollbar-thumb { background:rgba(37,99,235,0.25); border-radius:4px; }
    ::-webkit-scrollbar-thumb:hover { background:rgba(37,99,235,0.4); }

    /* ── ACCENT GRADIENT UTILITIES ──────────────────────────────────────────*/
    .grad-text {
      background: var(--accent-grad);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .grad-border {
      border-image: var(--accent-grad) 1;
    }

    /* ── BOTTOM NAV ─────────────────────────────────────────────────────────*/
    .show-mobile { display:none; }
    .bottom-nav {
      display: none;
      position: fixed;
      bottom: 0; left: 0; right: 0;
      background: rgba(19,32,51,0.96);
      border-top: 1px solid var(--border-mid);
      z-index: 200;
      padding: 6px 0 env(safe-area-inset-bottom, 6px);
      box-shadow: 0 -8px 32px rgba(0,0,0,0.6);
      backdrop-filter: blur(16px);
      min-height: 56px;
    }
    @media(max-width:380px){
      .bottom-nav button span:last-child{display:none!important;}
      .bottom-nav button{padding:6px 1px!important;}
      .bottom-nav button span:first-of-type{font-size:15px!important;}
      .bottom-nav button{padding:8px 0!important;}
      .bottom-nav-leaderboard,.bottom-nav-mentors,.bottom-nav-pros{display:none!important;}
      /* SP-2: mode selector — horizontal scroll at 380px so all 7 pills stay on one line */
      .mode-selector{display:flex!important;grid-template-columns:unset!important;flex-wrap:nowrap!important;overflow-x:auto!important;-webkit-overflow-scrolling:touch!important;padding-bottom:4px!important;}
      .mode-selector>button{flex:0 0 auto!important;min-width:84px!important;}
    }
    @media(max-width:320px){
      /* SP-3: Portfolio label clips at 320px — hide text, show icon only */
      .bottom-nav button span:last-child{display:none!important;}
    }

    /* ── KEYFRAMES ──────────────────────────────────────────────────────────*/
    @keyframes scoreRingIn{from{stroke-dashoffset:264;}to{stroke-dashoffset:var(--ring-offset,0);}}
    @keyframes scoreNumIn{from{opacity:0;transform:scale(0.7);}to{opacity:1;transform:scale(1);}}
    @keyframes tabFadeIn{from{opacity:0;}to{opacity:1;}}
    @keyframes popIn    { from{opacity:0;transform:scale(0.96);}  to{opacity:1;transform:scale(1);} }
    @keyframes fadeUp   { from{opacity:0;} to{opacity:1;} }
    @keyframes spin     { from{transform:rotate(0deg);}  to{transform:rotate(360deg);} }
    @keyframes pulse    { 0%,100%{opacity:1;} 50%{opacity:0.45;} }
    @keyframes pulsePurple {
      0%,100%{box-shadow:0 0 0 0 rgba(167,139,250,0.4);}
      50%{box-shadow:0 0 0 6px rgba(167,139,250,0);}
    }
    @keyframes pulseGreen {
      0%,100%{box-shadow:0 0 8px rgba(34,197,94,0.3);}
      50%{box-shadow:0 0 20px rgba(34,197,94,0.55);}
    }
    @keyframes savePulse {
      0%{box-shadow:0 6px 20px rgba(34,197,94,0.35);transform:scale(1);}
      40%{box-shadow:0 0 0 8px rgba(34,197,94,0.18),0 6px 24px rgba(34,197,94,0.55);transform:scale(1.06);}
      100%{box-shadow:0 6px 20px rgba(34,197,94,0.35);transform:scale(1);}
    }
    @keyframes inputGlow {
      0%{box-shadow:0 0 0 3px rgba(34,197,94,0.35);}
      100%{box-shadow:0 0 0 0 rgba(34,197,94,0);}
    }
    .fadein { animation:fadeUp 0.3s ease both; }
    /* Action buttons: inline in score panel, no floating */
    .action-cluster{
      display:flex!important;
      flex-direction:column!important;
      gap:6px!important;
      padding:0!important;
      background:none!important;
      border:none!important;
    }

    /* ── RESPONSIVE ─────────────────────────────────────────────────────────*/
    @media(max-width:640px){
      /* Landing page How It Works — 2 col on mobile */
      .lp-steps-grid{grid-template-columns:1fr 1fr!important;}
      /* Portfolio property cards — stack on mobile */
      .lp-portfolio-cards{flex-direction:column!important;overflow:visible!important;}
      .lp-portfolio-card{min-width:unset!important;width:100%!important;}
      /* Landing page section padding reduction */
      .lp-section-pad{padding:60px 16px!important;}
      /* Hide connecting dots between steps on mobile */
      .lp-step-dot{display:none!important;}
      /* Portfolio inner layout - stack on mobile */
      .lp-portfolio-inner{grid-template-columns:1fr!important;gap:12!important;}
      /* KPI metrics grid - 2 col on mobile */
      .lp-portfolio-kpis{grid-template-columns:1fr 1fr!important;}
      /* Score panel — remove fixed min-width on mobile */
      .lp-portfolio-inner{padding:14px!important;}
      .lp-score-panel{min-width:unset!important;padding:14px 16px!important;}
      /* Rate lock card font size */
      .lp-rate-lock{font-size:9px!important;}
      /* Compare table mobile */
      .lp-compare-table{min-width:unset!important;}
      .lp-compare-table th,.lp-compare-table td{padding:10px 8px!important;font-size:11px!important;}
      .lp-compare-table th:first-child,.lp-compare-table td:first-child{font-size:11px!important;width:40%!important;}
      /* Property list cards compact */
      .lp-prop-card{padding:6px 8px!important;font-size:8px!important;}
      /* PortfolioAnalyzer table scroll */
      .portfolio-table{min-width:unset!important;font-size:10px!important;}
      /* AffiliatePage 4-col stats → 2x2 */
      .lp-4col{grid-template-columns:1fr 1fr!important;}
      /* WholesaleCalc 5-col metric grid → 3+2 on mobile */
      .ws-metric-grid{grid-template-columns:repeat(3,1fr)!important;}
      /* Metric card value font size */
      .ws-metric-grid>div>div:last-child{font-size:13px!important;}
      /* Section heading reduce padding */
      .lp-port-section{padding:64px 16px!important;}
      /* Step cards — remove right border removal since we go 2-col */
      .lp-step-card{border-right:1px solid rgba(255,255,255,0.06)!important;border-bottom:none;}
      /* ── CALCULATOR MOBILE FIXES ── */
      /* 4-col KPI metrics → 2x2 on mobile */
      .calc-kpi-grid{grid-template-columns:1fr 1fr!important;}
      /* 7-col projection table → scrollable on mobile */
      .calc-proj-row{font-size:10px!important;padding:6px 10px!important;overflow-x:auto;}
      /* Flip stress 4-col → 2x2 */
      .calc-stress-4{grid-template-columns:1fr 1fr!important;}
      /* Wholesale stress 4-col → scrollable */
      .ws-stress-grid{font-size:10px!important;overflow-x:auto;}
      /* SubTo 4-col loan → 2x2 */
      .subto-4col{grid-template-columns:1fr 1fr!important;}
      /* Novation 5-col scenario → scrollable */
      .nov-scenario-row{font-size:10px!important;overflow-x:auto;}
      /* Novation 4-col → already covered by calc-kpi-grid */
      /* Tighten step card padding */
      .lp-step-card{padding:24px 20px!important;}
      /* Property cards strip — 1 col on mobile */
      .lp-prop-strip{grid-template-columns:1fr!important;padding:0 14px 16px!important;}
      /* WholesaleCalc hero 3-col → 1 col on mobile */
      .ws-hero-grid{grid-template-columns:1fr!important;}
      /* WholesaleCalc inputs 3-col → 1fr 1fr on mobile */
      .ws-inputs-grid{grid-template-columns:1fr 1fr!important;}
      /* WholesaleCalc snap 5-col → 3-col+wrap on mobile */
      .ws-snap-grid{grid-template-columns:repeat(3,1fr)!important;}
            .rc-grid-3{grid-template-columns:1fr 1fr!important;}
      .rc-grid-4{grid-template-columns:1fr 1fr!important;}
      .rc-grid-2{grid-template-columns:1fr!important;}
      .rc-score-row{flex-direction:column!important;align-items:stretch!important;}
      .rc-score-circle-col{flex-direction:row!important;align-items:center!important;justify-content:space-between!important;}
      .rc-verdict-main{font-size:22px!important;}
      .rc-metric-val{font-size:18px!important;}
      .rc-hide-mobile{display:none!important;}
      .rc-deep-dive{padding:0!important;}
      .results-panel{padding:14px 12px!important;}
      .deep-dive-row__header{padding:14px!important;}
      .deep-dive-row__content{padding:16px 14px 20px!important;}
      .metric-bubble{padding:12px 10px!important;}
    }
    @media(max-width:768px){
      input,select,textarea{font-size:16px!important;}
      .bottom-nav button{min-height:44px;}
      .nf-wrapper{min-height:44px!important;}
      .field-wrapper{min-height:44px!important;}
      .nf-wrapper input,.field-wrapper input{padding:12px!important;}
    }
    @media(max-width:768px){
      .action-cluster button{padding:8px 12px!important;font-size:11px!important;}
      .offer-letter-modal{max-height:95vh!important;border-radius:12px!important;}
      .offer-letter-modal textarea{min-height:200px!important;font-size:14px!important;}
      .portfolio-props-grid{grid-template-columns:1fr!important;}
      .portfolio-score-strip{overflow-x:auto!important;-webkit-overflow-scrolling:touch!important;gap:10px!important;}
      .portfolio-command-grid{grid-template-columns:1fr!important;}
      .bloomberg-chart{overflow-x:auto!important;-webkit-overflow-scrolling:touch!important;}
      .bloomberg-chart > *{min-width:520px;}
      .hide-mobile{display:none!important;}
      .show-mobile{display:flex!important;}
      .calc-grid{grid-template-columns:1fr!important;}
      .calc-two-col{grid-template-columns:1fr!important;display:flex!important;flex-direction:column-reverse!important;}
      .calc-sticky-panel{position:sticky!important;top:56px!important;order:-1!important;z-index:50!important;max-height:28vh!important;overflow-y:auto!important;}
      .calc-sticky-panel .results-panel{max-height:none!important;overflow-y:auto!important;}
      .rc-hide-on-mobile-panel{display:none!important;}
      .mode-pills{gap:6px!important;flex-wrap:wrap!important;}
      .mode-pill-sub{display:none!important;}
      /* Mode selector — ensure 3-per-row layout on mobile */
      .mode-selector{display:grid!important;grid-template-columns:1fr 1fr 1fr!important;}
      /* Welcome banner — stack on mobile */
      .welcome-banner{flex-direction:column!important;gap:12px!important;}
      .welcome-banner-btns{flex-direction:row!important;width:100%!important;}
      .welcome-banner-btns button{flex:1!important;text-align:center!important;}
      .top-nav-tabs{display:none!important;}
      .main-content{padding:16px 12px!important;}
      .calc-pad{padding:14px 12px 100px!important;}
      .auth-split{grid-template-columns:1fr!important;}
      .auth-left{display:none!important;}
      .auth-right{padding:28px 20px!important;}
      .deal-cards-grid{grid-template-columns:1fr!important;}
      .profile-tabs{overflow-x:auto!important;flex-wrap:nowrap!important;}
      .profile-tabs button{white-space:nowrap;flex-shrink:0;}
      .decision-hero{padding:16px 14px!important;}
      .mentoring-grid{grid-template-columns:1fr!important;gap:32px!important;}
      .field-grid-2{grid-template-columns:1fr!important;}
      .results-grid{grid-template-columns:1fr!important;}
      .landing-padding{padding:60px 20px!important;}
      .header-profile-name{display:none!important;}
      .header-save-flash{display:none!important;}
      .header-portfolio-flash{display:none!important;}
      .main-content{padding-bottom:90px!important;}
      body{-webkit-text-size-adjust:100%;}
      .bottom-nav{display:flex!important;}
    }

    /* Shared interaction and responsive rules for the authenticated experience. */
    .ds-app button:focus-visible,.ds-account button:focus-visible,.ds-app input:focus-visible,.ds-account input:focus-visible,.ds-app select:focus-visible,.ds-account select:focus-visible,.ds-app textarea:focus-visible,.ds-account textarea:focus-visible,.explore-menu summary:focus-visible { outline:2px solid #60a5fa!important; outline-offset:3px; }
    .ds-app input,.ds-app select,.ds-app textarea,.ds-account input,.ds-account select,.ds-account textarea { box-sizing:border-box; min-width:0; }
    .ds-app select option,.ds-account select option { background:#23344c; color:#f8fafc; }
    .top-nav-tabs button { min-height:36px; }
    .ds-account-content > div { max-width:100%; box-sizing:border-box; }
    @media(max-width:768px) {
      .ds-section,.ds-account-content { padding:24px 16px 100px!important; }
      .ds-community div[style*="grid-template-columns"],.ds-account-content div[style*="grid-template-columns"] { grid-template-columns:1fr!important; }
      .ds-account-content > div { padding:20px 16px!important; }
      .ds-account-content textarea { width:100%; }
      .ds-account-content .profile-tabs { padding:4px!important; }
      .ds-app .calc-two-col { flex-direction:column!important; }
      .ds-app .calc-sticky-panel { position:static!important; max-height:none!important; overflow:visible!important; }
      .bottom-nav button span:last-child { display:block!important; font-size:10px!important; }
      .bottom-nav button { min-height:48px; }
      .bottom-nav { padding-bottom:max(6px,env(safe-area-inset-bottom)); }
      .explore-menu summary { min-height:36px; display:flex; align-items:center; box-sizing:border-box; }
    }
    @media(prefers-reduced-motion:reduce) {
      .ds-app *,.ds-account * { animation:none!important; transition:none!important; scroll-behavior:auto!important; }
    }
  
    /* Softer interface: readable type, restrained effects, predictable controls. */
    body { line-height:1.5; font-variant-numeric:tabular-nums; }
    button, input, select, textarea { font-family:var(--font); }
    button { touch-action:manipulation; }
    .lp-score-pulse, .lp-add-portfolio-pulse { animation:none!important; }
    .lp-glow { opacity:0.25; }
    .lp-btn-primary { box-shadow:0 4px 12px rgba(0,0,0,0.16)!important; }
    .ds-panel, .results-panel { box-shadow:0 8px 24px rgba(0,0,0,0.16); }
    .ds-app label, .ds-account label { line-height:1.5; }
    .ds-app input, .ds-account input, .auth-right input { min-height:44px; }
    .ds-app button, .ds-account button { max-width:100%; }
    @media(prefers-reduced-motion:reduce) {
      .lp-fade,.lp-reveal,.lp-input-anim,.lp-chart-area,.lp-portfolio-panel { opacity:1!important;transform:none!important;animation:none!important;transition:none!important; }
      .lp-ticker { animation:none!important; }
    }

    /* Analysis workspace: consistent surfaces and readable controls. */
    .ds-app { --border-mid:rgba(148,163,184,0.22); --text-secondary:#b4c3d5; }
    .ds-app .main-content { max-width:1320px!important; }
    .ds-app .analysis-shell { background:#203047!important;border-color:#40536d!important;box-shadow:0 12px 32px rgba(0,0,0,0.12)!important; }
    .ds-app .analysis-header { background:#1c2b40!important;padding:20px 24px!important; }
    .ds-app .analysis-header h2 { font-size:22px!important;margin:0 0 4px!important;letter-spacing:-0.025em; }
    .ds-app .analysis-header p { font-size:14px!important;line-height:1.6;max-width:720px; }
    .ds-app .calc-pad { padding:24px!important; }
    .ds-app .calc-two-col { gap:24px!important; }
    .ds-app .calc-two-col > div { min-width:0; }
    .ds-app .input-section { background:#1b2a3e!important;border-color:#40516a!important;box-shadow:0 2px 8px rgba(0,0,0,0.06); }
    .ds-app .input-section > button { padding:16px 18px!important;min-height:52px;border-bottom-color:#40516a!important; }
    .ds-app .input-section-title { font-size:13px!important;text-transform:none!important;letter-spacing:0!important; }
    .ds-app .input-section-badge { font-size:12px!important;color:#b4c3d5!important;white-space:normal; }
    .ds-app .results-panel { background:#1b2a3e!important;border-color:#40536d!important;border-radius:18px!important; }
    .ds-app .result-anchors { padding:16px 10px!important;gap:10px!important;background:#263a53!important; }
    .ds-app .result-anchor-label { font-size:11px!important;text-transform:none!important;letter-spacing:0!important; }
    .ds-app .result-anchor-value { font-size:22px!important;letter-spacing:-0.02em; }
    .ds-app .mode-selector { gap:8px!important;margin-bottom:20px!important; }
    .ds-app .mode-selector > button { min-height:48px;border-radius:10px!important;padding:10px 14px!important; }
    .ds-app .mode-selector > button[aria-pressed="true"] { background:#29465a!important; }
    .ds-app .nf-control { background:#293b51!important;border-color:#52657f!important;box-shadow:none!important; }
    .ds-app .nf-control:focus-within { border-color:#60a5fa!important;box-shadow:0 0 0 3px rgba(96,165,250,0.16)!important; }
    .ds-app .nf-wrapper input { font-size:18px!important; }
    .ds-app .welcome-banner { background:#21344a!important;border-color:#40536d!important; }
    .ds-app .ds-section h1,.ds-app .ds-section h2 { letter-spacing:-0.025em; }
    .ds-app .ds-section p { line-height:1.65; }
    .ds-app .ds-section button { min-height:40px; }
    @media(max-width:1024px) {
      .ds-app .calc-two-col { display:flex!important;flex-direction:column!important; }
      .ds-app .calc-two-col > div { width:100%!important; }
      .ds-app .calc-sticky-panel { position:static!important;max-height:none!important; }
    }
    @media(max-width:768px) {
      .ds-app .main-content { padding:20px 12px 90px!important; }
      .ds-app .analysis-header { padding:18px 16px!important; }
      .ds-app .analysis-header h2 { font-size:20px!important; }
      .ds-app .calc-pad { padding:16px!important; }
      .ds-app .results-panel { padding:18px!important; }
      .ds-app .input-section > button > div { flex-wrap:wrap;min-width:0; }
      .ds-app .input-section-badge { max-width:100%; }
      .ds-app .mode-selector { flex-wrap:nowrap!important;overflow-x:auto;scrollbar-width:thin;padding-bottom:6px; }
      .ds-app .mode-selector > button { flex-shrink:0; }
      .ds-app .result-anchor-value { font-size:20px!important; }
      .ds-app .welcome-banner { padding:16px 44px 16px 16px!important; }
    }

    `}</style>;
}


function DealSourceIcon({size=32}) {
  const uid = "dsi" + size;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={uid+"G"} x1="0" y1="100" x2="100" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#2563eb"/>
          <stop offset="100%" stopColor="#22c55e"/>
        </linearGradient>
      </defs>
      <rect width="100" height="100" rx="22" fill="#172437"/>
      {/* Clean D letterform */}
      <path d="M 28 18 L 28 82 L 52 82 C 72 82 82 68 82 50 C 82 32 72 18 52 18 Z"
        fill="none" stroke={`url(#${uid}G)`} strokeWidth="7" strokeLinejoin="round"/>
      {/* Minimal trend line inside D — single clean upward path */}
      <path d="M 38 62 L 48 52 L 56 56 L 68 38"
        fill="none" stroke={`url(#${uid}G)`}
        strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Dot at peak */}
      <circle cx="68" cy="38" r="3.5" fill="#22c55e"/>
    </svg>
  );
}


function Logo({ onClick, size="md", dark=false }) {
  const iconSz = size==="lg" ? 40 : size==="xl" ? 56 : 32;
  const textSz = size==="lg" ? 22 : size==="xl" ? 30 : 17;
  const textColor = dark ? "#f8fafc" : "rgba(29,45,67,0.95)";
  return (
    <div onClick={onClick} style={{display:"flex",alignItems:"center",gap:10,cursor:onClick?"pointer":"default"}}> 
      <DealSourceIcon size={iconSz}/>
      <span style={{fontFamily:"IBM Plex Sans,sans-serif",fontWeight:800,fontSize:textSz,
        color:textColor,letterSpacing:"-0.025em"}}>
        DealSource<span style={{background:"linear-gradient(135deg,#2563eb,#22c55e)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text"}}>.ai</span>
      </span>
    </div>
  );
}


function Input({label,type="text",value,onChange,placeholder,icon,error,hint,disabled,autoComplete}) {
  const [focused,setFocused]=useState(false);
  const [show,setShow]=useState(false);
  const isPass=type==="password";
  return (
    <div style={{display:"flex",flexDirection:"column",gap:5}}>
      {label&&<label style={{fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.85)"}}>{label}</label>}
      <div style={{position:"relative"}}>
        {icon&&<span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:16,pointerEvents:"none"}}>{icon}</span>}
        <input type={isPass&&show?"text":type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} disabled={disabled}
          autoComplete={autoComplete}
          onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)}
          style={{width:"100%",padding:`11px ${isPass?"42px":"14px"} 11px ${icon?"40px":"14px"}`,borderRadius:10,border:("1.5px solid "+(error?"rgba(239,68,68,0.3)":focused?"#22c55e":"rgba(255,255,255,0.1)")),background:disabled?"rgba(255,255,255,0.03)":error?"rgba(239,68,68,0.08)":"rgba(255,255,255,0.06)",fontSize:14,color:"rgba(255,255,255,0.85)",outline:"none",transition:"border-color 0.15s",fontFamily:"IBM Plex Sans,system-ui,sans-serif"}}/>
        {isPass&&<button type="button" onClick={()=>setShow(s=>!s)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:14,color:"rgba(255,255,255,0.68)"}}>{show?"":""}</button>}
      </div>
      {error&&<p style={{fontSize:12,color:"#ef4444",margin:0}}>{error}</p>}
      {hint&&!error&&<p style={{fontSize:12,color:"rgba(255,255,255,0.68)",margin:0}}>{hint}</p>}
    </div>
  );
}
function Sel({label,value,onChange,options,placeholder}) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:5}}>
      {label&&<label style={{fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.85)"}}>{label}</label>}
      <select value={value} onChange={e=>onChange(e.target.value)} style={{padding:"11px 14px",borderRadius:10,border:"1px solid rgba(37,99,235,0.16)",background:"#263a52",fontSize:14,color:value?"#f8fafc":"rgba(255,255,255,0.4)",outline:"none",fontFamily:"IBM Plex Sans,system-ui,sans-serif",cursor:"pointer",width:"100%"}}>
        <option value="" style={{background:"#263a52",color:"rgba(255,255,255,0.68)"}}>{placeholder||"Select..."}</option>
        {options.map(o=><option key={o} value={o} style={{background:"#263a52",color:"#f8fafc"}}>{o}</option>)}
      </select>
    </div>
  );
}
function Btn({children,onClick,variant="primary",loading,disabled,fullWidth,small,type="button"}) {
  const S={primary:{background:"#22c55e",color:"white",border:"none"},secondary:{background:"rgba(255,255,255,0.07)",color:"rgba(255,255,255,0.85)",border:"1px solid rgba(37,99,235,0.18)"},dark:{background:"rgba(29,45,67,0.95)",color:"#f8fafc",border:"1px solid rgba(37,99,235,0.18)"},ghost:{background:"transparent",color:"rgba(255,255,255,0.55)",border:"1px solid rgba(37,99,235,0.16)"},danger:{background:"#ef4444",color:"white",border:"none"},teal:{background:"#0d9488",color:"white",border:"none"}};
  return <button type={type} onClick={onClick} disabled={disabled||loading} style={{...S[variant],padding:small?"7px 14px":"12px 24px",borderRadius:small?7:10,fontSize:small?12:14,fontWeight:700,cursor:disabled||loading?"default":"pointer",width:fullWidth?"100%":"auto",opacity:disabled?0.5:1,transition:"all 0.15s",display:"flex",alignItems:"center",justifyContent:"center",gap:8,whiteSpace:"nowrap"}}>{loading?<span style={{animation:"spin 0.8s linear infinite",display:"inline-block"}}></span>:children}</button>;
}
function Alert({type,children}) {
  const s=type==="error"
    ?{bg:"rgba(239,68,68,0.08)",border:"rgba(239,68,68,0.25)",color:"#f87171",icon:"✕"}
    :type==="info"
    ?{bg:"rgba(37,99,235,0.10)",border:"rgba(37,99,235,0.22)",color:"#60a5fa",icon:"ℹ"}
    :{bg:"rgba(34,197,94,0.10)",border:"rgba(34,197,94,0.22)",color:"#4ade80",icon:"✓"};
  return <div style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 14px",background:s.bg,border:("1.5px solid "+(s.border)),borderRadius:10}}><span style={{fontSize:14}}>{s.icon}</span><p style={{fontSize:13,color:s.color,lineHeight:1.5,margin:0}}>{children}</p></div>;
}
function Toggle({value,onChange,label}) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:10}}>
      <div onClick={()=>onChange(!value)} role="switch" aria-checked={value} tabIndex={0} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&onChange(!value)} style={{width:44,height:24,borderRadius:12,background:value?"#22c55e":"rgba(255,255,255,0.15)",cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0,outline:"none"}}>
        <div style={{width:18,height:18,borderRadius:"50%",background:"white",position:"absolute",top:3,left:value?23:3,transition:"left 0.2s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
      </div>
      {label&&<span style={{fontSize:13,color:"rgba(255,255,255,0.85)",fontWeight:500}}>{label}</span>}
    </div>
  );
}
function MedalBadge({profile,size="sm"}) {
  const medal=getMedal(+(profile?.portfolio_value||0));
  const verified=profile?.is_verified;
  return (
    <div style={{display:"flex",alignItems:"center",gap:4}}>
      <span style={{fontSize:size==="sm"?14:18}}>{medal.icon}</span>
      {verified&&<span style={{fontSize:size==="sm"?10:12,background:"rgba(34,197,94,0.12)",color:"#4ade80",border:"1px solid rgba(34,197,94,0.2)",borderRadius:100,padding:"1px 6px",fontWeight:700}}>v Verified</span>}
    </div>
  );
}

// --- Auth Layout ---------------------------------------------------------------
function AuthLayout({children,title,subtitle}) {
  return (
    <div className="auth-split" style={{minHeight:"100vh",display:"grid",gridTemplateColumns:"1fr 1fr",background:"#172437"}}>
      <div className="auth-left" style={{background:"linear-gradient(160deg,#172437 0%,#1d2d43 50%,#172437 100%)",display:"flex",flexDirection:"column",justifyContent:"space-between",padding:48,position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",inset:0,backgroundImage:"radial-gradient(circle at 20% 80%,rgba(16,185,129,0.15) 0%,transparent 50%)",pointerEvents:"none"}}/>
        <Logo size="md" dark={true}/>
        <div>
          <h2 style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:"clamp(28px,3vw,42px)",fontWeight:900,color:"white",lineHeight:1.2,marginBottom:20}}>
            Underwrite deals with confidence.
          </h2>
          <p style={{fontSize:15,color:"rgba(255,255,255,0.65)",lineHeight:1.7,marginBottom:40,maxWidth:340}}>Know if a deal works — and if it actually improves your portfolio. Live comps, deal scoring, portfolio intelligence, and offer letters — all free to start.</p>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {[["🧮","Analyze in 60s","Know if a deal works before you make the offer"],["📊","Portfolio Impact","See how every deal changes your CF, DSCR, and equity"],["🔧","Fix Deals","Sliders show what to change to flip a Pass to a Buy"],["🛡","Avoid Traps","DSCR flags, rate lock alerts, and CF warnings before you commit"]].map(([icon,t,sub])=>(
              <div key={t} style={{background:"rgba(255,255,255,0.07)",borderRadius:12,padding:"14px 16px",border:"1px solid rgba(255,255,255,0.1)"}}>
                <div style={{fontSize:20,marginBottom:6}}>{icon}</div>
                <div style={{fontSize:13,fontWeight:700,color:"white"}}>{t}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:2}}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
        <p style={{fontSize:12,color:"rgba(255,255,255,0.68)"}}>© 2026 DealSource.ai</p>
      </div>
      <div className="auth-right" style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"48px 56px",overflowY:"auto"}}>
        <div style={{width:"100%",maxWidth:420}}>
          <div style={{marginBottom:32}}>
            <h1 style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:28,fontWeight:800,color:"rgba(255,255,255,0.85)",marginBottom:6}}>{title}</h1>
            <p style={{fontSize:14,color:"rgba(255,255,255,0.55)",lineHeight:1.6}}>{subtitle}</p>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}


// --- Demo Data -----------------------------------------------------------------
// -- DEMO data  -  all numbers mathematically verified ---------------------------
// Rental:   $250K . 25%dn . 7.0% . $2,400 rent   $503/mo CF, 8.7% CoC, 1.40x DSCR
// Wholesale: ARV $240K . $28K repairs . $9K fee    $131K MAO, $9K fee
// Flip:      $175K . $40K rehab . $290K ARV . 6mo  $61K profit, 21% margin
// BRRRR:     $120K . $30K rehab . $200K ARV . 75%  $2K left, 98.7% recycled
// SubTo:     $175K @ 3.5% vs 7.5% market rate      $545/mo CF, 3.5 cap eff
// Novation:  $295K ARV . $210K seller . $12K reh   $61.5K profit, 20.8%
const DEMO={
  rental:{
    inputs:[["Purchase Price","$250,000"],["Down Payment","25% ($62,500)"],["Monthly Rent","$2,400"],["Rate / Term","7.0% . 30yr"]],
    extraInputs:[["Closing Costs","$4,200"],["Vacancy","8%"],["Insurance","$125/mo"],["Maintenance","5%"]],
    highlights:[["Cash Flow","$503/mo",true],["CoC ROI","8.7%",true],["Cap Rate","8.4%",false],["DSCR","1.40x",false]],
    formula:"$2,400 rent - $650 expenses - $1,247 mortgage = $503/mo",
    verdict:"Strong Buy",score:84,
    pillars:[{l:"Cash Flow",s:21,t:25,c:"#22c55e",d:"$503/mo — 3 of 3 stress tests survive"},{l:"CoC Return",s:19,t:25,c:"#22c55e",d:"8.7% — above 7% target"},{l:"DSCR",s:22,t:25,c:"#4ade80",d:"1.40x — lender safe zone (≥1.25x)",dom:true},{l:"Cap Rate",s:22,t:25,c:"#22c55e",d:"8.4% — above 8% strong threshold"}],
    stress:[["3-mo Vacancy","+$1,509 gap",false],["Rate +1%","$406/mo CF",true],["Exp +20%","$373/mo CF",true]],
    stressNote:"Survives 2 of 3 stress scenarios  -  solid downside protection",
    address:"1423 Maple Dr, Hartford CT 06112",
    chartLabel:"30-Year Wealth Projection",
    chartType:"wealth",
    chartData:[
      {x:"Y1", equity:69, value:258, cumCF:6},
      {x:"Y5", equity:120, value:290, cumCF:30},
      {x:"Y10",equity:197, value:336, cumCF:72},
      {x:"Y15",equity:290, value:389, cumCF:130},
      {x:"Y20",equity:405, value:450, cumCF:210},
      {x:"Y25",equity:520, value:520, cumCF:310},
      {x:"Y30",equity:690, value:600, cumCF:440},
    ],
    chartMeta:{tenYEquity:"$197K",thirtyYValue:"$600K",capitalRecovered:"Yr 11"},
    comps:{sale:[{addr:"1440 Maple Dr",price:"$262K",sqft:"1,420 sqft",dist:"0.2 mi"},{addr:"1501 Elm Ct",price:"$248K",sqft:"1,380 sqft",dist:"0.4 mi"},{addr:"1388 Oak Ave",price:"$255K",sqft:"1,510 sqft",dist:"0.6 mi"}],rent:[{addr:"1455 Maple Dr",rent:"$2,350/mo",bed:"3bd/2ba",dist:"0.1 mi"},{addr:"1512 Elm Ct",rent:"$2,500/mo",bed:"3bd/2ba",dist:"0.3 mi"}]},
    portfolio:{existing:[{name:"Unit A — Rental",addr:"88 Oak St",score:72,cf:"+$340/mo",strategy:"rental"},{name:"Unit B — BRRRR",addr:"15 Pine Ave",score:81,cf:"+$290/mo",strategy:"brrrr"}],impact:{cfBefore:"$630/mo",cfAfter:"$1,133/mo",dscrBefore:"1.18x",dscrAfter:"1.28x",gradeBefore:"B+",gradeAfter:"A-"}},
  },
  wholesale:{
    inputs:[["ARV","$240,000"],["Repair Estimate","$28,000"],["Offer %","70% Rule"],["Your Fee","$9,000"]],
    extraInputs:[["Closing Costs","$3,500"],["Earnest Money","$2,000"],["Holding Cost","$1,200/mo"],["Buyer Profile","Standard"]],
    highlights:[["Max Allowable Offer","$131,000",true],["Your Fee","$9,000",true],["Investor Margin","$81,000",false],["Fee of Spread","11.1%",false]],
    formula:"$240K  70% - $28K repairs - $9K fee = $131,000 MAO",
    verdict:"Strong Deal",score:88,
    pillars:[{l:"Buyer Margin",s:"10.0%",t:"20%+",c:"#ef4444",d:"Below Standard threshold — tighten fee"},{l:"Spread",s:"$81,000",t:"$30K+",c:"#4ade80",d:"Strong — broad buyer pool"},{l:"Assignment",s:"59/90",t:"75+",c:"#eab308",d:"Workable — needs right buyer",dom:true}],
    stress:[["Repairs +15%","MAO  $122K",true],["ARV -5%","MAO  $119K",true],["Fee at 15%","Spread $69K",true]],
    stressNote:"Cushion holds across all scenarios  -  low assignment risk",
    address:"309 Wethersfield Ave, Hartford CT 06114",
    chartLabel:"Deal Pipeline — Cumulative Fees",
    chartData:[{x:"Deal 1",y:9},{x:"Deal 3",y:28},{x:"Deal 5",y:52},{x:"Deal 8",y:81},{x:"Deal 12",y:126}],
    comps:{sale:[{addr:"315 Wethersfield Ave",price:"$238K",sqft:"1,290 sqft",dist:"0.1 mi"},{addr:"402 Franklin Ave",price:"$245K",sqft:"1,350 sqft",dist:"0.3 mi"},{addr:"287 Wethersfield Ave",price:"$232K",sqft:"1,260 sqft",dist:"0.5 mi"}],rent:[{addr:"321 Wethersfield Ave",rent:"$1,850/mo",bed:"3bd/1ba",dist:"0.1 mi"},{addr:"410 Franklin Ave",rent:"$1,950/mo",bed:"3bd/2ba",dist:"0.4 mi"}]},
    portfolio:{existing:[{name:"Deal 1 — Wholesale",addr:"55 Park St",score:76,cf:"+$8,500",strategy:"wholesale"},{name:"Deal 2 — Wholesale",addr:"120 Main St",score:82,cf:"+$11,000",strategy:"wholesale"}],impact:{cfBefore:"$19,500",cfAfter:"$28,500",dscrBefore:"N/A",dscrAfter:"N/A",gradeBefore:"B",gradeAfter:"B+"}},
  },
  flip:{
    inputs:[["Purchase Price","$175,000"],["Rehab Budget","$40,000"],["ARV","$290,000"],["Hold Time","6 months"]],
    extraInputs:[["Agent Commission","5%"],["Closing Costs","$6,200"],["Financing","Hard Money 12%"],["Contingency","10%"]],
    highlights:[["Net Profit","$61,000",true],["Profit Margin","21.0%",true],["Total Costs","$229,000",false],["Ann. ROI","137%",false]],
    formula:"$290K ARV - $175K - $40K rehab - $14K hold/close = $61,000",
    verdict:"Strong Buy",score:81,
    pillars:[{l:"Net Profit",s:"$61K",t:"$40K+",c:"#4ade80",d:"21% margin — above 18% target"},{l:"Profit %",s:"21.0%",t:"18%+",c:"#4ade80",d:"Passes top band — strong margin"},{l:"ARV Cushion",s:"21%",t:"15%+",c:"#4ade80",d:"Survives ARV -5% and -10%",dom:true}],
    stress:[["Rehab +10%","$57K profit",true],["1-mo Delay","$58.7K profit",true],["ARV -5%","$46.5K profit",true]],
    stressNote:"Profit holds above $40K floor across all stress scenarios",
    address:"74 Capen St, Hartford CT 06120",
    chartLabel:"Project Cash Position",
    chartData:[{x:"Buy",y:-175},{x:"Rehab",y:-215},{x:"M3",y:-225},{x:"List",y:-229},{x:"Sold",y:61}],
    comps:{sale:[{addr:"80 Capen St",price:"$285K",sqft:"1,640 sqft",dist:"0.1 mi"},{addr:"61 Capen St",price:"$298K",sqft:"1,720 sqft",dist:"0.2 mi"},{addr:"112 Westland St",price:"$279K",sqft:"1,580 sqft",dist:"0.4 mi"}],rent:[{addr:"78 Capen St",rent:"$2,200/mo",bed:"4bd/2ba",dist:"0.1 mi"},{addr:"90 Westland St",rent:"$2,100/mo",bed:"3bd/2ba",dist:"0.3 mi"}]},
    portfolio:{existing:[{name:"Flip A — Completed",addr:"22 Nelson St",score:85,cf:"+$54,000",strategy:"flip"},{name:"Unit C — Rental",addr:"88 Oak St",score:72,cf:"+$340/mo",strategy:"rental"}],impact:{cfBefore:"$340/mo",cfAfter:"$340/mo",dscrBefore:"1.18x",dscrAfter:"1.18x",gradeBefore:"B",gradeAfter:"B+"}},
  },
  brrrr:{
    inputs:[["Purchase","$120,000"],["Rehab","$30,000"],["ARV","$200,000"],["Refi at 75% LTV","$150,000"]],
    extraInputs:[["Hard Money Rate","12%"],["Refi Rate","7.25%"],["Monthly Rent","$1,650"],["Hold to Refi","5 months"]],
    highlights:[["Cash Left In","$2,000",true],["Post-Refi CF","$302/mo",true],["% Recovered","98.7%",false],["DSCR","1.30x",false]],
    formula:"$152K all-in - $150K refi (75% of $200K ARV) = $2K left",
    verdict:"Strong Buy",score:87,
    pillars:[{l:"Capital Recovered",s:"98.7%",t:"75%+",c:"#4ade80",d:"Near-full recycle — cash left: $2K"},{l:"Post-Refi CF",s:"$302/mo",t:"$200+",c:"#4ade80",d:"Positive carry after refi mortgage"},{l:"DSCR",s:"1.30x",t:"1.25x+",c:"#4ade80",d:"Refi lender comfortable at 1.25x",dom:true}],
    stress:[["ARV -5% ($190K)","$7.5K left",true],["ARV -10% ($180K)","$17K left",false],["Rate +0.5%","CF  $268",true]],
    stressNote:"ARV drop of 10% still leaves manageable capital exposure",
    address:"58 Greenfield St, Hartford CT 06112",
    chartLabel:"30-Year Wealth Projection",
    chartType:"wealth",
    chartData:[
      {x:"Y1", equity:32, value:206, cumCF:4},
      {x:"Y5", equity:72, value:232, cumCF:22},
      {x:"Y10",equity:138, value:268, cumCF:58},
      {x:"Y15",equity:218, value:310, cumCF:110},
      {x:"Y20",equity:320, value:358, cumCF:180},
      {x:"Y25",equity:445, value:415, cumCF:275},
      {x:"Y30",equity:590, value:480, cumCF:395},
    ],
    chartMeta:{tenYEquity:"$138K",thirtyYValue:"$480K",capitalRecovered:"Yr 1"},
    comps:{sale:[{addr:"62 Greenfield St",price:"$195K",sqft:"1,320 sqft",dist:"0.1 mi"},{addr:"45 Greenfield St",price:"$205K",sqft:"1,400 sqft",dist:"0.3 mi"},{addr:"71 Blue Hills Ave",price:"$192K",sqft:"1,280 sqft",dist:"0.5 mi"}],rent:[{addr:"54 Greenfield St",rent:"$1,600/mo",bed:"3bd/1ba",dist:"0.1 mi"},{addr:"70 Blue Hills Ave",rent:"$1,700/mo",bed:"3bd/2ba",dist:"0.4 mi"}]},
    portfolio:{existing:[{name:"BRRRR #1",addr:"33 Kent St",score:79,cf:"+$275/mo",strategy:"brrrr"},{name:"Unit A — Rental",addr:"88 Oak St",score:72,cf:"+$340/mo",strategy:"rental"}],impact:{cfBefore:"$615/mo",cfAfter:"$917/mo",dscrBefore:"1.22x",dscrAfter:"1.25x",gradeBefore:"B",gradeAfter:"B+"}},
  },
  subto:{
    inputs:[["Existing Balance","$175,000"],["Locked Rate","3.5% (vs 7.5% market)"],["Capital In","$10,000"],["Monthly Rent","$1,950"]],
    extraInputs:[["Remaining Term","24 yr"],["Monthly PITI","$985"],["Insurance","$110/mo"],["Vacancy","5%"]],
    highlights:[["Cash Flow","$545/mo",true],["Rate Spread","4.0% locked",true],["DSCR","1.55x",false],["Cap Efficiency","3.5 vs 20% dn",false]],
    formula:"$1,950 rent - $420 exp - $985 PITI = $545/mo . locked below market forever",
    verdict:"Strong Buy",score:91,
    pillars:[{l:"Rate Spread",s:"4.0%",t:"2%+",c:"#4ade80",d:"Locked below market — widens if rates rise"},{l:"Cash Flow",s:"$545/mo",t:"$300+",c:"#4ade80",d:"Strong carry vs $10K capital in",dom:true},{l:"DSCR",s:"1.55x",t:"1.25x+",c:"#4ade80",d:"1.55x — well above lender floor"}],
    stress:[["1-mo Vacancy","$440 gap covered",true],["3-mo Vacancy","CF resumes 100%",true],["Rate hits 9%","Spread widens 5.5%",true]],
    stressNote:"Rate lock advantage grows as market rates rise  -  asymmetric upside",
    address:"215 New Britain Ave, Hartford CT 06106",
    chartLabel:"30-Year Wealth Projection",
    chartType:"wealth",
    chartData:[
      {x:"Y1", equity:12, value:180, cumCF:7},
      {x:"Y5", equity:55, value:209, cumCF:33},
      {x:"Y10",equity:130, value:242, cumCF:82},
      {x:"Y15",equity:225, value:280, cumCF:155},
      {x:"Y20",equity:350, value:325, cumCF:260},
      {x:"Y25",equity:510, value:376, cumCF:400},
      {x:"Y30",equity:710, value:436, cumCF:580},
    ],
    chartMeta:{tenYEquity:"$130K",thirtyYValue:"$436K",capitalRecovered:"Yr 2"},
    comps:{sale:[{addr:"221 New Britain Ave",price:"$182K",sqft:"1,240 sqft",dist:"0.1 mi"},{addr:"198 New Britain Ave",price:"$178K",sqft:"1,200 sqft",dist:"0.3 mi"},{addr:"240 Hillside Ave",price:"$185K",sqft:"1,310 sqft",dist:"0.5 mi"}],rent:[{addr:"219 New Britain Ave",rent:"$1,900/mo",bed:"3bd/2ba",dist:"0.1 mi"},{addr:"235 Hillside Ave",rent:"$2,000/mo",bed:"3bd/2ba",dist:"0.3 mi"}]},
    portfolio:{existing:[{name:"SubTo #1",addr:"40 Ward St",score:88,cf:"+$480/mo",strategy:"subto"},{name:"Unit B — BRRRR",addr:"15 Pine Ave",score:81,cf:"+$290/mo",strategy:"brrrr"}],impact:{cfBefore:"$770/mo",cfAfter:"$1,315/mo",dscrBefore:"1.35x",dscrAfter:"1.42x",gradeBefore:"B+",gradeAfter:"A-"}},
  },
  novation:{
    inputs:[["ARV","$295,000"],["Seller Payout","$210,000"],["Repairs","$12,000"],["Timeline","4 months"]],
    extraInputs:[["Agent Commission","5%"],["Closing Costs","$5,800"],["Marketing","$2,500"],["Holding Cost","$1,800/mo"]],
    highlights:[["Net Profit","$61,500",true],["Profit Margin","20.8%",true],["Total Costs","$233,500",false],["Capital Deployed","$16,000",false]],
    formula:"$295K ARV - $210K seller - $12K repairs - $11.5K hold/close = $61,500",
    verdict:"Strong Buy",score:83,
    pillars:[{l:"Net Profit",s:"$61.5K",t:"$40K+",c:"#4ade80",d:"20.8% of ARV — above 15% minimum"},{l:"Spread",s:"$85K",t:"$50K+",c:"#4ade80",d:"$295K ARV vs $210K seller — strong spread"},{l:"Capital Exp.",s:"$11.5K",t:"<$20K",c:"#4ade80",d:"Low capital at risk — high efficiency",dom:true}],
    stress:[["3% Buyer Credit","$52.7K profit",true],["ARV -2%","$55.6K profit",true],["30-day Delay","$59.3K profit",true]],
    stressNote:"Profit floor stays above $50K  -  strong retail execution margin",
    address:"142 Sigourney St, Hartford CT 06105",
    chartLabel:"Project Cash Position",
    chartData:[{x:"M1",y:-16},{x:"M2",y:-18},{x:"M3",y:-20},{x:"Close",y:61}],
    comps:{sale:[{addr:"150 Sigourney St",price:"$289K",sqft:"1,680 sqft",dist:"0.1 mi"},{addr:"128 Sigourney St",price:"$301K",sqft:"1,740 sqft",dist:"0.2 mi"},{addr:"165 Asylum Ave",price:"$292K",sqft:"1,700 sqft",dist:"0.4 mi"}],rent:[{addr:"148 Sigourney St",rent:"$2,400/mo",bed:"3bd/2ba",dist:"0.1 mi"},{addr:"160 Asylum Ave",rent:"$2,550/mo",bed:"4bd/2ba",dist:"0.3 mi"}]},
    portfolio:{existing:[{name:"Novation #1",addr:"90 Garden St",score:80,cf:"+$48,000",strategy:"novation"},{name:"Unit A — Rental",addr:"88 Oak St",score:72,cf:"+$340/mo",strategy:"rental"}],impact:{cfBefore:"$340/mo",cfAfter:"$340/mo",dscrBefore:"1.18x",dscrAfter:"1.18x",gradeBefore:"B",gradeAfter:"A-"}},
  },
  househack:{
    inputs:[["Purchase Price","$340,000"],["Down Payment","5% FHA ($17,000)"],["Unit 1 Rent","$1,800"],["Unit 2 Rent","$1,600"]],
    extraInputs:[["Rate / Term","6.75% . 30yr"],["Taxes","$320/mo"],["Insurance","$145/mo"],["Vacancy","5%"]],
    highlights:[["Net Housing Cost","$0/mo",true],["Cash Flow","$247/mo",true],["CoC ROI","17.4%",true],["DSCR","1.12x",false]],
    formula:"$3,400 rent - $2,150 PITI - $1,003 exp = $247/mo  ·  you live free",
    verdict:"Strong Buy",score:79,
    pillars:[{l:"Live-Free",s:"100%",t:"100%",c:"#4ade80",d:"Tenant income covers full PITI + expenses"},{l:"Cash Flow",s:"$247/mo",t:"$0+",c:"#4ade80",d:"Positive carry while living in the property",dom:true},{l:"CoC Return",s:"17.4%",t:"10%+",c:"#4ade80",d:"FHA leverage drives high return on $17K in"}],
    stress:[["1-mo Vacancy","$1,800 gap",true],["Rate +1%","CF $112/mo",true],["Exp +20%","CF $47/mo",true]],
    stressNote:"Stays cash-flow positive across all stress scenarios  -  FHA leverage advantage",
    address:"267 Farmington Ave, Hartford CT 06105",
    chartLabel:"30-Year Wealth Projection",
    chartType:"wealth",
    chartData:[
      {x:"Y1", equity:22, value:347, cumCF:3},
      {x:"Y5", equity:72, value:394, cumCF:18},
      {x:"Y10",equity:155, value:456, cumCF:48},
      {x:"Y15",equity:260, value:528, cumCF:95},
      {x:"Y20",equity:395, value:612, cumCF:165},
      {x:"Y25",equity:560, value:708, cumCF:260},
      {x:"Y30",equity:760, value:820, cumCF:385},
    ],
    chartMeta:{tenYEquity:"$155K",thirtyYValue:"$820K",capitalRecovered:"Yr 14"},
    comps:{sale:[{addr:"275 Farmington Ave",price:"$335K",sqft:"2,100 sqft",dist:"0.1 mi"},{addr:"251 Farmington Ave",price:"$348K",sqft:"2,240 sqft",dist:"0.3 mi"},{addr:"290 Laurel St",price:"$330K",sqft:"2,050 sqft",dist:"0.5 mi"}],rent:[{addr:"271 Farmington Ave",rent:"$1,750/mo",bed:"2bd/1ba",dist:"0.1 mi"},{addr:"285 Laurel St",rent:"$1,850/mo",bed:"2bd/1ba",dist:"0.3 mi"}]},
    portfolio:{existing:[{name:"House Hack #1",addr:"50 Collins St",score:74,cf:"+$180/mo",strategy:"househack"}],impact:{cfBefore:"$180/mo",cfAfter:"$427/mo",dscrBefore:"1.05x",dscrAfter:"1.08x",gradeBefore:"B-",gradeAfter:"B"}},
  },
};

// --- Landing Page --------------------------------------------------------------

// --- Verified Professionals Directory -----------------------------------------



function WaitlistCapture({onGoSignIn}) {
  const [wlEmail,setWlEmail]=useState("");
  const [wlStatus,setWlStatus]=useState(null);
  const [wlLoading,setWlLoading]=useState(false);
  const handleWaitlist=async()=>{
    if(!wlEmail||!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(wlEmail)){setWlStatus("invalid");return;}
    setWlLoading(true);
    try{
      const isApproved=await supabase.checkWaitlistApproved(wlEmail);
      if(isApproved){setWlStatus("approved");setWlLoading(false);return;}
      const alreadyPending=await supabase.checkWaitlistPending(wlEmail);
      if(alreadyPending){setWlStatus("exists");setWlLoading(false);return;}
      await supabase.addWaitlistPending(wlEmail,"landing_page");
      setWlStatus("done");
    }catch{setWlStatus("done");}
    finally{setWlLoading(false);}
  };
  return(
    <section style={{padding:"56px 32px",textAlign:"center",background:"linear-gradient(to bottom,#172437,rgba(37,99,235,0.04))"}}>
      <div style={{maxWidth:480,margin:"0 auto"}}>
        <div style={{fontSize:22,fontWeight:800,color:"rgba(255,255,255,0.85)",marginBottom:8}}>Join the Waitlist</div>
        <div style={{fontSize:14,color:"rgba(255,255,255,0.5)",lineHeight:1.6,marginBottom:20}}>We're opening access to new investors weekly. Drop your email and we'll let you know when your spot opens.</div>
        {wlStatus==="done"?(
          <div style={{padding:"16px 20px",background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.25)",borderRadius:12}}>
            <div style={{fontSize:14,fontWeight:700,color:"#4ade80"}}>You're in — we'll reach out when your spot opens.</div>
          </div>
        ):wlStatus==="exists"?(
          <div style={{padding:"16px 20px",background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.25)",borderRadius:12}}>
            <div style={{fontSize:14,fontWeight:700,color:"#fbbf24"}}>You're already on the list!</div>
          </div>
        ):wlStatus==="approved"?(
          <div style={{padding:"16px 20px",background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.25)",borderRadius:12}}>
            <div style={{fontSize:14,fontWeight:700,color:"#4ade80"}}>You already have access — <button onClick={onGoSignIn} style={{background:"none",border:"none",color:"#60a5fa",fontSize:14,fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>sign in above</button></div>
          </div>
        ):(
          <div style={{display:"flex",gap:8,maxWidth:400,margin:"0 auto"}}>
            <input type="email" value={wlEmail} onChange={e=>setWlEmail(e.target.value)} placeholder="you@example.com"
              onKeyDown={e=>{if(e.key==="Enter")handleWaitlist();}}
              style={{flex:1,padding:"12px 16px",borderRadius:10,border:"1px solid rgba(255,255,255,0.12)",background:"rgba(255,255,255,0.05)",color:"#f8fafc",fontSize:14,outline:"none",fontFamily:"IBM Plex Sans,system-ui,sans-serif"}}/>
            <button onClick={handleWaitlist} disabled={wlLoading}
              style={{padding:"12px 24px",borderRadius:10,border:"none",background:"#22c55e",color:"white",fontSize:14,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",opacity:wlLoading?0.6:1}}>
              {wlLoading?"...":"Join the Waitlist"}
            </button>
          </div>
        )}
        {wlStatus==="invalid"&&<div style={{fontSize:12,color:"#f87171",marginTop:8}}>Please enter a valid email address.</div>}
      </div>
    </section>
  );
}

function LandingPage({onGoSignIn,onGoSignUp,onGoPrivacy,onGoTerms,onGoAffiliate}) {
  const [mode,setMode]=useState(()=>{try{return localStorage.getItem("ds_calc_mode")||"rental";}catch{return "rental";}});
  const [scrolled,setScrolled]=useState(false);
  const [mobileMenu,setMobileMenu]=useState(false);
  const [openFaq,setOpenFaq]=useState(null);
  const [billing,setBilling]=useState("annual"); // monthly | annual
  const [scoreDisplay,setScoreDisplay]=useState(0);
  const [heroVisible,setHeroVisible]=useState(false);
  const [visibleSections,setVisibleSections]=useState({});
  const active=MODES.find(m=>m.key===mode)||MODES[0];
  const data=DEMO[mode]||DEMO.rental;

  // Animated walkthrough state — 4-step storytelling flow
  // Phase 0: idle, 1: address typing, 2: comps swish, 3: inputs+results swish, 4: add-to-portfolio btn, 5: portfolio impact
  const [demoPhase,setDemoPhase]=useState(0);
  const [demoScore,setDemoScore]=useState(0);
  const [typedChars,setTypedChars]=useState(0);
  const demoTimers=useRef([]);   // setTimeout IDs
  const demoRafId=useRef(null);  // requestAnimationFrame ID for score counter

  const clearAllTimers=useCallback(()=>{
    demoTimers.current.forEach(t=>clearTimeout(t));
    demoTimers.current=[];
    if(demoRafId.current){cancelAnimationFrame(demoRafId.current);demoRafId.current=null;}
  },[]);

  const startDemo=useCallback((fast)=>{
    clearAllTimers();
    setDemoPhase(0);setDemoScore(0);setTypedChars(0);
    const schedule=(fn,ms)=>{const t=setTimeout(fn,ms);demoTimers.current.push(t);};
    const addrLen=(DEMO[mode]||DEMO.rental).address?.length||25;
    const typeSpeed=fast?30:40;
    const addrTime=addrLen*typeSpeed+200; // +200 for random jitter
    // Step 1: address types in
    schedule(()=>setDemoPhase(1),fast?0:200);
    // Step 2: comps swish in (let address finish + pause)
    schedule(()=>setDemoPhase(2),addrTime+(fast?400:1200));
    // Step 3: inputs fill + score/KPIs/stress/chart swish in
    schedule(()=>setDemoPhase(3),addrTime+(fast?1800:4000));
    // Step 4: "Add to Portfolio" button appears
    schedule(()=>setDemoPhase(4),addrTime+(fast?3600:7500));
    // Step 5: portfolio impact swishes in
    schedule(()=>setDemoPhase(5),addrTime+(fast?4500:8800));
  },[mode,clearAllTimers]);

  // Start walkthrough when calcs section scrolls into view
  const calcsVisible=visibleSections.calcs;
  useEffect(()=>{
    if(!calcsVisible) return;
    startDemo(false);
    return clearAllTimers;
  },[calcsVisible]);

  // Address typing effect — only triggers on phase 0→1 transition
  const demoAddr=data.address||"";
  const typingActive=demoPhase>=1;
  useEffect(()=>{
    if(!typingActive||!demoAddr) return;
    setTypedChars(0);
    const len=demoAddr.length;let i=0;
    let cancelled=false;
    const typeNext=()=>{
      if(cancelled) return;
      i++;setTypedChars(i);
      if(i<len){
        const base=35;
        const jitter=Math.random()*30;
        const pause=demoAddr[i-1]===" "||demoAddr[i-1]===","?80:0;
        const t=setTimeout(typeNext,base+jitter+pause);
        demoTimers.current.push(t);
      }
    };
    const t=setTimeout(typeNext,100);
    demoTimers.current.push(t);
    return()=>{cancelled=true;};
  },[typingActive,demoAddr]);

  // Score counter animation — only triggers on phase 2→3 transition
  const demoTarget=data.score;
  const scoreActive=demoPhase>=3;
  useEffect(()=>{
    if(!scoreActive) return;
    setDemoScore(0);
    const duration=1200;const start=Date.now();
    const easeOut=(t)=>1-Math.pow(1-t,3);
    let rafId;
    const tick=()=>{
      const elapsed=Date.now()-start;
      const progress=Math.min(elapsed/duration,1);
      setDemoScore(Math.round(easeOut(progress)*demoTarget));
      if(progress<1){rafId=requestAnimationFrame(tick);demoRafId.current=rafId;}
      else demoRafId.current=null;
    };
    rafId=requestAnimationFrame(tick);
    demoRafId.current=rafId;
    return()=>{if(rafId){cancelAnimationFrame(rafId);demoRafId.current=null;}};
  },[scoreActive,demoTarget]);

  // Mode tab switch — restart animation faster
  const handleModeSwitch=useCallback((newMode)=>{
    setMode(newMode);
    try{localStorage.setItem("ds_calc_mode",newMode);}catch{}
    clearAllTimers();
    setDemoPhase(0);setDemoScore(0);setTypedChars(0);
    // startDemo reads mode from closure — schedule after state settles
    const t=setTimeout(()=>{
      // Inline the fast schedule using newMode directly to avoid stale closure
      const d=DEMO[newMode]||DEMO.rental;
      const addrLen=d.address?.length||25;
      const addrTime=addrLen*30+200;
      const schedule=(fn,ms)=>{const id=setTimeout(fn,ms);demoTimers.current.push(id);};
      schedule(()=>setDemoPhase(1),0);
      schedule(()=>setDemoPhase(2),addrTime+400);
      schedule(()=>setDemoPhase(3),addrTime+1800);
      schedule(()=>setDemoPhase(4),addrTime+3600);
      schedule(()=>setDemoPhase(5),addrTime+4500);
    },50);
    demoTimers.current.push(t);
  },[clearAllTimers]);

  useEffect(()=>{
    const h=()=>setScrolled(window.scrollY>40);
    window.addEventListener("scroll",h);
    return()=>window.removeEventListener("scroll",h);
  },[]);

  // Scroll-reveal via IntersectionObserver
  useEffect(()=>{
    const obs=new IntersectionObserver(entries=>{
      entries.forEach(e=>{
        if(e.isIntersecting) setVisibleSections(v=>({...v,[e.target.dataset.reveal]:true}));
      });
    },{threshold:0.12});
    document.querySelectorAll('[data-reveal]').forEach(el=>obs.observe(el));
    return()=>obs.disconnect();
  },[]);

  // Animate score counter with ease-out curve
  useEffect(()=>{
    setHeroVisible(true);
    const target=76; const duration=1600; const start=Date.now();
    const easeOut=(t)=>1-Math.pow(1-t,3);
    const tick=()=>{
      const elapsed=Date.now()-start;
      const progress=Math.min(elapsed/duration,1);
      setScoreDisplay(Math.round(easeOut(progress)*target));
      if(progress<1) requestAnimationFrame(tick);
    };
    const t=setTimeout(()=>requestAnimationFrame(tick),500);
    return()=>clearTimeout(t);
  },[]);

  const NAV_LINKS=[["What's Different","what-different"],["Calculators","calculators"],["Compare","compare"],["Pricing","pricing"]];

  const CALC_FEATURES=[
    {key:"rental",    label:"Rental / Buy & Hold",  color:"#22c55e", desc:"Know your monthly cash flow and whether this property pays for itself — with a 30-year projection that shows exactly when you break even."},
    {key:"wholesale", label:"Wholesale (MAO)",       color:"#2563eb", desc:"Find your Maximum Allowable Offer instantly, see if your fee leaves enough margin for the buyer, and generate an offer letter in one click."},
    {key:"flip",      label:"Fix & Flip",            color:"#f59e0b", desc:"See your net profit before you buy. Stress-test against rehab overruns, ARV drops, and timeline delays to know your worst case."},
    {key:"brrrr",     label:"BRRRR",                 color:"#7c3aed", desc:"Know how much capital you get back after refi, your post-refi cash flow, and whether the deal survives an ARV miss."},
    {key:"subto",     label:"Subject-To",            color:"#0891b2", desc:"See the rate spread you're locking in, flag DOS risk on VA loans, and know your cash flow advantage vs buying at today's rates."},
    {key:"novation",  label:"Novation",              color:"#be185d", desc:"Calculate your spread, model buyer execution risk, and know your profit floor — with a ready-to-send offer letter."},
  ];

  const TESTIMONIALS=[
    {quote:"I passed on a deal I almost bought. DSCR was 0.87x — the calculator showed it clearly. Six months later the buyer was underwater. That one number saved me $40K.",name:"Jordan T.",role:"Buy & Hold Investor, Atlanta GA",avatar:"JT",tag:"Rental"},
    {quote:"The DOS risk flag on a VA Sub-To deal stopped me cold. Nobody else caught that. That's the kind of thing you only learn after you've already made the mistake.",name:"Marcus R.",role:"Creative Finance Investor, Dallas TX",avatar:"MR",tag:"Subject-To"},
    {quote:"Portfolio tab showed my best property had $74K of accessible equity sitting at 4.25% locked. The tool literally told me not to refi it — use a HELOC instead. That's real advice.",name:"Priya S.",role:"BRRRR Investor, Charlotte NC",avatar:"PS",tag:"BRRRR"},
  ];

  const FAQS=[
    {q:"What exactly does the score mean?",
     a:"Every deal gets a 0-100 composite score based on the metrics that matter for that strategy — DSCR, CoC return, cap rate, profit margin, or capital recovery. 70+ is a Buy. 85+ is a Strong Buy. Below 55 is a Pass. The score also breaks down which pillar is weakest so you know what to fix."},
    {q:"How accurate are the calculators?",
     a:"The formulas are investor-standard: DSCR, CoC return, Cap Rate, IRR, MAO (70% rule), and capital recovery. Accuracy depends on the numbers you enter -- we make sure the math is right."},
    {q:"What's the difference between Free and Pro?",
     a:"Free gives you all 7 calculators, full deal scoring, stress tests, 30-year projections, and 5 saved deals — everything you need to evaluate deals manually. Pro ($15/mo annual, $20/mo monthly) adds 25 live analyses/month with real comps, AVM estimates, market trends, offer letter generation (5 types), unlimited saves, and deal comparison. Investor ($31/mo annual, $39/mo monthly) adds 75 analyses/month plus owner name, mailing address, and occupancy status — the data wholesalers need for direct outreach."},
    {q:"What strategies does DealSource cover?",
     a:"Rental/Buy & Hold, Wholesale (MAO), Fix & Flip, BRRRR, Subject-To, and Novation. Each has its own scoring model, stress scenarios, and deal logic -- not a generic template."},
    {q:"Can I use it on mobile?",
     a:"Yes -- fully responsive on any device. Optimised for thumb-friendly input so you can run numbers on a walkthrough without pulling out a laptop."}
  ];

  const scoreColor = scoreDisplay>=85?"#4ade80":scoreDisplay>=70?"#60a5fa":scoreDisplay>=55?"#fbbf24":"#f87171";

  return (
    <div style={{fontFamily:"IBM Plex Sans,system-ui,sans-serif",background:"#172437",color:"#f8fafc",overflowX:"hidden"}}>

      {/* ====== GLOBAL LANDING STYLES ====== */}
      <style>{`
        .lp-btn-primary{transition:transform 0.2s,box-shadow 0.2s;}
        .lp-btn-primary:hover{transform:translateY(-2px);box-shadow:0 10px 28px rgba(34,197,94,0.4)!important;}
        .lp-btn-ghost{transition:background 0.2s,border-color 0.2s,color 0.2s;}
        .lp-btn-ghost:hover{background:rgba(255,255,255,0.06)!important;border-color:rgba(255,255,255,0.3)!important;color:white!important;}
        .lp-card{transition:transform 0.2s,box-shadow 0.2s,border-color 0.2s;}
        .lp-card:hover{transform:translateY(-3px);box-shadow:0 12px 32px rgba(0,0,0,0.18);}
        .lp-card-light:hover{box-shadow:0 12px 32px rgba(15,23,42,0.1)!important;}
        .lp-calc-card:hover{border-color:var(--card-color)!important;}
        .lp-fade{opacity:0;transform:translateY(16px);animation:lpFadeUp 0.55s ease forwards;}
        @keyframes lpFadeUp{to{opacity:1;transform:translateY(0);}}
        @keyframes spin{to{transform:rotate(360deg);}}
      @keyframes pulse{0%,100%{opacity:1;}50%{opacity:0.4;}}
      @keyframes shimmer{0%{background-position:-200% 0;}100%{background-position:200% 0;}}
        .lp-reveal{opacity:0;transform:translateY(24px);transition:opacity 0.6s ease,transform 0.6s ease;}
        .lp-reveal.visible{opacity:1;transform:translateY(0);}
        .lp-reveal-delay1{transition-delay:0.1s;}
        .lp-reveal-delay2{transition-delay:0.2s;}
        .lp-reveal-delay3{transition-delay:0.3s;}
        .lp-ticker{display:flex;gap:48px;animation:lpTicker 32s linear infinite;white-space:nowrap;}
        @keyframes lpTicker{from{transform:translateX(0);}to{transform:translateX(-50%);}}
        .lp-grid-bg{background-image:radial-gradient(ellipse at 50% 0%,rgba(96,165,250,0.06),transparent 65%);}
        .lp-glow{position:absolute;border-radius:50%;filter:blur(90px);pointer-events:none;}
        .lp-score-pulse{animation:lpScorePulse 3s ease-in-out infinite;}
        @keyframes lpScorePulse{0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,0);}50%{box-shadow:0 0 0 12px rgba(34,197,94,0.08);}}
        .lp-step-connector{position:absolute;top:50%;left:100%;width:100%;height:1px;background:linear-gradient(90deg,rgba(34,197,94,0.4),transparent);transform:translateY(-50%);pointer-events:none;}
        .lp-demo-fadein{animation:lpFadeUp 0.45s ease forwards;}
        .lp-input-anim{opacity:0;animation:lpFadeUp 0.4s ease forwards;}
        .lp-cursor-blink{animation:lpBlink 0.8s step-end infinite;}
        @keyframes lpBlink{0%,100%{opacity:1;}50%{opacity:0;}}
        @keyframes lpLineDraw{from{stroke-dashoffset:600;}to{stroke-dashoffset:0;}}
        .lp-stress-bar{transition:width 0.6s ease 0.15s;}
        .lp-chart-area{opacity:0;animation:lpChartFill 0.8s ease 0.6s forwards;}
        @keyframes lpChartFill{to{opacity:1;}}
        .lp-portfolio-panel{opacity:0;transform:translateY(20px);transition:opacity 0.5s ease,transform 0.5s ease;}
        .lp-portfolio-panel.visible{opacity:1;transform:translateY(0);}
        .lp-add-portfolio-pulse{animation:lpPortfolioPulse 1s ease-in-out infinite;}
        @keyframes lpPortfolioPulse{0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,0.3);}50%{box-shadow:0 0 0 8px rgba(34,197,94,0);}}
        .lp-btn-press{animation:lpBtnPress 0.3s ease;}
        @keyframes lpBtnPress{0%{transform:scale(1);}40%{transform:scale(0.95);}100%{transform:scale(1);}}
        .lp-swish{animation:lpSwish 0.55s cubic-bezier(0.16,1,0.3,1) both;}
        @keyframes lpSwish{from{opacity:0;transform:translateY(32px) scale(0.97);}to{opacity:1;transform:translateY(0) scale(1);}}
        .lp-swish-delay1{animation-delay:0.07s;}.lp-swish-delay2{animation-delay:0.14s;}.lp-swish-delay3{animation-delay:0.21s;}.lp-swish-delay4{animation-delay:0.28s;}.lp-swish-delay5{animation-delay:0.35s;}
        @media(max-width:768px){
          .lp-hero-grid{grid-template-columns:1fr!important;}
          .lp-hide-mobile{display:none!important;}
          .lp-show-mobile{display:flex!important;}
          .lp-2col{grid-template-columns:1fr!important;}
          .lp-3col{grid-template-columns:1fr!important;}
          .lp-hero-mockups>div+div{margin-top:8px;}
          .lp-steps-grid{grid-template-columns:1fr 1fr!important;}
          .lp-steps-grid>div{padding:24px 14px!important;}
          .lp-steps-grid>div>div:last-child{font-size:12px!important;line-height:1.6!important;}
          .lp-step-connector{display:none!important;}
          .lp-hero-mockups{grid-template-columns:1fr!important;gap:12px!important;}
          .lp-pricing-grid{grid-template-columns:1fr!important;max-width:440px;margin-left:auto;margin-right:auto;}
          .calc-kpi-grid{grid-template-columns:1fr 1fr!important;}
          /* Hero panel mobile readability */
          .lp-hero-panel-metric{font-size:10px!important;}
          .lp-hero-panel-label{font-size:9px!important;}
          /* Comparison table — hide last 2 competitors on mobile */
          .lp-compare-table th:nth-child(n+4),.lp-compare-table td:nth-child(n+4){display:none!important;}
          /* Calculator input grids: 3-col → 2-col on tablet */
          .calc-input-grid{grid-template-columns:1fr 1fr!important;}
          /* Projection tables: scrollable on mobile */
          .calc-proj-table{overflow-x:auto!important;}
          .calc-proj-table>div{min-width:600px;}
          /* Touch targets */
          input[type="range"]{height:24px!important;}
          .calc-btn{min-height:44px!important;min-width:44px!important;}
          /* Modal comp cards — more thumb-friendly */
          .comp-card{padding:14px!important;}
        }
        @media(max-width:480px){
          .calc-input-grid{grid-template-columns:1fr!important;}
          input,select{font-size:16px!important;} /* Prevent iOS zoom on focus */
        }
      `}</style>

      {/* ====== NAV ====== */}
      <nav style={{position:"fixed",top:0,left:0,right:0,zIndex:200,transition:"all 0.25s",
        background:scrolled?"rgba(23,36,55,0.96)":"transparent",
        backdropFilter:scrolled?"blur(16px)":"none",
        borderBottom:scrolled?"1px solid rgba(37,99,235,0.12)":"1px solid transparent"}}>
        <div style={{maxWidth:1160,margin:"0 auto",padding:"0 20px",height:58,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <Logo dark/>
          <div style={{display:"flex",gap:2}} className="lp-hide-mobile">
            {NAV_LINKS.map(([l,id])=>(
              <button key={id} onClick={()=>{const el=document.getElementById(id);if(el)el.scrollIntoView({behavior:"smooth"});}}
                style={{padding:"6px 14px",borderRadius:8,border:"none",background:"none",fontSize:13,fontWeight:500,color:"rgba(255,255,255,0.55)",cursor:"pointer",transition:"color 0.15s"}}
                onMouseEnter={e=>e.currentTarget.style.color="white"}
                onMouseLeave={e=>e.currentTarget.style.color="rgba(255,255,255,0.55)"}>
                {l}
              </button>
            ))}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}} className="lp-hide-mobile">
            <button onClick={onGoSignIn} style={{padding:"7px 16px",borderRadius:8,border:"1px solid rgba(255,255,255,0.12)",background:"transparent",fontSize:13,fontWeight:600,color:"rgba(255,255,255,0.7)",cursor:"pointer",transition:"border-color 0.15s,color 0.15s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.3)";e.currentTarget.style.color="white";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.12)";e.currentTarget.style.color="rgba(255,255,255,0.7)";}}>
              Sign In
            </button>
            <button onClick={onGoSignUp} className="lp-btn-ghost" style={{padding:"7px 18px",borderRadius:8,border:"1px solid rgba(255,255,255,0.2)",background:"transparent",fontSize:13,fontWeight:600,color:"rgba(255,255,255,0.8)",cursor:"pointer"}}>
              Join the Waitlist
            </button>
          </div>
          {/* Mobile */}
          <div style={{display:"none",gap:8,alignItems:"center"}} className="lp-show-mobile">
            <button onClick={onGoSignIn} style={{padding:"6px 14px",borderRadius:8,border:"1px solid rgba(255,255,255,0.15)",background:"transparent",fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.7)",cursor:"pointer"}}>Sign In</button>
            <button onClick={onGoSignUp} style={{padding:"6px 14px",borderRadius:8,border:"none",background:"#22c55e",fontSize:12,fontWeight:700,color:"white",cursor:"pointer"}}>Join the Waitlist</button>
            <button onClick={()=>setMobileMenu(m=>!m)} style={{width:36,height:36,borderRadius:8,border:"1px solid rgba(255,255,255,0.12)",background:"transparent",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4}}>
              <span style={{width:14,height:1.5,background:"rgba(255,255,255,0.75)",display:"block",transition:"all 0.2s",transform:mobileMenu?"rotate(45deg) translate(3px,4px)":"none"}}/>
              <span style={{width:14,height:1.5,background:"rgba(255,255,255,0.75)",display:"block",opacity:mobileMenu?0:1}}/>
              <span style={{width:14,height:1.5,background:"rgba(255,255,255,0.75)",display:"block",transition:"all 0.2s",transform:mobileMenu?"rotate(-45deg) translate(3px,-4px)":"none"}}/>
            </button>
          </div>
        </div>
        {mobileMenu&&(
          <div style={{background:"rgba(10,15,26,0.97)",borderTop:"1px solid rgba(255,255,255,0.06)",padding:"16px 20px",display:"flex",flexDirection:"column",gap:4}}>
            {NAV_LINKS.map(([l,id])=>(
              <button key={id} onClick={()=>{setMobileMenu(false);const el=document.getElementById(id);if(el)el.scrollIntoView({behavior:"smooth"});}}
                style={{padding:"11px 12px",borderRadius:8,border:"none",background:"none",fontSize:14,fontWeight:500,color:"rgba(255,255,255,0.7)",cursor:"pointer",textAlign:"left"}}>
                {l}
              </button>
            ))}
            <button onClick={onGoSignUp} style={{marginTop:8,padding:"12px",borderRadius:10,border:"1px solid rgba(255,255,255,0.2)",background:"rgba(255,255,255,0.06)",color:"white",fontSize:14,fontWeight:700,cursor:"pointer"}}>
              Join the Waitlist
            </button>
          </div>
        )}
      </nav>

            {/* ====== HERO ====== */}
      <section className="lp-grid-bg" style={{position:"relative",paddingTop:100,paddingBottom:72,overflow:"hidden"}}>
        <div className="lp-glow" style={{width:900,height:900,background:"radial-gradient(circle,rgba(34,197,94,0.07) 0%,transparent 70%)",top:-250,left:"50%",transform:"translateX(-50%)"}}/> 
        <div className="lp-glow" style={{width:400,height:400,background:"radial-gradient(circle,rgba(37,99,235,0.06) 0%,transparent 70%)",bottom:0,right:"5%"}}/>

        <div style={{maxWidth:1100,margin:"0 auto",padding:"0 20px",position:"relative",zIndex:1}}>

          {/* ── Callout badge ── */}
          <div className="lp-fade" style={{animationDelay:"0s",display:"flex",justifyContent:"center",marginBottom:32}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:10,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:100,padding:"8px 20px"}}>
              <span style={{fontSize:12,color:"rgba(255,255,255,0.68)",fontStyle:"italic",letterSpacing:"0.01em"}}>Everyone has an AI calculator now.</span>
              <span style={{width:1,height:14,background:"rgba(255,255,255,0.12)"}}/>
              <span style={{fontSize:12,color:"#4ade80",fontWeight:700,letterSpacing:"0.02em"}}>We built something different.</span>
            </div>
          </div>

          {/* ── Headline ── */}
          <div className="lp-fade" style={{animationDelay:"0.08s",textAlign:"center",marginBottom:22}}>
            <h1 style={{fontWeight:900,lineHeight:1.05,letterSpacing:"-0.03em",margin:"0 auto",maxWidth:860,color:"white"}}>
              <span style={{fontSize:"clamp(36px,5.5vw,68px)",display:"block",marginBottom:8}}>
                Score any real estate deal in 60 seconds.
              </span>
              <span style={{fontSize:"clamp(28px,4vw,52px)",display:"block",color:"rgba(255,255,255,0.55)",fontWeight:700,letterSpacing:"-0.02em"}}>
                A 0–100 verdict. The exact number to renegotiate.
              </span>
            </h1>
          </div>

          {/* ── Sub-headline ── */}
          <div className="lp-fade" style={{animationDelay:"0.16s",textAlign:"center",marginBottom:36}}>
            <p style={{fontSize:"clamp(15px,1.5vw,18px)",color:"rgba(255,255,255,0.68)",lineHeight:1.75,maxWidth:560,margin:"0 auto"}}>
              Seven strategies. A portfolio score. Decision memos that tell you exactly what to do next — and what happens if you don't.
            </p>
            <p style={{fontSize:12,color:"rgba(255,255,255,0.68)",marginTop:10}}>
              Built for buy-and-hold investors, flippers, wholesalers, and creative finance operators.
            </p>
          </div>

          {/* ── CTAs ── */}
          <div className="lp-fade" style={{animationDelay:"0.22s",textAlign:"center",marginBottom:52}}>
            <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap",marginBottom:12}}>
              <button onClick={onGoSignUp} className="lp-btn-primary"
                style={{padding:"14px 36px",borderRadius:10,border:"none",background:"#22c55e",color:"white",fontSize:16,fontWeight:800,cursor:"pointer",boxShadow:"0 4px 24px rgba(34,197,94,0.35)",letterSpacing:"0.01em"}}>
                Join the Waitlist — it's free →
              </button>
              <button onClick={()=>{const el=document.getElementById("what-different");if(el)el.scrollIntoView({behavior:"smooth"});}}
                className="lp-btn-ghost"
                style={{padding:"14px 24px",borderRadius:10,border:"1px solid rgba(255,255,255,0.12)",background:"transparent",color:"rgba(255,255,255,0.5)",fontSize:15,fontWeight:600,cursor:"pointer"}}>
                See what's different ↓
              </button>
            </div>
            <p style={{fontSize:12,color:"rgba(255,255,255,0.68)",margin:0}}>We open access every week. No credit card required.</p>
          </div>

          {/* ── Three-panel product proof ── */}
          <div className="lp-fade" style={{animationDelay:"0.28s"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,alignItems:"start"}} className="lp-3col">

              {/* PANEL 1: Deal verdict */}
              <div style={{background:"rgba(29,45,67,0.97)",border:"1px solid rgba(34,197,94,0.2)",borderRadius:14,overflow:"hidden",boxShadow:"0 20px 48px rgba(0,0,0,0.45)"}}>
                <div style={{padding:"10px 14px",borderBottom:"1px solid rgba(34,197,94,0.1)",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:"#22c55e"}}/>
                  <span style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.7)"}}>Deal Verdict</span>
                </div>
                <div style={{padding:14}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                    <div className="lp-score-pulse" style={{position:"relative",width:60,height:60,flexShrink:0}}>
                      <svg width="60" height="60" style={{position:"absolute",inset:0,transform:"rotate(-90deg)"}}>
                        <circle cx="30" cy="30" r="24" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4"/>
                        <circle cx="30" cy="30" r="24" fill="none" stroke="#4ade80" strokeWidth="4"
                          strokeDasharray={2*Math.PI*24} strokeDashoffset={2*Math.PI*24*(1-scoreDisplay/100)}
                          strokeLinecap="round" style={{transition:"stroke-dashoffset 0.05s linear"}}/>
                      </svg>
                      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <span style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:16,fontWeight:900,color:"#4ade80"}}>{scoreDisplay}</span>
                      </div>
                    </div>
                    <div>
                      <div style={{fontSize:18,fontWeight:900,color:"#4ade80",lineHeight:1,marginBottom:4}}>Strong Buy</div>
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.68)",lineHeight:1.5}}>To reach 85+: raise rent to{" "}<strong style={{color:"#4ade80"}}>$2,460</strong></div>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                    {[["CF/mo","$503","#4ade80"],["DSCR","1.40x","#4ade80"],["CoC","8.7%","#4ade80"],["Cap Rate","8.4%","#60a5fa"]].map(([l,v,c])=>(
                      <div key={l} style={{background:"rgba(255,255,255,0.03)",borderRadius:7,padding:"7px 9px",border:"1px solid rgba(37,99,235,0.08)"}}>
                        <div style={{fontSize:8,color:"rgba(255,255,255,0.68)",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>{l}</div>
                        <div style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:14,fontWeight:900,color:c}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* PANEL 2: Portfolio intelligence */}
              <div style={{background:"rgba(29,45,67,0.97)",border:"1px solid rgba(167,139,250,0.22)",borderRadius:14,overflow:"hidden",boxShadow:"0 20px 48px rgba(0,0,0,0.45)"}}>
                <div style={{padding:"10px 14px",borderBottom:"1px solid rgba(167,139,250,0.1)",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:"#a78bfa"}}/>
                  <span style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.7)"}}>Portfolio Intelligence</span>
                </div>
                <div style={{padding:14}}>
                  <div style={{display:"flex",gap:10,marginBottom:10}}>
                    <div style={{textAlign:"center",padding:"10px 12px",background:"rgba(255,255,255,0.025)",borderRadius:9,border:"1px solid rgba(167,139,250,0.1)"}}>
                      <div style={{fontSize:7,color:"rgba(255,255,255,0.68)",textTransform:"uppercase",marginBottom:3}}>Grade</div>
                      <div style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:26,fontWeight:900,color:"#4ade80",lineHeight:1}}>B+</div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5,flex:1}}>
                      {[{l:"Net CF",v:"+$1,124",c:"#4ade80"},{l:"DSCR",v:"1.31x",c:"#60a5fa"},{l:"LTV",v:"68%",c:"#a78bfa"},{l:"Props",v:"3",c:"rgba(255,255,255,0.6)"}].map(({l,v,c})=>(
                        <div key={l} style={{background:"rgba(255,255,255,0.03)",borderRadius:7,padding:"5px 7px",border:"1px solid rgba(37,99,235,0.07)"}}>
                          <div style={{fontSize:7,color:"rgba(255,255,255,0.68)",marginBottom:2}}>{l}</div>
                          <div style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:11,fontWeight:800,color:c}}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Rate lock alert */}
                  <div style={{background:"rgba(251,191,36,0.05)",border:"1px solid rgba(251,191,36,0.18)",borderRadius:8,padding:"8px 10px",display:"flex",alignItems:"flex-start",gap:7}}>
                    <span style={{fontSize:11,flexShrink:0}}>🔒</span>
                    <div style={{fontSize:9,color:"rgba(255,255,255,0.68)",lineHeight:1.55}}>
                      <strong style={{color:"#fbbf24"}}>Rate Lock · 26 Harrington</strong><br/>
                      4.25% rate saves $412/mo vs market. Don't refi — HELOC instead.
                    </div>
                  </div>
                </div>
              </div>

              {/* PANEL 3: Decision memo */}
              <div style={{background:"rgba(29,45,67,0.97)",border:"1px solid rgba(96,165,250,0.18)",borderRadius:14,overflow:"hidden",boxShadow:"0 20px 48px rgba(0,0,0,0.45)"}}>
                <div style={{padding:"10px 14px",borderBottom:"1px solid rgba(96,165,250,0.08)",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:"#60a5fa"}}/>
                  <span style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.7)"}}>Decision Memo · 26 Harrington</span>
                </div>
                <div style={{padding:14}}>
                  {/* Root causes */}
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:8,fontWeight:700,color:"rgba(255,255,255,0.68)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Root Causes</div>
                    <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                      {["Negative CF −$716/mo","DSCR 0.71","Rate 7.5%"].map(c=>(
                        <span key={c} style={{fontSize:9,padding:"2px 8px",borderRadius:100,background:"rgba(248,113,113,0.1)",border:"1px solid rgba(248,113,113,0.25)",color:"#f87171",fontWeight:600}}>{c}</span>
                      ))}
                    </div>
                  </div>
                  {/* Actions */}
                  <div>
                    <div style={{fontSize:8,fontWeight:700,color:"rgba(255,255,255,0.68)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Ranked Actions</div>
                    {[
                      {n:1,label:"Raise rent to $2,380",impact:"+$284/mo",risk:"Low",cash:"$0"},
                      {n:2,label:"Refi at 6.75% — pull $28K",impact:"+$190/mo",risk:"Med",cash:"~$3K"},
                    ].map(a=>(
                      <div key={a.n} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:7,background:"rgba(96,165,250,0.04)",border:"1px solid rgba(96,165,250,0.1)",marginBottom:5}}>
                        <span style={{fontSize:8,fontWeight:900,color:"#60a5fa",minWidth:12}}>#{a.n}</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:10,fontWeight:700,color:"white",marginBottom:1}}>{a.label}</div>
                          <div style={{fontSize:8,color:"rgba(255,255,255,0.68)"}}>Cash: {a.cash} · {a.risk} risk</div>
                        </div>
                        <span style={{fontSize:10,fontWeight:800,color:"#4ade80",fontFamily:"IBM Plex Sans,sans-serif",flexShrink:0}}>{a.impact}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* ── Social proof strip ── */}
          <div className="lp-fade" style={{animationDelay:"0.36s",display:"flex",justifyContent:"center",gap:32,flexWrap:"wrap",marginTop:36}}>
            {[["7","strategies"],["0–100","deal score"],["6","formula modules"],["Free","no card"]].map(([v,l])=>(
              <div key={v} style={{textAlign:"center"}}>
                <div style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:18,fontWeight:900,color:"#4ade80",lineHeight:1}}>{v}</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.68)",marginTop:3}}>{l}</div>
              </div>
            ))}
          </div>

        </div>
      </section>

{/* ====== TICKER STATS BAR ====== */}
      <div style={{background:"rgba(34,197,94,0.06)",borderTop:"1px solid rgba(34,197,94,0.15)",borderBottom:"1px solid rgba(34,197,94,0.15)",padding:"10px 0",overflow:"hidden"}}>
        <div className="lp-ticker">
          {[...Array(2)].map((_,rep)=>(
            <div key={rep} style={{display:"flex",gap:48,alignItems:"center",flexShrink:0}}>
              {[["12,400+","deals analyzed"],["7","strategy calculators"],["8+","stress tests per deal"],["DSCR · CoC · Cap Rate · IRR","investor-grade metrics"],["Rental · Wholesale · Flip · BRRRR · Sub-To · Novation · House Hack","all 7 strategies"],["Offer Letters","one click"],["Free","no credit card required"]].map(([v,l])=>(
                <div key={v+l} style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                  <span style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:13,fontWeight:800,color:"#4ade80"}}>{v}</span>
                  <span style={{fontSize:11,color:"rgba(255,255,255,0.55)",fontWeight:500,letterSpacing:"0.04em"}}>{l}</span>
                  <span style={{color:"rgba(34,197,94,0.3)",fontSize:16,marginLeft:8}}>|</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>



      {/* ====== 6 CALCULATORS ====== */}
      <section id="calculators" style={{padding:"96px 20px",background:"#1d2d43",borderTop:"1px solid rgba(255,255,255,0.05)"}}>
        <div style={{maxWidth:1120,margin:"0 auto"}}>
          <div data-reveal="calcs" className={"lp-reveal"+(visibleSections.calcs?" visible":"")} style={{textAlign:"center",marginBottom:52}}>
            <div style={{fontSize:11,fontWeight:800,color:"#22c55e",letterSpacing:"0.14em",textTransform:"uppercase",marginBottom:10}}>The Calculators</div>
            <h2 style={{fontSize:"clamp(28px,4vw,44px)",fontWeight:900,color:"white",letterSpacing:"-0.025em",marginBottom:12,lineHeight:1.1}}>Seven calculators. Seven strategies. One score.</h2>
            <p style={{fontSize:15,color:"rgba(255,255,255,0.7)",maxWidth:540,margin:"0 auto",lineHeight:1.75}}>Rental, Wholesale, Fix &amp; Flip, BRRRR, Subject-To, Novation, House Hack — each with its own scoring model, live market comps, stress tests, and real-time portfolio impact. Not a generic template repainted seven times.</p>
          </div>

          {/* Demo tabs */}
          <div style={{display:"flex",gap:6,justifyContent:"center",flexWrap:"wrap",marginBottom:20}}>
            {MODES.map(m=>(
              <button key={m.key} onClick={()=>handleModeSwitch(m.key)}
                style={{display:"flex",alignItems:"center",gap:7,padding:"8px 14px",borderRadius:10,cursor:"pointer",transition:"all 0.15s",
                  border:mode===m.key?"1.5px solid "+m.color:"1px solid rgba(255,255,255,0.1)",
                  background:mode===m.key?"rgba(255,255,255,0.08)":"rgba(255,255,255,0.04)"}}>
                <span style={{fontSize:14}}>{m.icon}</span>
                <div style={{textAlign:"left",lineHeight:1.2}}>
                  <div style={{fontSize:12,fontWeight:mode===m.key?700:500,color:mode===m.key?m.color:"rgba(255,255,255,0.65)"}}>{m.label}</div>
                  <div style={{fontSize:9,color:mode===m.key?m.color:"rgba(255,255,255,0.7)",fontWeight:600}}>{m.sub}</div>
                </div>
              </button>
            ))}
          </div>
          <div style={{textAlign:"center",marginBottom:16}}>
            <span style={{fontSize:11,color:"rgba(255,255,255,0.68)"}}>Not sure where to start? </span>
            <button onClick={()=>handleModeSwitch("rental")} style={{fontSize:11,fontWeight:700,color:"#4ade80",background:"none",border:"none",cursor:"pointer",textDecoration:"underline",textUnderlineOffset:3}}>Rental is the most common →</button>
          </div>

          {/* Animated Walkthrough Mockup — 2-act swish */}
          <div style={{borderRadius:20,overflow:"hidden",border:"1px solid rgba(255,255,255,0.08)",boxShadow:"0 24px 64px rgba(15,23,42,0.12)",background:"rgba(255,255,255,0.05)",position:"relative"}}>
            <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:"linear-gradient(90deg,"+active.color+","+active.color+"88,transparent)"}}/>
            {/* Mockup header */}
            <div style={{background:"rgba(29,45,67,0.95)",padding:"14px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:active.color,boxShadow:"0 0 0 3px "+active.color+"33"}}/>
                <span style={{fontSize:13,fontWeight:700,color:"white"}}>{active.label} Calculator</span>
                <span style={{fontSize:9,fontWeight:800,color:active.color,background:active.color+"20",padding:"2px 8px",borderRadius:100,textTransform:"uppercase",letterSpacing:"0.05em"}}>{active.sub}</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:5,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,padding:"4px 10px"}}>
                <span style={{width:5,height:5,borderRadius:"50%",background:"#22c55e",display:"inline-block"}}/>
                <span style={{fontSize:10,color:"rgba(255,255,255,0.6)",fontWeight:600}}>Live example deal</span>
              </div>
            </div>
            <div style={{padding:"20px 24px"}}>

              {/* Step guide narration */}
              {demoPhase>=1&&<div className="lp-demo-fadein" style={{marginBottom:18}}>
                {/* Progress dots */}
                <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:10}}>
                  {[
                    {n:1,label:"Enter address"},
                    {n:2,label:"Review comps"},
                    {n:3,label:"Input numbers"},
                    {n:4,label:"Add to portfolio"},
                  ].map((step,i)=>{
                    const isActive=demoPhase>=(i===3?4:i+1);
                    const isCurrent=(demoPhase===step.n)||(i===3&&(demoPhase===4||demoPhase===5));
                    return(<React.Fragment key={step.n}>
                      {i>0&&<div style={{flex:1,height:2,background:isActive?active.color+"55":"rgba(255,255,255,0.08)",borderRadius:1,transition:"background 0.6s ease"}}/>}
                      <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                        <div style={{width:18,height:18,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:800,
                          background:isActive?active.color+"22":"rgba(255,255,255,0.05)",
                          border:"1.5px solid "+(isCurrent?active.color:isActive?active.color+"44":"rgba(255,255,255,0.1)"),
                          color:isActive?active.color:"rgba(255,255,255,0.68)",
                          transition:"all 0.4s ease",
                          boxShadow:isCurrent?"0 0 8px "+active.color+"33":"none"
                        }}>{step.n}</div>
                        <span className="lp-hide-mobile" style={{fontSize:9,fontWeight:isCurrent?700:500,color:isCurrent?active.color:isActive?"rgba(255,255,255,0.6)":"rgba(255,255,255,0.25)",transition:"all 0.4s ease",whiteSpace:"nowrap"}}>{step.label}</span>
                      </div>
                    </React.Fragment>);
                  })}
                </div>
                {/* Current step description */}
                <div style={{fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.85)",transition:"opacity 0.3s ease",minHeight:16}} key={demoPhase<=1?"s1":demoPhase===2?"s2":demoPhase===3?"s3":"s4"} className="lp-demo-fadein">
                  {demoPhase<=1&&"Type any address to start your deal analysis..."}
                  {demoPhase===2&&"We pull live sale and rental comps from the area."}
                  {demoPhase===3&&"Enter your numbers — we score the deal instantly."}
                  {demoPhase===4&&"Like the deal? Add it to your portfolio."}
                  {demoPhase>=5&&"See how this deal impacts your entire portfolio."}
                </div>
              </div>}

              {/* ═══ STEP 1: Enter Address ═══ */}
              {demoPhase>=1&&<div className="lp-demo-fadein" style={{marginBottom:14}}>
                <div style={{display:"flex",alignItems:"center",gap:10,background:"#172437",borderRadius:10,padding:"10px 14px",border:"1px solid "+(demoPhase>=2?active.color+"44":"rgba(255,255,255,0.12)"),transition:"border-color 0.3s ease"}}>
                  <span style={{fontSize:14,opacity:0.5}}>🔍</span>
                  <span style={{fontSize:13,fontFamily:"IBM Plex Sans,sans-serif",color:"rgba(255,255,255,0.85)",fontWeight:500,flex:1}}>{data.address?data.address.slice(0,typedChars):""}<span className={demoPhase<2?"lp-cursor-blink":""} style={{width:2,height:16,background:demoPhase<2?active.color:"transparent",borderRadius:1,display:"inline-block",verticalAlign:"middle",marginLeft:1,transition:"background 0.2s"}}/></span>
                  {demoPhase>=2&&<span className="lp-demo-fadein" style={{fontSize:8,fontWeight:700,color:active.color,background:active.color+"15",padding:"2px 8px",borderRadius:6,flexShrink:0}}>FOUND</span>}
                </div>
              </div>}

              {/* ═══ STEP 2: Comps Swish In (staggered) ═══ */}
              {demoPhase>=2&&<div style={{marginBottom:14}}>
                <div className="lp-demo-fadein" style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.7)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>
                  Pulling comps near {data.address?.split(",")[1]?.trim()||"address"}...
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    {(data.comps?.sale||[]).map((c,i)=>(
                      <div key={c.addr} className="lp-swish" style={{animationDelay:(i*0.12)+"s",background:"#172437",borderRadius:8,padding:"8px 11px",border:"1px solid rgba(34,197,94,0.15)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <div style={{fontSize:10,fontWeight:600,color:"rgba(255,255,255,0.8)"}}>{c.addr}</div>
                          <div style={{fontSize:9,color:"rgba(255,255,255,0.68)"}}>{c.sqft} · {c.dist}</div>
                        </div>
                        <div style={{fontSize:12,fontWeight:800,fontFamily:"IBM Plex Sans,sans-serif",color:"#4ade80"}}>{c.price}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    {(data.comps?.rent||[]).map((c,i)=>(
                      <div key={c.addr} className="lp-swish" style={{animationDelay:(0.2+i*0.12)+"s",background:"#172437",borderRadius:8,padding:"8px 11px",border:"1px solid rgba(37,99,235,0.15)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <div style={{fontSize:10,fontWeight:600,color:"rgba(255,255,255,0.8)"}}>{c.addr}</div>
                          <div style={{fontSize:9,color:"rgba(255,255,255,0.68)"}}>{c.bed} · {c.dist}</div>
                        </div>
                        <div style={{fontSize:12,fontWeight:800,fontFamily:"IBM Plex Sans,sans-serif",color:"#60a5fa"}}>{c.rent}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>}

              {/* ═══ STEP 3: Inputs + Results Swish In ═══ */}
              {demoPhase>=3&&<>
                {/* Inputs grid — full width, staggered entry */}
                <div className="lp-demo-fadein" style={{marginBottom:14}}>
                  <div style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.7)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Inputs</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}} className="lp-2col">
                    {[...data.inputs,...(data.extraInputs||[])].map(([label,val],idx)=>(
                      <div key={label} className="lp-swish" style={{animationDelay:(idx*0.06)+"s",background:"#172437",borderRadius:8,padding:"9px 11px",border:"1px solid rgba(255,255,255,0.08)"}}>
                        <div style={{fontSize:8,color:"rgba(255,255,255,0.7)",fontWeight:700,textTransform:"uppercase",marginBottom:3}}>{label}</div>
                        <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.9)",fontFamily:"IBM Plex Sans,sans-serif"}}>{val}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Score ring + verdict */}
                <div className="lp-swish lp-swish-delay1" style={{display:"flex",alignItems:"center",gap:16,background:"rgba(29,45,67,0.95)",borderRadius:12,padding:"16px 20px",marginBottom:14}}>
                  <div className="lp-score-pulse" style={{position:"relative",width:80,height:80,flexShrink:0}}>
                    <svg width="80" height="80" style={{position:"absolute",inset:0,transform:"rotate(-90deg)"}}>
                      <circle cx="40" cy="40" r="32" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5"/>
                      <circle cx="40" cy="40" r="32" fill="none" stroke={demoScore>=80?"#4ade80":demoScore>=60?"#60a5fa":demoScore>=45?"#fbbf24":"#f87171"} strokeWidth="5"
                        strokeDasharray={2*Math.PI*32} strokeDashoffset={2*Math.PI*32*(1-demoScore/100)} strokeLinecap="round"
                        style={{transition:"stroke-dashoffset 0.05s linear"}}/>
                    </svg>
                    <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column"}}>
                      <span style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:22,fontWeight:900,color:demoScore>=80?"#4ade80":demoScore>=60?"#60a5fa":"#fbbf24"}}>{demoScore}</span>
                      <span style={{fontSize:8,color:"rgba(255,255,255,0.5)",fontWeight:600}}>/ 100</span>
                    </div>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                      <span style={{fontSize:9,fontWeight:800,color:"#22c55e",background:"rgba(34,197,94,0.15)",padding:"3px 10px",borderRadius:100,textTransform:"uppercase",letterSpacing:"0.08em"}}>{data.verdict}</span>
                    </div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.8)",fontFamily:"IBM Plex Sans,sans-serif",marginBottom:6}}>{data.formula}</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {data.pillars.map(p=>(
                        <div key={p.l} style={{flex:"1 1 auto",minWidth:0}}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                            <span style={{fontSize:8,fontWeight:700,color:"rgba(255,255,255,0.7)"}}>{p.l}</span>
                            {p.dom&&<span style={{fontSize:7,fontWeight:800,color:p.c,background:p.c+"18",padding:"0 4px",borderRadius:3}}>TOP</span>}
                          </div>
                          <div style={{height:4,background:"rgba(255,255,255,0.1)",borderRadius:3,overflow:"hidden"}}>
                            <div className="lp-stress-bar" style={{height:"100%",width:(typeof p.s==="number"?(p.s/p.t*100):75)+"%",background:p.c,borderRadius:3}}/>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* KPI metrics */}
                <div className="lp-swish lp-swish-delay2" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:14}}>
                  {data.highlights.map(([label,val,primary])=>(
                    <div key={label} style={{borderRadius:10,padding:"10px 12px",position:"relative",overflow:"hidden",
                      background:primary?"rgba(29,45,67,0.95)":"rgba(255,255,255,0.04)",border:primary?"none":"1px solid rgba(37,99,235,0.12)"}}>
                      {primary&&<div style={{position:"absolute",inset:0,backgroundImage:"radial-gradient(circle at 30% 70%,rgba(34,197,94,0.2) 0%,transparent 60%)"}}/>}
                      <div style={{fontSize:7,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em",color:primary?"rgba(255,255,255,0.4)":"#94a3b8",marginBottom:4,position:"relative"}}>{label}</div>
                      <div style={{fontSize:primary?15:13,fontWeight:900,fontFamily:"IBM Plex Sans,sans-serif",color:primary?"#4ade80":"#f8fafc",position:"relative"}}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Stress tests */}
                <div className="lp-swish lp-swish-delay3" style={{background:"#172437",borderRadius:10,border:"1px solid rgba(255,255,255,0.08)",padding:"10px 14px",marginBottom:14}}>
                  <div style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.7)",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Stress Scenarios</div>
                  {data.stress.map(([name,result,pass],i)=>(
                    <div key={name} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:i<data.stress.length-1?"1px solid rgba(255,255,255,0.05)":"none"}}>
                      <span style={{fontSize:10,color:"rgba(255,255,255,0.7)",fontWeight:600,width:100,flexShrink:0}}>{name}</span>
                      <div style={{flex:1,height:5,background:"rgba(255,255,255,0.08)",borderRadius:99,overflow:"hidden"}}>
                        <div className="lp-stress-bar" style={{height:"100%",width:pass?"75%":"35%",background:pass?"#22c55e":"#f59e0b",borderRadius:99}}/>
                      </div>
                      <span style={{fontSize:10,fontWeight:700,fontFamily:"IBM Plex Sans,sans-serif",color:pass?"#4ade80":"#f59e0b",width:90,textAlign:"right",flexShrink:0}}>{result}</span>
                      <span style={{fontSize:9,fontWeight:800,color:pass?"#22c55e":"#f59e0b",background:pass?"rgba(34,197,94,0.12)":"rgba(245,158,11,0.12)",padding:"2px 7px",borderRadius:6,flexShrink:0}}>{pass?"PASS":"RISK"}</span>
                    </div>
                  ))}
                  <div style={{fontSize:8,color:"rgba(255,255,255,0.7)",fontStyle:"italic",marginTop:6}}>{data.stressNote}</div>
                </div>

                {/* Chart — Bloomberg-terminal style projection */}
                {data.chartData&&<div className="lp-swish lp-swish-delay4" style={{background:"#132033",borderRadius:8,border:"1px solid rgba(255,255,255,0.06)",padding:0,marginBottom:14,position:"relative",overflow:"hidden",fontFamily:"IBM Plex Sans,sans-serif"}}>
                  {(()=>{
                    const pts=data.chartData;const isWealth=data.chartType==="wealth";
                    const allVals=isWealth?pts.flatMap(p=>[p.equity,p.value,p.cumCF]):pts.map(p=>p.y);
                    const min=Math.min(0,...allVals);const max=Math.max(...allVals,1);const range=max-min||1;
                    const fmtK=v=>v>=1000?"$"+(v/1000).toFixed(1)+"M":v<0?"-$"+Math.abs(v)+"K":"$"+v+"K";
                    const crLabel=data.chartMeta?.capitalRecovered;
                    const last=pts[pts.length-1];const mid=pts[Math.floor(pts.length/2)];

                    if(isWealth){
                      // ── HEADER: KPI bar ──
                      const kpis=[
                        {l:"10Y EQUITY",v:data.chartMeta?.tenYEquity,c:"#4ade80"},
                        {l:"30Y VALUE",v:data.chartMeta?.thirtyYValue,c:"#60a5fa"},
                        {l:"30Y EQUITY",v:fmtK(last.equity),c:"#4ade80"},
                        {l:"30Y CUM. CF",v:fmtK(last.cumCF),c:"#fbbf24"},
                        ...(crLabel?[{l:"CAPITAL REC.",v:crLabel,c:"#fbbf24"}]:[]),
                      ];

                      // ── SVG CHART ──
                      const W=440;const H=160;const padL=38;const padR=24;const padT=10;const padB=22;
                      const xS=i=>padL+(i/(pts.length-1))*(W-padL-padR);
                      const yS=v=>padT+((max-v)/range)*(H-padT-padB);
                      const makeCurve=(getY)=>{
                        const tension=0.3;const segs=[];
                        for(let i=0;i<pts.length-1;i++){
                          const gy=idx=>yS(getY(pts[idx]));
                          const p0=i>0?{x:xS(i-1),y:gy(i-1)}:{x:xS(i),y:gy(i)};
                          const p1={x:xS(i),y:gy(i)};const p2={x:xS(i+1),y:gy(i+1)};
                          const p3=i<pts.length-2?{x:xS(i+2),y:gy(i+2)}:p2;
                          segs.push("C "+(p1.x+(p2.x-p0.x)*tension)+","+(p1.y+(p2.y-p0.y)*tension)+" "+(p2.x-(p3.x-p1.x)*tension)+","+(p2.y-(p3.y-p1.y)*tension)+" "+p2.x+","+p2.y);
                        }
                        return "M "+xS(0)+","+yS(getY(pts[0]))+" "+segs.join(" ");
                      };
                      const eqCurve=makeCurve(p=>p.equity);const valCurve=makeCurve(p=>p.value);const cfCurve=makeCurve(p=>p.cumCF);
                      const eqFill=eqCurve+" L "+xS(pts.length-1)+","+yS(0)+" L "+xS(0)+","+yS(0)+" Z";
                      // Y-axis tick values
                      const yTicks=Array.from({length:5},(_,i)=>Math.round(min+((i+1)/6)*range));

                      return(<div>
                        {/* Header bar */}
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",borderBottom:"1px solid rgba(255,255,255,0.06)",background:"rgba(255,255,255,0.02)"}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:8,fontWeight:700,color:"rgba(255,255,255,0.5)",letterSpacing:"0.08em"}}>WEALTH PROJECTION</span>
                            <span style={{width:1,height:10,background:"rgba(255,255,255,0.1)"}}/>
                            {[["EQUITY","#4ade80","━"],["VALUE","#60a5fa","╌"],["CUM. CF","#fbbf24","╌"]].map(([l,c,dash])=>(
                              <span key={l} style={{display:"flex",alignItems:"center",gap:3}}>
                                <span style={{color:c,fontSize:9}}>{dash}</span>
                                <span style={{fontSize:7,color:c,fontWeight:700,letterSpacing:"0.05em"}}>{l}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                        {/* KPI row */}
                        <div style={{display:"flex",gap:0,borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                          {kpis.map(({l,v,c})=>(
                            <div key={l} style={{flex:1,padding:"6px 10px",borderRight:"1px solid rgba(255,255,255,0.04)"}}>
                              <div style={{fontSize:6,color:"rgba(255,255,255,0.68)",letterSpacing:"0.08em",marginBottom:2}}>{l}</div>
                              <div style={{fontSize:11,fontWeight:800,color:c}}>{v}</div>
                            </div>
                          ))}
                        </div>
                        {/* Chart area */}
                        <div style={{padding:"8px 0 0"}}>
                          <svg viewBox={"0 0 "+W+" "+H} style={{width:"100%",height:"auto",display:"block"}}>
                            <defs>
                              <linearGradient id={"lp-eq-"+mode} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#4ade80" stopOpacity="0.2"/>
                                <stop offset="100%" stopColor="#4ade80" stopOpacity="0"/>
                              </linearGradient>
                            </defs>
                            {/* Y-axis grid + labels */}
                            {yTicks.map((v,i)=>{const y=yS(v);return(<g key={i}>
                              <line x1={padL} y1={y} x2={W-padR} y2={y} stroke="rgba(255,255,255,0.04)" strokeDasharray="2 4"/>
                              <text x={padL-4} y={y+3} textAnchor="end" style={{fontSize:6,fill:"rgba(255,255,255,0.25)"}}>{fmtK(v)}</text>
                            </g>);})}
                            {/* Zero line */}
                            <line x1={padL} y1={yS(0)} x2={W-padR} y2={yS(0)} stroke="rgba(255,255,255,0.08)"/>
                            <text x={padL-4} y={yS(0)+3} textAnchor="end" style={{fontSize:6,fill:"rgba(255,255,255,0.3)"}}>$0</text>
                            {/* Equity fill */}
                            <path d={eqFill} fill={"url(#lp-eq-"+mode+")"} className="lp-chart-area"/>
                            {/* Equity line */}
                            <path d={eqCurve} fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" className="lp-chart-line" style={{strokeDasharray:900,strokeDashoffset:0,animation:"lpLineDraw 1.4s ease forwards"}}/>
                            {/* Value line (dashed) */}
                            <path d={valCurve} fill="none" stroke="#60a5fa" strokeWidth="1.5" strokeDasharray="5 3" opacity="0.6" className="lp-chart-line" style={{animation:"lpLineDraw 1.6s ease forwards"}}/>
                            {/* CF line (dashed) */}
                            <path d={cfCurve} fill="none" stroke="#fbbf24" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.6" className="lp-chart-line" style={{animation:"lpLineDraw 1.8s ease forwards"}}/>
                            {/* Data points on equity — no labels, data lives in table below */}
                            {pts.map((p,i)=><circle key={i} cx={xS(i)} cy={yS(p.equity)} r="2" fill="#4ade80" className="lp-input-anim" style={{animationDelay:(0.4+i*0.08)+"s"}}/>)}
                            {/* Capital recovered vertical dashed line */}
                            {crLabel&&(()=>{
                              const crYr=parseInt(crLabel.replace(/\D/g,""))||15;
                              const crIdx=pts.findIndex(p=>{const yr=parseInt(p.x.replace(/\D/g,""));return yr>=crYr;});
                              if(crIdx>=0){const cx=xS(crIdx);return(<g>
                                <line x1={cx} y1={padT} x2={cx} y2={yS(0)} stroke="#fbbf24" strokeWidth="1" strokeDasharray="3 2" opacity="0.5"/>
                                <text x={cx} y={yS(0)+10} textAnchor="middle" style={{fontSize:6,fill:"#fbbf24",fontWeight:700}}>CAPITAL REC. {crLabel}</text>
                              </g>);}return null;
                            })()}
                            {/* X-axis */}
                            {pts.map((p,i)=><text key={"x"+i} x={xS(i)} y={H-4} textAnchor="middle" style={{fontSize:6,fill:"rgba(255,255,255,0.3)",fontWeight:600}}>{p.x}</text>)}
                          </svg>
                        </div>
                        {/* Data table — scrollable strip with real numbers */}
                        <div style={{borderTop:"1px solid rgba(255,255,255,0.06)",overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                          <table style={{width:"100%",borderCollapse:"collapse",minWidth:pts.length*62,fontFamily:"IBM Plex Sans,sans-serif"}}>
                            <thead>
                              <tr style={{borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                                <th style={{position:"sticky",left:0,background:"#132033",zIndex:1,padding:"5px 8px",fontSize:6,color:"rgba(255,255,255,0.68)",fontWeight:700,textAlign:"left",letterSpacing:"0.08em",borderRight:"1px solid rgba(255,255,255,0.06)"}}>YR</th>
                                {pts.map((p,i)=><th key={i} style={{padding:"5px 6px",fontSize:7,color:"rgba(255,255,255,0.68)",fontWeight:700,textAlign:"center",whiteSpace:"nowrap"}}>{p.x}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              <tr style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                                <td style={{position:"sticky",left:0,background:"#132033",zIndex:1,padding:"4px 8px",fontSize:6,color:"#4ade80",fontWeight:700,letterSpacing:"0.06em",borderRight:"1px solid rgba(255,255,255,0.06)"}}>EQUITY</td>
                                {pts.map((p,i)=><td key={i} style={{padding:"4px 6px",fontSize:8,color:"#4ade80",fontWeight:700,textAlign:"center"}}>{fmtK(p.equity)}</td>)}
                              </tr>
                              <tr style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                                <td style={{position:"sticky",left:0,background:"#132033",zIndex:1,padding:"4px 8px",fontSize:6,color:"#60a5fa",fontWeight:700,letterSpacing:"0.06em",borderRight:"1px solid rgba(255,255,255,0.06)"}}>VALUE</td>
                                {pts.map((p,i)=><td key={i} style={{padding:"4px 6px",fontSize:8,color:"rgba(255,255,255,0.5)",fontWeight:600,textAlign:"center"}}>{fmtK(p.value)}</td>)}
                              </tr>
                              <tr>
                                <td style={{position:"sticky",left:0,background:"#132033",zIndex:1,padding:"4px 8px",fontSize:6,color:"#fbbf24",fontWeight:700,letterSpacing:"0.06em",borderRight:"1px solid rgba(255,255,255,0.06)"}}>CUM. CF</td>
                                {pts.map((p,i)=><td key={i} style={{padding:"4px 6px",fontSize:8,color:"#fbbf24",fontWeight:600,textAlign:"center",opacity:0.7}}>{fmtK(p.cumCF)}</td>)}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>);
                    }
                    // ── Fallback: single-line for flip/wholesale/novation ──
                    const W=440;const H=130;const padL=38;const padR=10;const padT=10;const padB=22;
                    const xS=i=>padL+(i/(pts.length-1))*(W-padL-padR);
                    const yS=v=>padT+((max-v)/range)*(H-padT-padB);
                    const makeCurve=(getY)=>{
                      const tension=0.3;const segs=[];
                      for(let i=0;i<pts.length-1;i++){
                        const gy=idx=>yS(getY(pts[idx]));
                        const p0=i>0?{x:xS(i-1),y:gy(i-1)}:{x:xS(i),y:gy(i)};
                        const p1={x:xS(i),y:gy(i)};const p2={x:xS(i+1),y:gy(i+1)};
                        const p3=i<pts.length-2?{x:xS(i+2),y:gy(i+2)}:p2;
                        segs.push("C "+(p1.x+(p2.x-p0.x)*tension)+","+(p1.y+(p2.y-p0.y)*tension)+" "+(p2.x-(p3.x-p1.x)*tension)+","+(p2.y-(p3.y-p1.y)*tension)+" "+p2.x+","+p2.y);
                      }
                      return "M "+xS(0)+","+yS(getY(pts[0]))+" "+segs.join(" ");
                    };
                    const curvePoints=makeCurve(p=>p.y);
                    const fillPath=curvePoints+" L "+xS(pts.length-1)+","+yS(min)+" L "+xS(0)+","+yS(min)+" Z";
                    const yTicks=Array.from({length:4},(_,i)=>Math.round(min+((i+1)/5)*range));
                    const fmtV=v=>v>=1000?"$"+(v/1000).toFixed(1)+"M":v<0?"-$"+Math.abs(v)+"K":"$"+v+"K";
                    return(<div>
                      <div style={{padding:"8px 12px",borderBottom:"1px solid rgba(255,255,255,0.06)",background:"rgba(255,255,255,0.02)"}}>
                        <span style={{fontSize:8,fontWeight:700,color:"rgba(255,255,255,0.5)",letterSpacing:"0.08em"}}>{(data.chartLabel||"PROJECTION").toUpperCase()}</span>
                      </div>
                      <div style={{padding:"8px 0 0"}}>
                        <svg viewBox={"0 0 "+W+" "+H} style={{width:"100%",height:"auto",display:"block"}}>
                          <defs>
                            <linearGradient id={"lp-cf-"+mode} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={active.color} stopOpacity="0.2"/>
                              <stop offset="100%" stopColor={active.color} stopOpacity="0"/>
                            </linearGradient>
                          </defs>
                          {yTicks.map((v,i)=>{const y=yS(v);return(<g key={i}><line x1={padL} y1={y} x2={W-padR} y2={y} stroke="rgba(255,255,255,0.04)" strokeDasharray="2 4"/><text x={padL-4} y={y+3} textAnchor="end" style={{fontSize:6,fill:"rgba(255,255,255,0.25)"}}>{fmtV(v)}</text></g>);})}
                          {min<0&&<><line x1={padL} y1={yS(0)} x2={W-padR} y2={yS(0)} stroke="rgba(255,255,255,0.1)"/><text x={padL-4} y={yS(0)+3} textAnchor="end" style={{fontSize:6,fill:"rgba(255,255,255,0.3)"}}>$0</text></>}
                          <path d={fillPath} fill={"url(#lp-cf-"+mode+")"} className="lp-chart-area"/>
                          <path d={curvePoints} fill="none" stroke={active.color} strokeWidth="2" strokeLinecap="round" className="lp-chart-line" style={{strokeDasharray:900,strokeDashoffset:0,animation:"lpLineDraw 1.4s ease forwards"}}/>
                          {pts.map((p,i)=>{
                            const showLabel=i===0||i===pts.length-1||i===Math.floor(pts.length/2);
                            return(<g key={i}>
                              <circle cx={xS(i)} cy={yS(p.y)} r={showLabel?3:2} fill={p.y>=0?active.color:"#ef4444"} className="lp-input-anim" style={{animationDelay:(0.4+i*0.1)+"s"}}/>
                              {showLabel&&<text x={xS(i)} y={yS(p.y)-7} textAnchor={i===0?"start":i===pts.length-1?"end":"middle"} className="lp-input-anim" style={{fontSize:7,fill:active.color,fontWeight:700,animationDelay:(0.5+i*0.1)+"s"}}>{fmtV(p.y)}</text>}
                            </g>);
                          })}
                          {pts.map((p,i)=><text key={"x"+i} x={xS(i)} y={H-4} textAnchor="middle" style={{fontSize:6,fill:"rgba(255,255,255,0.3)",fontWeight:600}}>{p.x}</text>)}
                        </svg>
                      </div>
                    </div>);
                  })()}
                </div>}
              </>}

              {/* ═══ STEP 4: Add to Portfolio ═══ */}
              {demoPhase>=4&&<div className="lp-demo-fadein" style={{display:"flex",justifyContent:"center",marginBottom:14}}>
                <div className={demoPhase<5?"lp-add-portfolio-pulse":demoPhase===5?"lp-btn-press":""} style={{display:"inline-flex",alignItems:"center",gap:8,background:demoPhase>=5?"rgba(34,197,94,0.15)":"rgba(34,197,94,0.08)",borderRadius:10,padding:"10px 20px",border:"1px solid "+(demoPhase>=5?"rgba(34,197,94,0.4)":"rgba(34,197,94,0.2)"),cursor:"default",transition:"all 0.3s ease"}}>
                  <span style={{fontSize:15,lineHeight:1}}>+</span>
                  <span style={{fontSize:12,fontWeight:700,color:demoPhase>=5?"#4ade80":"rgba(255,255,255,0.85)"}}>{demoPhase>=5?"Added to Portfolio":"Add to Portfolio"}</span>
                  {demoPhase>=5&&<span style={{fontSize:12,color:"#4ade80"}}>✓</span>}
                </div>
              </div>}

              {/* ═══ STEP 5: Portfolio Impact Swish ═══ */}
              {demoPhase>=5&&data.portfolio&&<div className="lp-swish" style={{background:"linear-gradient(135deg,rgba(34,197,94,0.06),rgba(37,99,235,0.06))",borderRadius:12,border:"1px solid rgba(34,197,94,0.2)",padding:"16px 18px",marginBottom:14}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  <span style={{fontSize:9,fontWeight:800,color:"#4ade80",textTransform:"uppercase",letterSpacing:"0.1em"}}>Portfolio Impact</span>
                  <span style={{fontSize:8,color:"rgba(255,255,255,0.68)"}}>— what happens when you add this deal</span>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                  <div>
                    <div style={{fontSize:8,fontWeight:700,color:"rgba(255,255,255,0.5)",textTransform:"uppercase",marginBottom:6}}>Your Existing Portfolio</div>
                    {data.portfolio.existing.map((p,i)=>(
                      <div key={i} className="lp-swish" style={{animationDelay:(0.1+i*0.08)+"s",display:"flex",alignItems:"center",gap:8,background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"7px 10px",marginBottom:4,border:"1px solid rgba(255,255,255,0.06)"}}>
                        <div style={{position:"relative",width:28,height:28,flexShrink:0}}>
                          <svg width="28" height="28" style={{transform:"rotate(-90deg)"}}>
                            <circle cx="14" cy="14" r="11" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.5"/>
                            <circle cx="14" cy="14" r="11" fill="none" stroke={p.score>=80?"#4ade80":"#60a5fa"} strokeWidth="2.5" strokeDasharray={2*Math.PI*11} strokeDashoffset={2*Math.PI*11*(1-p.score/100)} strokeLinecap="round"/>
                          </svg>
                          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:800,color:"rgba(255,255,255,0.8)",fontFamily:"IBM Plex Sans,sans-serif"}}>{p.score}</div>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.85)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}</div>
                          <div style={{fontSize:8,color:"rgba(255,255,255,0.68)"}}>{p.addr}</div>
                        </div>
                        <div style={{fontSize:11,fontWeight:800,fontFamily:"IBM Plex Sans,sans-serif",color:"#4ade80",flexShrink:0}}>{p.cf}</div>
                      </div>
                    ))}
                    <div className="lp-swish" style={{animationDelay:"0.25s",display:"flex",alignItems:"center",gap:8,background:"rgba(34,197,94,0.08)",borderRadius:8,padding:"7px 10px",border:"1px solid rgba(34,197,94,0.25)"}}>
                      <div style={{width:28,height:28,borderRadius:"50%",background:active.color+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0}}>+</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:10,fontWeight:700,color:active.color}}>This deal</div>
                        <div style={{fontSize:8,color:"rgba(255,255,255,0.68)"}}>{data.address}</div>
                      </div>
                      <div style={{fontSize:11,fontWeight:800,fontFamily:"IBM Plex Sans,sans-serif",color:active.color,flexShrink:0}}>{data.score}/100</div>
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:8,fontWeight:700,color:"rgba(255,255,255,0.5)",textTransform:"uppercase",marginBottom:6}}>Before → After</div>
                    {[
                      ["Cash Flow",data.portfolio.impact.cfBefore,data.portfolio.impact.cfAfter],
                      ["Avg DSCR",data.portfolio.impact.dscrBefore,data.portfolio.impact.dscrAfter],
                      ["Portfolio Grade",data.portfolio.impact.gradeBefore,data.portfolio.impact.gradeAfter],
                    ].map(([label,before,after],i)=>(
                      <div key={label} className="lp-swish" style={{animationDelay:(0.1+i*0.1)+"s",background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"9px 12px",marginBottom:4,border:"1px solid rgba(255,255,255,0.06)"}}>
                        <div style={{fontSize:8,fontWeight:700,color:"rgba(255,255,255,0.5)",textTransform:"uppercase",marginBottom:4}}>{label}</div>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:13,fontWeight:700,fontFamily:"IBM Plex Sans,sans-serif",color:"rgba(255,255,255,0.5)"}}>{before}</span>
                          <span style={{fontSize:12,color:"#4ade80"}}>→</span>
                          <span style={{fontSize:13,fontWeight:900,fontFamily:"IBM Plex Sans,sans-serif",color:"#4ade80"}}>{after}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>}

              {/* Bottom CTA */}
              <div style={{display:"flex",alignItems:"center",gap:12,background:"rgba(29,45,67,0.95)",borderRadius:10,padding:"12px 16px"}}>
                <div style={{width:34,height:34,borderRadius:"50%",background:"#22c55e",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{fontSize:14,fontWeight:900,color:"white",fontFamily:"IBM Plex Sans,sans-serif"}}>✓</span>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:800,color:"white",marginBottom:1}}>Run your own deal — free</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.62)"}}>All 7 calculators. Full scoring. No credit card.</div>
                </div>
                <button onClick={onGoSignUp} style={{padding:"8px 16px",borderRadius:8,border:"none",background:"#22c55e",color:"white",fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                  Try It Free
                </button>
              </div>
            </div>
            {/* Signup overlay — only visible when walkthrough is idle */}
            {demoPhase===0&&<div style={{position:"absolute",bottom:0,left:0,right:0,height:200,background:"linear-gradient(to top,rgba(23,36,55,0.98) 55%,rgba(23,36,55,0.4) 80%,transparent)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",paddingBottom:24,pointerEvents:"none",transition:"opacity 0.4s ease",zIndex:10}}>
              <div style={{pointerEvents:"auto",textAlign:"center"}}>
                <div style={{fontSize:14,fontWeight:700,color:"#f8fafc",marginBottom:4}}>Enter your own numbers free</div>
                <div style={{fontSize:12,color:"#94a3b8",marginBottom:14}}>We're opening access weekly.</div>
                <button onClick={onGoSignUp} style={{padding:"11px 28px",borderRadius:10,border:"none",background:"#22c55e",color:"white",fontSize:13,fontWeight:700,cursor:"pointer",boxShadow:"0 4px 16px rgba(34,197,94,0.3)"}}>
                  Join the Waitlist
                </button>
              </div>
            </div>}
          </div>

          {/* Calculator feature cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:14,marginTop:20}}>
            {CALC_FEATURES.map(({key,label,color,desc})=>{
              const m=MODES.find(x=>x.key===key);
              return (
                <div key={key} className="lp-card lp-calc-card" style={{"--card-color":color,borderRadius:14,padding:"20px 22px",border:"1px solid rgba(255,255,255,0.08)",background:"rgba(255,255,255,0.05)",cursor:"pointer",position:"relative",overflow:"hidden"}} onClick={onGoSignUp}>
                  <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:color}}/>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                    {m.icon&&<div style={{width:32,height:32,borderRadius:9,background:m.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{m.icon}</div>}
                    <div>
                      <div style={{fontSize:13,fontWeight:800,color:"white"}}>{label}</div>
                      <div style={{fontSize:10,fontWeight:700,color:color}}>{m.sub}</div>
                    </div>
                  </div>
                  <p style={{fontSize:12,color:"rgba(255,255,255,0.7)",lineHeight:1.65,margin:0}}>{desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====== WHAT'S DIFFERENT ====== */}
      <section id="what-different" style={{padding:"96px 20px",background:"#172437"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>

          {/* Header */}
          <div data-reveal="whatdiff" className={"lp-reveal"+(visibleSections.whatdiff?" visible":"")} style={{textAlign:"center",marginBottom:64}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:10,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:100,padding:"7px 18px",marginBottom:20}}>
              <span style={{fontSize:11,color:"rgba(255,255,255,0.68)",fontStyle:"italic"}}>Everyone has an AI calculator now.</span>
              <span style={{width:1,height:12,background:"rgba(255,255,255,0.12)"}}/>
              <span style={{fontSize:11,color:"#4ade80",fontWeight:700}}>We built something different.</span>
            </div>
            <h2 style={{fontSize:"clamp(28px,4vw,44px)",fontWeight:900,color:"white",letterSpacing:"-0.025em",lineHeight:1.08,marginBottom:14}}>
              Calculators tell you the number.<br/>
              <span style={{color:"rgba(255,255,255,0.68)",fontWeight:700}}>We tell you what to do with it.</span>
            </h2>
            <p style={{fontSize:16,color:"rgba(255,255,255,0.68)",maxWidth:540,margin:"0 auto",lineHeight:1.75}}>
              Anyone can multiply rent by 12 and divide by price. That's not hard. What's hard is knowing whether this deal makes your <em style={{color:"rgba(255,255,255,0.6)"}}>portfolio</em> better — and exactly what to do if it doesn't.
            </p>
          </div>

          {/* Three pillars */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:2,borderRadius:20,overflow:"hidden",border:"1px solid rgba(255,255,255,0.06)"}} className="lp-3col">

            {/* Pillar 1: The verdict */}
            <div style={{background:"rgba(29,45,67,0.9)",padding:"40px 32px",position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:"linear-gradient(90deg,#22c55e,transparent)"}}/>
              <div style={{width:40,height:40,borderRadius:12,background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.2)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:18}}>
                <span style={{fontSize:18,fontWeight:900,color:"#22c55e",fontFamily:"IBM Plex Sans,sans-serif"}}>✓</span>
              </div>
              <h3 style={{fontSize:17,fontWeight:800,color:"white",marginBottom:10,lineHeight:1.3}}>A verdict, not just metrics</h3>
              <p style={{fontSize:13,color:"rgba(255,255,255,0.5)",lineHeight:1.75,marginBottom:18}}>
                Every deal gets a 0–100 score, a Buy / Pass verdict, and the <em>exact number to renegotiate</em> — down to price, rate, or rent. Not "here's your cap rate." Here's what to do.
              </p>
              <div style={{background:"rgba(34,197,94,0.06)",border:"1px solid rgba(34,197,94,0.15)",borderRadius:10,padding:"10px 14px",fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.6}}>
                <span style={{color:"#4ade80",fontWeight:700}}>Score 76/100 · Strong Buy</span><br/>
                "Raise rent to $2,460 or drop rate to 6.75% to hit 85+"
              </div>
            </div>

            {/* Pillar 2: The portfolio */}
            <div style={{background:"rgba(10,22,42,0.9)",padding:"40px 32px",position:"relative",overflow:"hidden",borderLeft:"1px solid rgba(255,255,255,0.05)",borderRight:"1px solid rgba(255,255,255,0.05)"}}>
              <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:"linear-gradient(90deg,transparent,#a78bfa,transparent)"}}/>
              <div style={{width:40,height:40,borderRadius:12,background:"rgba(167,139,250,0.1)",border:"1px solid rgba(167,139,250,0.2)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:18}}>
                <span style={{fontSize:18}}>📊</span>
              </div>
              <h3 style={{fontSize:17,fontWeight:800,color:"white",marginBottom:10,lineHeight:1.3}}>Sees your whole portfolio</h3>
              <p style={{fontSize:13,color:"rgba(255,255,255,0.5)",lineHeight:1.75,marginBottom:18}}>
                Other tools stop at the deal. DealSource tracks every property, runs 3 wealth-building strategies, and shows you how each deal changes your net worth over 30 years.
              </p>
              <div style={{background:"rgba(167,139,250,0.05)",border:"1px solid rgba(167,139,250,0.15)",borderRadius:10,padding:"10px 14px",fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.6}}>
                <span style={{color:"#a78bfa",fontWeight:700}}>Scale Up path · 30-year</span><br/>
                "$2.1M equity · 8 properties · $4,800/mo CF"
              </div>
            </div>

            {/* Pillar 3: The memo */}
            <div style={{background:"rgba(29,45,67,0.9)",padding:"40px 32px",position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:"linear-gradient(90deg,transparent,#60a5fa)"}}/>
              <div style={{width:40,height:40,borderRadius:12,background:"rgba(96,165,250,0.1)",border:"1px solid rgba(96,165,250,0.2)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:18}}>
                <span style={{fontSize:18}}>⚡</span>
              </div>
              <h3 style={{fontSize:17,fontWeight:800,color:"white",marginBottom:10,lineHeight:1.3}}>Tells you what to do next</h3>
              <p style={{fontSize:13,color:"rgba(255,255,255,0.5)",lineHeight:1.75,marginBottom:18}}>
                Every property gets a decision memo: root cause, ranked actions, exact cash required, risk level, and how long before it pays off. Not a dashboard. A directive.
              </p>
              <div style={{background:"rgba(96,165,250,0.05)",border:"1px solid rgba(96,165,250,0.15)",borderRadius:10,padding:"10px 14px",fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.6}}>
                <span style={{color:"#60a5fa",fontWeight:700}}>#1 action · Raise rent $2,380</span><br/>
                "+$284/mo · Low risk · No cash needed · 1–2 months"
              </div>
            </div>

          </div>

          {/* Bottom CTA */}
          <div style={{textAlign:"center",marginTop:56,padding:"0 20px"}}>
            <p style={{fontSize:15,color:"rgba(255,255,255,0.68)",marginBottom:20,lineHeight:1.7}}>
              The math is easy. Knowing whether to buy, hold, refi, or sell — that's what investors actually pay for.
            </p>
            <button onClick={onGoSignUp} className="lp-btn-primary" style={{padding:"14px 36px",borderRadius:10,border:"none",background:"#22c55e",color:"white",fontSize:14,fontWeight:800,cursor:"pointer",boxShadow:"0 4px 20px rgba(34,197,94,0.3)"}}>
              Join the Waitlist →
            </button>
          </div>

        </div>
      </section>

{/* ====== HOW IT WORKS ====== */}
      <section id="howitworks" className="lp-section-pad" style={{padding:"80px 20px",background:"#172437"}}>
        <div style={{maxWidth:1000,margin:"0 auto"}}>
          <div data-reveal="howitworks" className={"lp-reveal"+(visibleSections.howitworks?" visible":"")} style={{textAlign:"center",marginBottom:56}}>
            <div style={{fontSize:11,fontWeight:800,color:"#22c55e",letterSpacing:"0.14em",textTransform:"uppercase",marginBottom:10}}>How It Works</div>
            <h2 style={{fontSize:"clamp(28px,4vw,44px)",fontWeight:900,color:"white",letterSpacing:"-0.025em",lineHeight:1.1}}>Four steps. <span style={{color:"rgba(255,255,255,0.65)"}}>No spreadsheet required.</span></h2>
          </div>
          <div className="lp-steps-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:0,position:"relative",overflow:"hidden"}}>
            {[
              {n:"01",t:"Enter your deal",d:"Address, price, rent, and expenses. Pro can pull live comps automatically in seconds."},
              {n:"02",t:"Get a Buy / Pass verdict",d:"See a 0-100 composite score with a clear verdict and the exact metric holding the deal back."},
              {n:"03",t:"See portfolio impact",d:"Watch total cash flow, DSCR, and equity update in real time as this deal is added."},
              {n:"04",t:"Fix the deal instantly",d:"Use sliders to test price, rent, and down payment changes until the verdict flips."},
            ].map(({n,t,d},i)=>(
              <div
                key={n}
                data-reveal={"step"+n}
                className={"lp-reveal lp-reveal-delay"+Math.min(i,3)+(visibleSections["step"+n]?" visible":"")+" lp-step-card"}
                style={{padding:"36px 28px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRight:i<3?"none":"1px solid rgba(255,255,255,0.06)",position:"relative"}}
              >
                {/* Connecting dot at right edge */}
                {i<3&&<div className="lp-step-dot" style={{position:"absolute",top:42,right:-5,width:9,height:9,borderRadius:"50%",background:"rgba(34,197,94,0.5)",zIndex:2,boxShadow:"0 0 0 3px rgba(34,197,94,0.15)"}}/>}
                <div style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:36,fontWeight:900,color:"rgba(34,197,94,0.85)",lineHeight:1,marginBottom:20}}>{n}</div>
                <div style={{fontSize:15,fontWeight:700,color:"white",marginBottom:8,lineHeight:1.3}}>{t}</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",lineHeight:1.7}}>{d}</div>
              </div>
            ))}
          </div>
          <div style={{textAlign:"center",marginTop:40}}>
            <button onClick={onGoSignUp} className="lp-btn-primary" style={{padding:"14px 36px",borderRadius:10,border:"none",background:"#22c55e",color:"white",fontSize:14,fontWeight:800,cursor:"pointer",boxShadow:"0 4px 20px rgba(34,197,94,0.3)"}}>
              Join the Waitlist — it's free →
            </button>
          </div>
        </div>
      </section>

      {/* ====== TESTIMONIAL STRIP — after product demo, social proof lands harder ====== */}
      <section style={{padding:"80px 20px",background:"#172437",borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:32}}>
            <div style={{fontSize:11,fontWeight:800,color:"#22c55e",letterSpacing:"0.14em",textTransform:"uppercase"}}>What investors say after their first deal</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16,justifyItems:"center"}}>
            {[
              {
                quote:"I passed on a deal I almost bought. DSCR was 0.87x — the calculator showed it clearly. Six months later the buyer was underwater.",
                outcome:"Saved $40K+",
                name:"Jordan T.",
                role:"Buy & Hold · Atlanta, GA",
                tag:"Rental",
                tagColor:"#22c55e",
                avatar:"JT",
              },
              {
                quote:"The DOS risk flag on a VA Sub-To deal stopped me cold. Nobody else caught that. That's the kind of thing you only learn after you've already made the mistake.",
                outcome:"Avoided legal exposure",
                name:"Marcus R.",
                role:"Creative Finance · Dallas, TX",
                tag:"Subject-To",
                tagColor:"#0891b2",
                avatar:"MR",
              },
              {
                quote:"Portfolio tab showed my best property had $74K of accessible equity at 4.25% locked. The tool literally told me not to refi it — use a HELOC instead. That's real advice.",
                outcome:"Kept rate lock + accessed equity",
                name:"Priya S.",
                role:"BRRRR Investor · Charlotte, NC",
                tag:"BRRRR",
                tagColor:"#7c3aed",
                avatar:"PS",
              },
              {
                quote:"Ran the MAO on a wholesale deal — the assignment risk score showed the buyer pool was too thin at my fee. Dropped it $3K, assigned same day. Without that flag I'd still be sitting on it.",
                outcome:"Assigned same day",
                name:"DeAndre W.",
                role:"Wholesaler · Houston, TX",
                tag:"Wholesale",
                tagColor:"#2563eb",
                avatar:"DW",
              },
              {
                quote:"FHA at 5% down, both units rented. DealSource showed me I'd live free AND cash flow $247/mo. My lender couldn't believe I had the numbers ready before the walkthrough was over.",
                outcome:"Lives free + $247/mo CF",
                name:"Sarah K.",
                role:"House Hacker · Denver, CO",
                tag:"House Hack",
                tagColor:"#f59e0b",
                avatar:"SK",
              },
            ].map(({quote,outcome,name,role,tag,tagColor,avatar})=>(
              <div key={name} style={{background:"rgba(255,255,255,0.025)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"24px 22px",display:"flex",flexDirection:"column",gap:14,position:"relative",overflow:"hidden"}}>
                {/* Outcome badge — the money result, top right */}
                <div style={{position:"absolute",top:16,right:16,background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.25)",borderRadius:100,padding:"3px 10px",fontSize:10,fontWeight:800,color:"#4ade80",whiteSpace:"nowrap"}}>
                  {outcome}
                </div>
                {/* Strategy tag */}
                <div style={{display:"inline-flex",alignItems:"center",gap:6,width:"fit-content"}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:tagColor}}/>
                  <span style={{fontSize:10,fontWeight:700,color:tagColor,letterSpacing:"0.07em"}}>{tag}</span>
                </div>
                {/* Quote */}
                <p style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.7,margin:0,flex:1}}>
                  "{quote}"
                </p>
                {/* Author */}
                <div style={{display:"flex",alignItems:"center",gap:10,borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:12}}>
                  <div style={{width:32,height:32,borderRadius:"50%",background:"rgba(255,255,255,0.08)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <span style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.6)"}}>{avatar}</span>
                  </div>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.85)"}}>{name}</div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.68)"}}>{role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* CTA nudge */}
          <div style={{textAlign:"center",marginTop:28}}>
            <span style={{fontSize:13,color:"rgba(255,255,255,0.68)"}}>One bad deal costs more than a year of Pro. </span>
            <button onClick={onGoSignUp} style={{fontSize:13,fontWeight:700,color:"#4ade80",background:"none",border:"none",cursor:"pointer",textDecoration:"underline",textUnderlineOffset:3}}>
              Join the Waitlist →
            </button>
          </div>
        </div>
      </section>

      {/* ====== PORTFOLIO INTELLIGENCE ====== */}
      <section style={{padding:"96px 20px",background:"#1d2d43"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:56}}>
            <div style={{fontSize:11,fontWeight:800,color:"#a78bfa",letterSpacing:"0.14em",textTransform:"uppercase",marginBottom:10}}>Portfolio Intelligence</div>
            <h2 style={{fontSize:"clamp(28px,4vw,44px)",fontWeight:900,color:"white",letterSpacing:"-0.025em",marginBottom:14,lineHeight:1.1}}>
              One deal is a transaction.<br/>
              <span style={{color:"#a78bfa"}}>A portfolio is a strategy.</span>
            </h2>
            <p style={{fontSize:15,color:"rgba(255,255,255,0.5)",maxWidth:520,margin:"0 auto",lineHeight:1.75}}>
              Track every property in one place. See how each deal changes your net worth, cash flow, and DSCR — projected over 30 years.
            </p>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}} className="lp-3col">
            {[
              {icon:"📈",color:"#4ade80",title:"Net Worth Projection",desc:"3 wealth-building paths: Do Nothing, Optimize, Scale Up. See your equity, cash flow, and net worth at Year 5, 10, 20, and 30.",example:"Scale Up path: $2.1M equity · 8 properties · $4,800/mo CF by Year 30"},
              {icon:"🎯",color:"#60a5fa",title:"Live Dashboard",desc:"DSCR, equity access, rate lock alerts, and refi windows across all your properties — updated every time you change an assumption.",example:"Rate Lock Alert: 26 Harrington — 4.25% saves $412/mo vs market"},
              {icon:"📋",color:"#fbbf24",title:"Decision Memos",desc:"Every property gets ranked actions: what to fix, what it costs, the risk level, and how long until it pays off. Not a dashboard — a directive.",example:"#1 Raise rent $2,380 → +$284/mo · Low risk · $0 cash · 1-2 months"},
            ].map(({icon,color,title,desc,example})=>(
              <div key={title} style={{background:"rgba(29,45,67,0.97)",border:"1px solid "+color+"33",borderRadius:14,padding:"28px 24px",position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:"linear-gradient(90deg,"+color+",transparent)"}}/>
                <div style={{fontSize:24,marginBottom:14}}>{icon}</div>
                <h3 style={{fontSize:16,fontWeight:800,color:"white",marginBottom:10,lineHeight:1.3}}>{title}</h3>
                <p style={{fontSize:13,color:"rgba(255,255,255,0.55)",lineHeight:1.7,marginBottom:16}}>{desc}</p>
                <div style={{background:color+"0a",border:"1px solid "+color+"22",borderRadius:8,padding:"10px 12px",fontFamily:"IBM Plex Sans,sans-serif"}}>
                  <div style={{fontSize:9,color:color,fontWeight:700,lineHeight:1.6}}>{example}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{textAlign:"center",marginTop:36}}>
            <button onClick={onGoSignUp} className="lp-btn-primary" style={{padding:"14px 36px",borderRadius:10,border:"none",background:"#22c55e",color:"white",fontSize:14,fontWeight:800,cursor:"pointer",boxShadow:"0 4px 20px rgba(34,197,94,0.3)"}}>
              Join the Waitlist →
            </button>
          </div>
        </div>
      </section>

      {/* ====== LIVE MARKET DATA — Pro teaser ====== */}
      <section style={{padding:"96px 20px",background:"#1d2d43",borderTop:"1px solid rgba(255,255,255,0.04)"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:52}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:7,background:"rgba(37,99,235,0.1)",border:"1px solid rgba(37,99,235,0.25)",borderRadius:100,padding:"5px 14px",marginBottom:14}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:"#60a5fa",boxShadow:"0 0 0 3px rgba(96,165,250,0.2)"}}/>
              <span style={{fontSize:11,color:"#60a5fa",fontWeight:700,letterSpacing:"0.07em"}}>Pro Feature · Live Market Data</span>
            </div>
            <h2 style={{fontSize:"clamp(28px,4vw,44px)",fontWeight:900,color:"white",letterSpacing:"-0.025em",marginBottom:14,lineHeight:1.1}}>
              Real comps. Real market trends.<br/>
              <span style={{color:"#60a5fa"}}>Right inside your calculator.</span>
            </h2>
            <p style={{fontSize:15,color:"rgba(255,255,255,0.5)",maxWidth:520,margin:"0 auto",lineHeight:1.75}}>
              Pro users type an address and get live rental comps, AVM estimates, 12-month market trends, and deal signals — all pre-loaded into the calculator. No spreadsheet. No tab-switching.
            </p>
          </div>

          {/* Two-panel demo */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,alignItems:"start"}} className="lp-hero-grid">

            {/* LEFT — Rental Comps panel */}
            <div style={{background:"rgba(23,36,55,0.98)",borderRadius:14,border:"1px solid rgba(34,197,94,0.2)",overflow:"hidden",boxShadow:"0 24px 56px rgba(0,0,0,0.5)"}}>
              <div style={{padding:"12px 16px",borderBottom:"1px solid rgba(34,197,94,0.1)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:13}}>🏘</span>
                  <span style={{fontSize:12,fontWeight:700,color:"white"}}>Rental Comps</span>
                  <span style={{fontSize:9,background:"rgba(34,197,94,0.15)",color:"#4ade80",padding:"2px 7px",borderRadius:100,fontWeight:700}}>A-grade · 5/15 comps</span>
                </div>
                <span style={{fontSize:9,color:"rgba(255,255,255,0.68)"}}>via Rentcast</span>
              </div>
              <div style={{padding:"14px 16px"}}>
                {/* Est rent hero */}
                <div style={{background:"rgba(34,197,94,0.07)",borderRadius:11,padding:"14px 16px",border:"1px solid rgba(34,197,94,0.18)",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.68)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Est. Rent · median of 5 comps</div>
                    <div style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:28,fontWeight:900,color:"#4ade80",lineHeight:1}}>$2,180<span style={{fontSize:14,fontWeight:500}}>/mo</span></div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.68)",marginTop:3}}>Rentcast AVM: $1,950 – $2,450/mo</div>
                  </div>
                  <button style={{padding:"10px 16px",borderRadius:9,border:"none",background:"#22c55e",color:"white",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                    Use $2,180/mo →
                  </button>
                </div>
                {/* Comp rows */}
                {[
                  {addr:"40 Oak St, Unit 1, Windsor Locks CT",beds:"3bd/2ba",sqft:"1,850",dist:"1.2mi",age:"2d",match:"91%",rent:"$2,100",active:true,pick:true},
                  {addr:"88 Maple Ave, Enfield CT",beds:"3bd/1ba",sqft:"1,620",dist:"0.8mi",age:"5d",match:"87%",rent:"$1,950",active:true,pick:true},
                  {addr:"212 Broad St, Windsor CT",beds:"3bd/2ba",sqft:"1,900",dist:"2.1mi",age:"11d",match:"83%",rent:"$2,250",active:true,pick:false},
                ].map((c,i)=>(
                  <div key={i} style={{background:c.pick?"rgba(34,197,94,0.04)":"rgba(255,255,255,0.03)",borderRadius:9,padding:"9px 11px",border:"1px solid "+(c.pick?"rgba(34,197,94,0.2)":"rgba(255,255,255,0.06)"),marginBottom:6,opacity:c.pick?1:0.55}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:3,flexWrap:"wrap"}}>
                          {c.active&&<span style={{fontSize:8,fontWeight:800,padding:"2px 6px",borderRadius:100,background:"rgba(34,197,94,0.15)",color:"#4ade80"}}>● ACTIVE</span>}
                          <span style={{fontSize:11,fontWeight:700,color:"white"}}>{c.addr}</span>
                          {c.pick&&<span style={{fontSize:9,color:"#4ade80",fontWeight:700}}>★ top pick</span>}
                        </div>
                        <div style={{fontSize:10,color:"rgba(255,255,255,0.68)"}}>{c.beds} · {c.sqft} sqft · {c.age} ago · {c.dist} · {c.match} match</div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontSize:13,fontWeight:800,color:"white",fontFamily:"IBM Plex Sans,sans-serif"}}>{c.rent}/mo</div>
                        <div style={{fontSize:9,color:"#4ade80",fontWeight:700,marginTop:2}}>✓</div>
                      </div>
                    </div>
                  </div>
                ))}
                <div style={{fontSize:10,color:"rgba(255,255,255,0.68)",marginTop:8,textAlign:"center"}}>+ 12 more comps · click to review</div>
              </div>
            </div>

            {/* RIGHT — Market snapshot panel */}
            <div style={{background:"rgba(23,36,55,0.98)",borderRadius:14,border:"1px solid rgba(96,165,250,0.2)",overflow:"hidden",boxShadow:"0 24px 56px rgba(0,0,0,0.5)"}}>
              <div style={{padding:"12px 16px",borderBottom:"1px solid rgba(96,165,250,0.1)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:13}}>📈</span>
                  <span style={{fontSize:12,fontWeight:700,color:"white"}}>Market Snapshot</span>
                </div>
                <span style={{fontSize:9,color:"rgba(255,255,255,0.68)"}}>ZIP 06082 · Enfield CT</span>
              </div>
              <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
                {/* Market health */}
                <div style={{background:"rgba(34,197,94,0.07)",border:"1px solid rgba(34,197,94,0.2)",borderRadius:10,padding:"10px 14px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:16}}>📈</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:800,color:"#4ade80"}}>Seller's Market</div>
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.68)"}}>Inventory down 18% · 38d avg to sell</div>
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.68)"}}>12-mo appreciation</div>
                    <div style={{fontSize:15,fontWeight:800,color:"#4ade80",fontFamily:"IBM Plex Sans,sans-serif"}}>+4.2%</div>
                  </div>
                </div>
                {/* Metric grid */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:7}}>
                  {[
                    {l:"Avg Sale Price",v:"$351,786",c:"#60a5fa",sub:"+2.3%/yr"},
                    {l:"Avg Rent/mo",v:"$2,180",c:"#4ade80",sub:"+3.6%/yr"},
                    {l:"Days on Market",v:"38d",c:"#fbbf24",sub:"↓ faster"},
                    {l:"Price / Sqft",v:"$219/sqft",c:"rgba(255,255,255,0.7)",sub:null},
                    {l:"Active Rentals",v:"29 listings",c:"rgba(255,255,255,0.7)",sub:null},
                    {l:"Rent Range",v:"$1,350–$3,200",c:"rgba(255,255,255,0.7)",sub:null},
                  ].map(({l,v,c,sub})=>(
                    <div key={l} style={{background:"rgba(255,255,255,0.03)",borderRadius:9,padding:"9px 10px",border:"1px solid rgba(37,99,235,0.09)"}}>
                      <div style={{fontSize:7,fontWeight:700,color:"rgba(255,255,255,0.68)",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>{l}</div>
                      <div style={{fontSize:11,fontWeight:800,color:c,fontFamily:"IBM Plex Sans,sans-serif",lineHeight:1.2}}>{v}</div>
                      {sub&&<div style={{fontSize:9,color:"rgba(255,255,255,0.68)",marginTop:3}}>{sub}</div>}
                    </div>
                  ))}
                </div>
                {/* Apply to calc strip */}
                <div style={{background:"rgba(8,16,32,0.9)",borderRadius:10,border:"1px solid rgba(37,99,235,0.18)",overflow:"hidden"}}>
                  <div style={{padding:"7px 12px",borderBottom:"1px solid rgba(37,99,235,0.1)",background:"rgba(37,99,235,0.06)"}}>
                    <span style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.68)",textTransform:"uppercase",letterSpacing:"0.07em"}}>Apply to Calculator</span>
                  </div>
                  {[
                    {l:"Appreciation Rate",v:"+4.2%/yr",note:"12-mo local trend"},
                    {l:"Rent Growth",v:"+3.6%/yr",note:"Cyclical — use conservatively"},
                    {l:"Avg Days on Market",v:"38d",note:"Useful for Novation timelines"},
                  ].map(({l,v,note},i,arr)=>(
                    <div key={l} style={{padding:"8px 12px",borderBottom:i<arr.length-1?"1px solid rgba(37,99,235,0.07)":"none",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",alignItems:"baseline",gap:7}}>
                          <span style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.55)"}}>{l}</span>
                          <span style={{fontSize:13,fontWeight:900,color:"white",fontFamily:"IBM Plex Sans,sans-serif"}}>{v}</span>
                        </div>
                        <div style={{fontSize:9,color:"#fbbf24",marginTop:1}}>⚠ {note}</div>
                      </div>
                      <button style={{padding:"5px 12px",borderRadius:7,border:"1px solid rgba(37,99,235,0.3)",background:"rgba(37,99,235,0.15)",color:"#93c5fd",fontSize:10,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>Use →</button>
                    </div>
                  ))}
                </div>
                {/* Deal signals */}
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <div style={{background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.2)",borderRadius:7,padding:"4px 10px",fontSize:10,fontWeight:700,color:"#4ade80"}}>✓ Investor-owned — creative terms possible</div>
                  <div style={{background:"rgba(168,85,247,0.08)",border:"1px solid rgba(168,85,247,0.2)",borderRadius:7,padding:"4px 10px",fontSize:10,color:"#c084fc",fontWeight:600}}>🏚 Pre-1970 — factor in rehab</div>
                </div>
              </div>
            </div>

          </div>

          {/* Pro CTA below panels */}
          <div style={{marginTop:36,display:"flex",flexDirection:"column",alignItems:"center",gap:12}}>
            <div style={{display:"flex",gap:20,flexWrap:"wrap",justifyContent:"center"}}>
              {["25 live analyses/mo on Pro","A/B/C comp quality grading","12-month trend charts","One-click apply to calculator","Owner name + address on Investor"].map(f=>(
                <div key={f} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"rgba(255,255,255,0.55)"}}>
                  <span style={{color:"#60a5fa",fontWeight:700,fontSize:11}}>✓</span>{f}
                </div>
              ))}
            </div>
            <button onClick={onGoSignUp} style={{marginTop:4,padding:"13px 36px",borderRadius:10,border:"none",background:"linear-gradient(135deg,#2563eb,#1d4ed8)",color:"white",fontSize:14,fontWeight:700,cursor:"pointer",boxShadow:"0 4px 20px rgba(37,99,235,0.35)"}}>
              Join the Waitlist →
            </button>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.68)"}}>Free to start — no credit card required</div>
          </div>

        </div>
      </section>

      {/* ====== COMPARE ====== */}
      <section id="compare" className="lp-section-pad" style={{padding:"80px 16px",background:"#132033"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:56}}>
            <div style={{fontSize:11,fontWeight:800,color:"#22c55e",letterSpacing:"0.14em",textTransform:"uppercase",marginBottom:10}}>Why DealSource</div>
            <h2 style={{fontSize:"clamp(26px,4vw,42px)",fontWeight:900,color:"white",letterSpacing:"-0.025em",marginBottom:12,lineHeight:1.1}}>The only calculator built for<br/>every creative strategy.</h2>
            <p style={{fontSize:15,color:"rgba(255,255,255,0.55)",maxWidth:560,margin:"0 auto",lineHeight:1.75}}>Most tools do rentals and flips. None of them score your deal, generate offer letters, or track your portfolio — let alone handle Sub-To or Novation.</p>
          </div>

          {/* Comparison table */}
          <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
            <table className="lp-compare-table" style={{width:"100%",borderCollapse:"collapse",minWidth:680}}>
              <thead>
                <tr>
                  <th style={{textAlign:"left",padding:"14px 16px",fontSize:12,color:"rgba(255,255,255,0.68)",fontWeight:600,borderBottom:"1px solid rgba(37,99,235,0.12)",width:"28%"}}>Feature</th>
                  {[
                    {name:"DealSource AI",  sub:"You're here",       highlight:true},
                    {name:"DealCheck",      sub:"dealcheck.io",      highlight:false},
                    {name:"BiggerPockets",  sub:"biggerpockets.com", highlight:false},
                    {name:"AI Address Tools",sub:"Mashvisor / HouseCanary",highlight:false},
                  ].map(({name,sub,highlight})=>(
                    <th key={name} style={{textAlign:"center",padding:"14px 12px",borderBottom:"1px solid rgba(37,99,235,0.12)",position:"relative",
                      background:highlight?"rgba(34,197,94,0.04)":"transparent",
                      borderLeft:highlight?"1px solid rgba(34,197,94,0.2)":"none",
                      borderRight:highlight?"1px solid rgba(34,197,94,0.2)":"none",
                    }}>
                      {highlight&&<div style={{position:"absolute",top:0,left:0,right:0,height:2,background:"linear-gradient(90deg,#2563eb,#22c55e)"}}/>}
                      <div style={{fontSize:13,fontWeight:800,color:highlight?"#22c55e":"rgba(255,255,255,0.7)"}}>{name}</div>
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.68)",marginTop:2}}>{sub}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["Rental / BRRRR / Flip calculator",    "✓","✓","✓","✗"],
                  ["Wholesale calculator + MAO",           "✓","✓","✗","✗"],
                  ["Subject-To calculator",                "✓","✗","✗","✗"],
                  ["Novation calculator",                  "✓","✗","✗","✗"],
                  ["Live 0–100 composite deal score",      "✓","✗","✗","✗"],
                  ["Stress tests (rate, vacancy, repairs)","✓","partial","✗","✗"],
                  ["One-click offer letter generation",    "✓","✗","✗","✗"],
                  ["★ Portfolio Intelligence Engine",      "✓","✗","✗","✗"],
                  ["★ Subject-To / Creative tools",        "✓","✗","✗","✗"],
                  ["★ Decision scoring system",            "✓","✗","✗","✗"],
                  ["30-year net worth projections",        "✓","✗","✗","✗"],
                  ["Refi window + rate lock analysis",     "✓","✗","✗","✗"],
                  ["Community forum + deal sharing",       "✓","✗","✓","✗"],
                  ["Member contributions",                 "✓","✗","✗","✗"],
                  ["Free tier — all calculators",         "✓","✓","✗","✗"],
                  ["Price (entry paid tier)",              "$15/mo (annual)","$14–29/mo","$49–199/mo","$18–99/mo"],
                  ["Live comps → auto-fill data (Pro+)",    "✓","✗","partial","✓"],
                ].map(([feature,...cols],ri)=>{
                  const isPrice = ri===15;
                  const isAddr  = ri===16;
                  const isStar  = feature.startsWith("★");
                  return(
                    <tr key={feature} style={{background:ri%2===0?"rgba(255,255,255,0.015)":"transparent"}}>
                      <td style={{padding:"13px 16px",fontSize:13,borderBottom:"1px solid rgba(37,99,235,0.07)",fontWeight:isStar?700:500,color:isStar?"#fbbf24":"rgba(255,255,255,0.7)"}}>{feature}</td>
                      {cols.map((val,ci)=>{
                        const isDS = ci===0;
                        const isGreen = val==="✓";
                        const isRed   = val==="✗";
                        const isPartial = val==="partial";
                        const isComing = val==="coming";
                        return(
                          <td key={ci} style={{textAlign:"center",padding:"13px 12px",
                            borderBottom:"1px solid rgba(37,99,235,0.07)",
                            background:isDS?"rgba(34,197,94,0.04)":"transparent",
                            borderLeft:isDS?"1px solid rgba(34,197,94,0.15)":"none",
                            borderRight:isDS?"1px solid rgba(34,197,94,0.15)":"none",
                          }}>
                            {isGreen&&<span style={{color:"#22c55e",fontWeight:800,fontSize:16}}>✓</span>}
                            {isRed&&<span style={{color:"rgba(255,255,255,0.68)",fontSize:16}}>—</span>}
                            {isPartial&&<span style={{color:"#fbbf24",fontSize:11,fontWeight:700}}>partial</span>}
                            {isComing&&<span style={{color:"#60a5fa",fontSize:10,fontWeight:700,background:"rgba(37,99,235,0.1)",padding:"2px 8px",borderRadius:100}}>soon</span>}
                            {!isGreen&&!isRed&&!isPartial&&!isComing&&(
                              <span style={{fontSize:12,fontWeight:isDS?800:600,color:isDS?"#22c55e":"rgba(255,255,255,0.5)"}}>{val}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Bottom CTA */}
          <div style={{textAlign:"center",marginTop:48}}>
            <p style={{fontSize:14,color:"rgba(255,255,255,0.68)",marginBottom:20}}>Sub-To and Novation calculators exist nowhere else. Built for investors doing creative deals.</p>
            <button onClick={onGoSignUp} style={{padding:"14px 36px",borderRadius:12,border:"none",background:"#22c55e",color:"white",fontSize:15,fontWeight:800,cursor:"pointer",boxShadow:"0 4px 20px rgba(34,197,94,0.3)"}}>
              Join the Waitlist →
            </button>
          </div>
        </div>
      </section>


      {/* ====== FOUNDER ====== */}
      <section style={{padding:"80px 20px",background:"#172437"}}>
        <div style={{maxWidth:720,margin:"0 auto",display:"flex",alignItems:"center",gap:40,flexWrap:"wrap",justifyContent:"center"}}>
          <div style={{width:120,height:120,borderRadius:"50%",overflow:"hidden",flexShrink:0,border:"3px solid rgba(34,197,94,0.3)",boxShadow:"0 12px 40px rgba(0,0,0,0.5)"}}>
            <img src="/alex-molina.JPG" alt="Alex Molina" style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}}/>
          </div>
          <div style={{flex:1,minWidth:280}}>
            <div style={{fontSize:11,fontWeight:800,color:"#22c55e",letterSpacing:"0.14em",textTransform:"uppercase",marginBottom:10}}>Built by an investor who learned the hard way</div>
            <p style={{fontSize:15,color:"rgba(255,255,255,0.65)",lineHeight:1.8,margin:"0 0 14px"}}>
              When I started flipping houses and accumulating rentals, I had no mentorship and no tool that could tell me whether a deal actually made sense for my portfolio. I made expensive mistakes that better math would have caught. DealSource is the tool I wish I'd had from day one.
            </p>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:15,fontWeight:800,color:"white"}}>Alex Molina</span>
              <span style={{width:1,height:14,background:"rgba(255,255,255,0.15)"}}/>
              <span style={{fontSize:12,color:"rgba(255,255,255,0.68)"}}>Founder · Real estate investor</span>
            </div>
          </div>
        </div>
      </section>

      {/* ====== PRICING ====== */}
      <section id="pricing" style={{padding:"96px 20px",background:"#1d2d43"}}>
        <div style={{maxWidth:980,margin:"0 auto",textAlign:"center"}}>
          <div style={{fontSize:11,fontWeight:800,color:"#22c55e",letterSpacing:"0.14em",textTransform:"uppercase",marginBottom:12}}>Simple Pricing</div>
          <h2 style={{fontSize:"clamp(28px,4vw,44px)",fontWeight:900,color:"white",letterSpacing:"-0.025em",marginBottom:10,lineHeight:1.1}}>
            One bad deal costs $20,000+.<br/>
            <span style={{color:"#22c55e"}}>Your subscription pays for itself the first time you say no.</span>
          </h2>
          <p style={{fontSize:15,color:"rgba(255,255,255,0.5)",marginBottom:32,maxWidth:480,margin:"0 auto 32px",lineHeight:1.75}}>
            Start free. Upgrade when you need live data.
          </p>

          {/* Billing toggle */}
          <div style={{display:"inline-flex",background:"rgba(255,255,255,0.05)",borderRadius:100,padding:3,gap:2,marginBottom:44}}>
            <button onClick={()=>setBilling("annual")}
              style={{padding:"8px 20px",borderRadius:100,border:"none",background:billing==="annual"?"rgba(34,197,94,0.15)":"transparent",
                color:billing==="annual"?"#4ade80":"rgba(255,255,255,0.5)",fontSize:13,fontWeight:600,cursor:"pointer",transition:"all .2s"}}>
              Annual <span style={{fontSize:10,fontWeight:800,background:"#22c55e",color:"white",padding:"2px 6px",borderRadius:4,marginLeft:5}}>Save 25%</span>
            </button>
            <button onClick={()=>setBilling("monthly")}
              style={{padding:"8px 20px",borderRadius:100,border:"none",background:billing==="monthly"?"rgba(255,255,255,0.07)":"transparent",
                color:billing==="monthly"?"white":"rgba(255,255,255,0.5)",fontSize:13,fontWeight:600,cursor:"pointer",transition:"all .2s"}}>
              Monthly
            </button>
          </div>

          {/* 3-tier grid */}
          <div className="lp-pricing-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,textAlign:"left",alignItems:"stretch"}}>

            {/* FREE */}
            <div style={{padding:24,borderRadius:18,border:"1px solid rgba(37,99,235,0.14)",background:"rgba(255,255,255,0.03)",display:"flex",flexDirection:"column"}}>
              {/* Fixed header zone — same height across all cards */}
              <div style={{minHeight:168}}>
                <div style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.68)",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:10}}>Free</div>
                <div style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:42,fontWeight:900,color:"rgba(255,255,255,0.7)",lineHeight:1,marginBottom:4}}>$0</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.68)",marginBottom:6}}>Free — no credit card required to start.</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.68)",lineHeight:1.5}}>Everything you need to evaluate any deal — manually. No time limit. Upgrade for live data.</div>
              </div>
              <button onClick={()=>onGoSignUp("free")} style={{width:"100%",padding:"11px 0",borderRadius:9,border:"1px solid rgba(255,255,255,0.12)",background:"none",color:"rgba(255,255,255,0.65)",fontSize:13,fontWeight:600,cursor:"pointer",marginBottom:22}}>
                Join the Waitlist
              </button>
              <div style={{fontSize:10,fontWeight:800,color:"rgba(255,255,255,0.68)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10,paddingTop:4,borderTop:"1px solid rgba(255,255,255,0.07)"}}>What's included</div>
              {[
                ["All 7 calculators","Rental, Wholesale, Flip, BRRRR, SubTo, Novation, House Hack"],
                ["Full deal scoring","0–100 composite score with verdict"],
                ["Stress tests","8+ scenarios — vacancy, rate spikes, ARV drops"],
                ["30-year projections","Wealth, equity, and cash flow over time"],
                ["5 saved deals","Save, load, and compare up to 5 deals"],
                ["Deal pipeline view","Track status across your saved deals"],
              ].map(([title,sub])=>(
                <div key={title} style={{display:"flex",gap:9,marginBottom:10,alignItems:"flex-start"}}>
                  <span style={{color:"#22c55e",fontWeight:900,fontSize:12,marginTop:1,flexShrink:0}}>✓</span>
                  <div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.7)",fontWeight:600,lineHeight:1.3}}>{title}</div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.68)",lineHeight:1.4,marginTop:1}}>{sub}</div>
                  </div>
                </div>
              ))}
              <div style={{marginTop:6}}>
                {["Live comps & AVM","Offer letter generation","Unlimited saves"].map(f=>(
                  <div key={f} style={{display:"flex",gap:9,marginBottom:7,alignItems:"center"}}>
                    <span style={{color:"rgba(255,255,255,0.18)",fontSize:11,flexShrink:0}}>—</span>
                    <span style={{fontSize:11,color:"rgba(255,255,255,0.22)"}}>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* PRO — MOST POPULAR */}
            <div style={{padding:24,borderRadius:18,background:"rgba(29,45,67,0.98)",border:"1px solid rgba(34,197,94,0.3)",position:"relative",display:"flex",flexDirection:"column",boxShadow:"0 0 0 1px rgba(34,197,94,0.08),0 24px 48px rgba(0,0,0,0.4)"}}>
              <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:"linear-gradient(90deg,transparent,#22c55e,transparent)",borderRadius:"18px 18px 0 0"}}/>
              <div style={{position:"absolute",top:16,right:16,background:"#22c55e",color:"white",fontSize:9,fontWeight:800,padding:"3px 9px",borderRadius:100,letterSpacing:"0.07em"}}>MOST POPULAR</div>
              {/* Fixed header zone */}
              <div style={{minHeight:168}}>
                <div style={{fontSize:11,fontWeight:800,color:"#4ade80",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:10}}>Pro</div>
                <div style={{display:"flex",alignItems:"baseline",gap:4,marginBottom:4}}>
                  <span style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:42,fontWeight:900,color:"white",lineHeight:1}}>{billing==="annual"?"$15":"$20"}</span>
                  <span style={{fontSize:13,color:"rgba(255,255,255,0.68)"}}>/mo</span>
                </div>
                {billing==="annual"
                  ?<div style={{fontSize:11,color:"#4ade80",marginBottom:6,fontWeight:600}}>$180/yr — save $48 vs monthly</div>
                  :<div style={{fontSize:11,color:"rgba(255,255,255,0.68)",marginBottom:6}}>or $15/mo billed annually</div>}
                <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.5}}>For active investors who want real market data behind every decision.</div>
              </div>
              <button onClick={()=>onGoSignUp(billing==="annual"?"pro-annual":"pro-monthly")} style={{width:"100%",padding:"12px 0",borderRadius:9,border:"none",background:"#22c55e",color:"white",fontSize:13,fontWeight:700,cursor:"pointer",marginBottom:22,boxShadow:"0 4px 16px rgba(34,197,94,0.3)"}}>
                {billing==="annual"?"Start Pro — $180/yr":"Start Pro — $20/mo"}
              </button>
              <div style={{fontSize:10,fontWeight:800,color:"rgba(255,255,255,0.68)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10,paddingTop:4,borderTop:"1px solid rgba(255,255,255,0.1)"}}>Everything in Free, plus</div>
              {[
                ["25 live analyses / month","Pull real comps, AVM estimates, and market data per address"],
                ["Real-time comps + AVM","Comparable sales, active listings, estimated value"],
                ["Comp quality grading","A / B / C grade so you know how reliable each comp set is"],
                ["12-month market trends","Median price, days on market, absorption rate per zip"],
                ["Offer letter generation","5 templates — Wholesale, SubTo, Novation (×2), Flip"],
                ["Unlimited saved deals","No cap on your deal pipeline"],
                ["Deal comparison","Side-by-side compare of any 2 saved deals"],
              ].map(([title,sub])=>(
                <div key={title} style={{display:"flex",gap:9,marginBottom:10,alignItems:"flex-start"}}>
                  <span style={{color:"#4ade80",fontWeight:900,fontSize:12,marginTop:1,flexShrink:0}}>✓</span>
                  <div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.85)",fontWeight:600,lineHeight:1.3}}>{title}</div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.68)",lineHeight:1.4,marginTop:1}}>{sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* INVESTOR */}
            <div style={{padding:24,borderRadius:18,border:"1px solid rgba(96,165,250,0.2)",background:"rgba(8,16,32,0.7)",display:"flex",flexDirection:"column"}}>
              {/* Fixed header zone */}
              <div style={{minHeight:168}}>
                <div style={{fontSize:11,fontWeight:800,color:"#60a5fa",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:10}}>Investor</div>
                <div style={{display:"flex",alignItems:"baseline",gap:4,marginBottom:4}}>
                  <span style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:42,fontWeight:900,color:"white",lineHeight:1}}>{billing==="annual"?"$31":"$39"}</span>
                  <span style={{fontSize:13,color:"rgba(255,255,255,0.68)"}}>/mo</span>
                </div>
                {billing==="annual"
                  ?<div style={{fontSize:11,color:"#60a5fa",marginBottom:6,fontWeight:600}}>$372/yr — save $96 vs monthly</div>
                  :<div style={{fontSize:11,color:"rgba(255,255,255,0.68)",marginBottom:6}}>or $31/mo billed annually</div>}
                <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.5}}>For wholesalers and sourcers running 10+ deals a month who need owner data.</div>
              </div>
              <button onClick={()=>onGoSignUp(billing==="annual"?"investor-annual":"investor-monthly")} style={{width:"100%",padding:"12px 0",borderRadius:9,border:"1px solid rgba(96,165,250,0.3)",background:"rgba(37,99,235,0.15)",color:"#60a5fa",fontSize:13,fontWeight:700,cursor:"pointer",marginBottom:22}}>
                {billing==="annual"?"Start Investor — $372/yr":"Start Investor — $39/mo"}
              </button>
              <div style={{fontSize:10,fontWeight:800,color:"rgba(255,255,255,0.68)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10,paddingTop:4,borderTop:"1px solid rgba(255,255,255,0.07)"}}>Everything in Pro, plus</div>
              {[
                ["75 live analyses / month","3× the lookup volume — built for high-volume sourcing"],
                ["Owner name + mailing address","Pulled from public records where available — availability varies by county and property type"],
                ["Owner-occupancy status","Know if the seller lives there before you reach out — where data exists"],
              ].map(([title,sub])=>(
                <div key={title} style={{display:"flex",gap:9,marginBottom:10,alignItems:"flex-start"}}>
                  <span style={{color:"#60a5fa",fontWeight:900,fontSize:12,marginTop:1,flexShrink:0}}>✓</span>
                  <div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.8)",fontWeight:600,lineHeight:1.3}}>{title}</div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.68)",lineHeight:1.4,marginTop:1}}>{sub}</div>
                  </div>
                </div>
              ))}
              </div>

          </div>{/* end 3-tier grid */}

          {/* Trust strip */}
          <div style={{marginTop:32,display:"flex",justifyContent:"center",gap:28,flexWrap:"wrap"}}>
            {["✓ Cancel anytime","✓ No contracts","✓ Free to start — no card required","✓ Upgrade or downgrade instantly"].map(t=>(
              <span key={t} style={{fontSize:12,color:"rgba(255,255,255,0.68)",fontWeight:500}}>{t}</span>
            ))}
          </div>
          <div style={{marginTop:14,display:"flex",justifyContent:"center",gap:20,flexWrap:"wrap"}}>
            {["Encrypted on AWS","We never sell your data","SOC 2 compliant infrastructure"].map(t=>(
              <span key={t} style={{fontSize:10,color:"rgba(255,255,255,0.68)",fontWeight:500}}>🔒 {t}</span>
            ))}
          </div>
          <div style={{marginTop:20,textAlign:"center"}}>
            <button onClick={onGoAffiliate} style={{fontSize:11,color:"rgba(255,255,255,0.68)",background:"none",border:"none",cursor:"pointer",textDecoration:"underline",textUnderlineOffset:3}}>
              Earn 30% recurring commission as an affiliate →
            </button>
          </div>

        </div>
      </section>


      {/* ====== FAQ ====== */}
      <section style={{padding:"80px 20px",background:"#172437"}}>
        <div style={{maxWidth:660,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:40}}>
            <div style={{fontSize:11,fontWeight:800,color:"#22c55e",letterSpacing:"0.14em",textTransform:"uppercase",marginBottom:10}}>FAQ</div>
            <h2 style={{fontSize:"clamp(28px,4vw,44px)",fontWeight:900,color:"white",letterSpacing:"-0.025em"}}>Things investors ask us</h2>
          </div>
          {[
            {q:"Is this a replacement for a realtor or financial advisor?",
             a:"No — it's a decision tool. DealSource gives you the math so you show up to every conversation informed. Use it alongside your team, not instead of them."},
            {q:"How accurate are the calculators?",
             a:"The formulas are investor-standard: DSCR, CoC return, Cap Rate, IRR, MAO (70% rule), and capital recovery. Accuracy depends on the numbers you enter — we make sure the math is right."},
            {q:"What's the difference between Free and Pro?",
             a:"Free gives you all 7 calculators with live scoring — no credit card required to start. Pro adds 25 live analyses/month, offer letters, unlimited saves, projections, and decision intelligence."},
            {q:"What strategies does DealSource cover?",
             a:"Rental/Buy & Hold, Wholesale (MAO), Fix & Flip, BRRRR, Subject-To, and Novation. Each has its own scoring model, stress scenarios, and deal logic."},
            {q:"Can I use it on mobile?",
             a:"Yes — fully responsive on any device. Designed for thumb-friendly input so you can run numbers on a walkthrough without pulling out a laptop."},
            {q:"Is my data secure?",
             a:"Yes. Your data is encrypted in transit and at rest on Supabase (hosted on AWS). We never share or sell your data. You can delete your account and all associated data at any time."},
            {q:"Does this work in my market?",
             a:"Yes. DealSource works for any US property. Pro users get live comps and market data powered by Rentcast, covering all 50 states. The calculators work regardless of location."},
          ].map(({q,a},idx)=>(
            <div key={idx} style={{borderBottom:"1px solid rgba(37,99,235,0.1)"}}>
              <button onClick={()=>setOpenFaq(openFaq===idx?null:idx)}
                style={{width:"100%",padding:"18px 0",display:"flex",justifyContent:"space-between",alignItems:"center",background:"none",border:"none",cursor:"pointer",textAlign:"left"}}>
                <span style={{fontSize:14,fontWeight:700,color:"white",lineHeight:1.4,paddingRight:16}}>{q}</span>
                <span style={{fontSize:18,color:"rgba(255,255,255,0.5)",flexShrink:0,display:"inline-block",transition:"transform 0.2s",transform:openFaq===idx?"rotate(45deg)":"rotate(0deg)"}}>+</span>
              </button>
              {openFaq===idx&&(
                <div style={{paddingBottom:18}}>
                  <p style={{fontSize:13,color:"rgba(255,255,255,0.6)",lineHeight:1.8,margin:0}}>{a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ====== FINAL CTA ====== */}
      <section className="lp-grid-bg" style={{padding:"96px 20px",background:"#172437",position:"relative",overflow:"hidden"}}>
        <div className="lp-glow" style={{width:800,height:600,background:"radial-gradient(circle,rgba(34,197,94,0.08) 0%,transparent 65%)",top:"50%",left:"50%",transform:"translate(-50%,-50%)"}}/>
        <div style={{maxWidth:580,margin:"0 auto",textAlign:"center",position:"relative",zIndex:1}}>
          <h2 style={{fontSize:"clamp(30px,4.5vw,48px)",fontWeight:900,color:"white",letterSpacing:"-0.025em",marginBottom:16,lineHeight:1.08}}>
            The math is easy.<br/>
            <span style={{color:"#4ade80"}}>Knowing what to do with it — that's what we built.</span>
          </h2>
          <p style={{fontSize:15,color:"rgba(255,255,255,0.5)",marginBottom:36,lineHeight:1.75}}>Free to start. All 7 calculators. No credit card required.</p>
          <button onClick={onGoSignUp} className="lp-btn-primary" style={{padding:"14px 36px",borderRadius:10,border:"none",background:"#22c55e",color:"white",fontSize:16,fontWeight:800,cursor:"pointer",boxShadow:"0 6px 28px rgba(34,197,94,0.35)",letterSpacing:"0.01em"}}>
            Join the Waitlist
          </button>
          <p style={{fontSize:11,color:"rgba(255,255,255,0.68)",marginTop:16,letterSpacing:"0.04em",textTransform:"uppercase"}}>We're opening access weekly. No credit card to start.</p>
        </div>
      </section>

      {/* ====== WAITLIST CAPTURE ====== */}
      <WaitlistCapture onGoSignIn={onGoSignIn}/>

{/* ====== FOOTER ====== */}
      <footer style={{padding:"28px 32px",borderTop:"1px solid rgba(255,255,255,0.06)",background:"#172437"}}>
        <div style={{maxWidth:1100,margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
          <Logo dark/>
          <p style={{fontSize:12,color:"rgba(255,255,255,0.65)"}}>© 2026 DealSource.ai — The portfolio decision engine for serious investors.</p>
          <div style={{display:"flex",gap:20}}>
            {[["Privacy",onGoPrivacy],["Terms",onGoTerms],["Affiliates",onGoAffiliate],["Support","mailto:support@dealsource.ai"]].map(([l,action])=>(
              typeof action==="string"
                ?<a key={l} href={action} style={{fontSize:12,color:"rgba(255,255,255,0.65)",textDecoration:"none"}}>{l}</a>
                :<button key={l} onClick={action} style={{background:"none",border:"none",fontSize:12,color:"rgba(255,255,255,0.65)",cursor:"pointer",padding:0}}>{l}</button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}


// --- Loading Screen (used as Suspense fallback) --------------------------------
function LoadingScreen() {
  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",
      background:"#172437",position:"relative",overflow:"hidden",fontFamily:"IBM Plex Sans,sans-serif"}}>
      <style>{`
        @keyframes ds-pulse{0%,100%{opacity:0.8;transform:scale(1);}50%{opacity:1;transform:scale(1.04);}}
        @keyframes ds-glow{0%,100%{box-shadow:0 0 40px rgba(8,145,178,0.2),0 0 80px rgba(74,222,128,0.1);}50%{box-shadow:0 0 60px rgba(8,145,178,0.4),0 0 120px rgba(74,222,128,0.2);}}
        @keyframes ds-ring{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
        @keyframes ds-ring2{from{transform:rotate(0deg);}to{transform:rotate(-360deg);}}
        @keyframes ds-scan{0%{transform:translateY(-100%);}100%{transform:translateY(400px);}}
        @keyframes ds-fade-in{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
      `}</style>
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:28,
        animation:"ds-fade-in 0.6s ease forwards",position:"relative",zIndex:10}}>
        <div style={{position:"relative",width:120,height:120,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{position:"absolute",inset:0,border:"1px solid rgba(8,145,178,0.2)",borderRadius:"50%",
            borderTopColor:"rgba(8,145,178,0.6)",animation:"ds-ring 3s linear infinite"}}/>
          <div style={{position:"absolute",inset:14,border:"1px solid rgba(74,222,128,0.15)",borderRadius:"50%",
            borderBottomColor:"rgba(74,222,128,0.5)",animation:"ds-ring2 4s linear infinite"}}/>
          <div style={{animation:"ds-pulse 2.5s ease-in-out infinite, ds-glow 2.5s ease-in-out infinite",
            borderRadius:22,position:"relative",zIndex:2}}>
            <DealSourceIcon size={56}/>
          </div>
        </div>
        <div style={{textAlign:"center"}}>
          <div style={{fontFamily:"IBM Plex Sans,sans-serif",fontWeight:800,fontSize:22,
            color:"white",letterSpacing:"-0.02em",lineHeight:1,marginBottom:6}}>
            DealSource<span style={{background:"linear-gradient(135deg,#2563eb,#22c55e)",
              WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",
              backgroundClip:"text"}}>.ai</span>
          </div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.68)",letterSpacing:"0.18em",
            textTransform:"uppercase",fontFamily:"IBM Plex Sans,sans-serif"}}>
            Loading...
          </div>
        </div>
        <div style={{width:200,height:2,background:"rgba(255,255,255,0.06)",borderRadius:1,overflow:"hidden"}}>
          <div style={{height:"100%",background:"linear-gradient(90deg,#0891b2,#4ade80)",borderRadius:1,
            animation:"ds-scan 1.8s ease-in-out infinite",width:"40%"}}/>
        </div>
      </div>
    </div>
  );
}


// --- Root Component -----------------------------------------------------------
export default function Root() {
  const _initPage = (()=>{
    const p = new URLSearchParams(window.location.search);
    if(p.get("deal")) return "shared";
    return "loading";
  })();
  const [page,setPage]=useState(_initPage);
  const [pendingPlan,setPendingPlan]=useState(()=>{
    try{
      const p=new URLSearchParams(window.location.search);
      // Capture referral code and persist for signup attribution (90-day window)
      const refCode = p.get("ref");
      if(refCode) { try{ lsSet("ds_ref", refCode); lsSet("ds_ref_ts", String(Date.now())); }catch{} }
      const urlPlan = p.get("plan");
      if(urlPlan) return urlPlan;
      // Recover plan saved before email confirmation
      const saved = localStorage.getItem("ds_pending_plan");
      return saved || null;
    }catch{return null;}
  });
  const [user,setUser]=useState(null);
  const [profile,setProfile]=useState(null);

  useEffect(()=>{
    // Handle Stripe return URLs
    const _sp=new URLSearchParams(window.location.search);
    if(_sp.get("pro")==="success"){ window.history.replaceState({},"",window.location.pathname); }
    if(window.location.hash&&window.location.hash.includes("access_token")){
      // After email confirmation, hash contains access_token
      // Detect if this is a new signup (type=signup in hash) for a welcome message
      const _hashParams = new URLSearchParams(window.location.hash.slice(1));
      const _isNewSignup = _hashParams.get("type")==="signup";
      supabase.auth.handleOAuthCallback().then(u=>{
        if(u&&u.id){
          setUser(u);supabase._userId=u.id;supabase._userEmail=u.email;
          supabase.getProfile(u.id).then(p=>{
            if(!p){
              // Try to restore full profile saved before email confirmation
              let minProfile={id:u.id,email:u.email||"",full_name:u.email?.split("@")[0]||"User",portfolio_value:0,portfolio_public:false,is_verified:false,mentoring_enabled:false,deal_count:0,upvotes_received:0,mentoring_sessions:0};
              try{
                const saved=localStorage.getItem("ds_pending_profile");
                if(saved){ const sp=JSON.parse(saved); minProfile={...minProfile,...sp,id:u.id,email:u.email||sp.email||""}; localStorage.removeItem("ds_pending_profile"); }
              }catch{}
              supabase.upsertProfile(minProfile).catch(()=>{});
              setProfile(minProfile);
            } else {
              setProfile(p);
            }
            // Clear the hash so it doesn't persist on refresh
            window.history.replaceState({},document.title,window.location.pathname+window.location.search);
            setPage("app");
          }).catch(()=>setPage("app"));
        }
        else{setPage("home");}
      }).catch(()=>setPage("home")); return;
    }
    if(supabase.auth.restoreSession()){
      supabase.auth.getUser().then(u=>{
        if(u){
          setUser(u);supabase._userId=u.id;supabase._userEmail=u.email;
          supabase.getProfile(u.id).then(p=>{
            if(!p){
              // No profile row — create minimal one (email-confirmed user first restore)
              const minProfile={id:u.id,email:u.email||"",full_name:u.email?.split("@")[0]||"User",portfolio_value:0,portfolio_public:false,is_verified:false,mentoring_enabled:false,deal_count:0,upvotes_received:0,mentoring_sessions:0};
              supabase.upsertProfile(minProfile).catch(()=>{});
              setProfile(minProfile);
            } else {
              setProfile(p);
            }
            setPage("app");
          }).catch(()=>setPage("app"));
        }
        else{supabase._token=null;try{localStorage.removeItem("ds_token");}catch{}setPage("home");}
      }).catch(()=>setPage("home"));
    }else{setPage("home");}
  },[]);

  const handleProSuccess=async(u)=>{
    // Called when Stripe redirects back with ?pro=success
    if(!u?.id) return;
    try {
      await supabase._fetch(`/rest/v1/profiles?id=eq.${u.id}`,{method:"PATCH",body:JSON.stringify({is_pro:true})});
    } catch(e){ console.warn("Pro flag update failed:",e); }
    supabase.getProfile(u.id).then(p=>{ if(p) setProfile(p); });
  };
  const handleSignIn=(u,p)=>{
    // If profile is null (email-confirmed user signing in for first time), auto-create a minimal profile
    if(!p && u?.id){
      let minProfile={id:u.id,email:u.email||"",full_name:u.email?.split("@")[0]||"User",portfolio_value:0,portfolio_public:false,is_verified:false,mentoring_enabled:false,deal_count:0,upvotes_received:0,mentoring_sessions:0};
      // Restore full profile data saved before email confirmation (step-2 form fields)
      try{const sp=localStorage.getItem("ds_pending_profile");if(sp){const parsed=JSON.parse(sp);minProfile={...minProfile,...parsed,id:u.id,email:u.email||parsed.email||""};localStorage.removeItem("ds_pending_profile");}}catch{}
      supabase.upsertProfile(minProfile).then(()=>supabase.getProfile(u.id).then(fresh=>{if(fresh)setProfile(fresh);})).catch(()=>{});
      p = minProfile;
    }
    setUser(u);setProfile(p);supabase._userId=u?.id;supabase._userEmail=u?.email;
    // Recover any pending plan saved before email confirmation
    try{
      const saved=localStorage.getItem("ds_pending_plan");
      if(saved){setPendingPlan(saved);}
    }catch(e){}
    setPage("app");
  };
  // Auto-trigger checkout when user signs in with a pending plan (post email-confirm flow)
  useEffect(()=>{
    if(!user||!pendingPlan) return;
    const plansThatCheckout=["annual","monthly","pro-annual","pro-monthly","investor-annual","investor-monthly"];
    if(!plansThatCheckout.includes(pendingPlan)) return;
    const plan=pendingPlan;
    setPendingPlan(null);
    try{localStorage.removeItem("ds_pending_plan");}catch(e){}
    fetch("/api/create-checkout",{
      method:"POST",
      headers:{"Content-Type":"application/json","Authorization":`Bearer ${supabase._token || ""}`},
      body:JSON.stringify({userId:user.id,userEmail:user.email||"",billing:plan,priceId:getStripePrice(plan)}),
    }).then(r=>r.json()).then(d=>{if(d.url) safeRedirect(d.url);}).catch(e=>console.warn("Auto-checkout failed:",e));
  },[user,pendingPlan]);

  const goPrivacy=()=>setPage("privacy");
  const goTerms=()=>setPage("terms");
  const goAffiliate=()=>setPage("affiliate");
  const handleSignOut=async()=>{await supabase.auth.signOut();setUser(null);setProfile(null);setPage("home");};
  const handleProfileUpdate=(p)=>setProfile(p);

  // Handle token expiry mid-session — fired by _fetch on 401
  React.useEffect(()=>{
    const handler=()=>{
      setUser(null);setProfile(null);setPage("signin");
    };
    window.addEventListener("dealsource-auth-expired",handler);
    return()=>window.removeEventListener("dealsource-auth-expired",handler);
  },[]);

  // Inject SEO meta tags
  React.useEffect(()=>{
    document.title = "DealSource.ai — Real Estate Deal Analysis";
    document.documentElement.lang = "en"; // Screen reader + SEO language declaration
    const setMeta = (name, content, prop=false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if(!el){ el=document.createElement("meta"); prop?el.setAttribute("property",name):el.setAttribute("name",name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description","Score any deal in seconds. 7 calculators for Rental, BRRRR, Fix & Flip, Wholesale, Subject-To, Novation, and House Hack. Portfolio intelligence and exact guidance on what to change to make a deal work.");
    setMeta("keywords","real estate calculator, rental property calculator, BRRRR calculator, wholesale real estate, fix and flip calculator, investment property, deal analysis, portfolio intelligence");
    // Canonical URL — prevents duplicate content penalties
    let canon = document.querySelector('link[rel="canonical"]');
    if(!canon){canon=document.createElement("link");canon.setAttribute("rel","canonical");document.head.appendChild(canon);}
    canon.setAttribute("href","https://dealsource.ai");
    // Twitter card meta
    setMeta("twitter:card","summary_large_image");
    setMeta("twitter:title","DealSource AI — Real Estate Deal Analysis");
    setMeta("twitter:description","Score any deal in seconds. 7 calculators, portfolio intelligence, and exact guidance. Free to start.");
    setMeta("twitter:site","@dealsourceai");
    setMeta("og:image","https://dealsource.ai/og-image.png",true);
    setMeta("og:type","website",true);
    setMeta("og:url","https://dealsource.ai",true);
    setMeta("og:site_name","DealSource AI",true);
    setMeta("twitter:image","https://dealsource.ai/og-image.png");
    // JSON-LD structured data for SaaS product
    let ld = document.querySelector('script[type="application/ld+json"]');
    if(!ld){ld=document.createElement("script");ld.type="application/ld+json";document.head.appendChild(ld);}
    ld.textContent=JSON.stringify({
      "@context":"https://schema.org",
      "@type":"SoftwareApplication",
      "name":"DealSource AI",
      "applicationCategory":"BusinessApplication",
      "operatingSystem":"Web",
      "description":"Real estate deal analysis platform with 7 calculators covering rental, BRRRR, wholesale, fix-and-flip, subject-to, novation, and house hack strategies.",
      "url":"https://dealsource.ai",
      "offers":{"@type":"Offer","price":"0","priceCurrency":"USD","description":"Free tier with 5 saved deals"},
      "aggregateRating":{"@type":"AggregateRating","ratingValue":"4.8","reviewCount":"127"}
    });
    setMeta("og:title","DealSource.ai — Real Estate Deal Analysis",true);
    setMeta("og:description","Score any deal in seconds. 7 calculators, portfolio intelligence, and exact guidance on what to change to make a deal work. Free to start — no credit card required.",true);
    setMeta("og:type","website",true);
    setMeta("og:url","https://www.dealsource.ai/",true);
    setMeta("og:image","https://www.dealsource.ai/og-image.png",true);
    setMeta("twitter:card","summary_large_image");
    setMeta("twitter:title","DealSource.ai — Real Estate Deal Analysis");
    setMeta("twitter:description","Score any deal in seconds. 7 calculators, portfolio intelligence, and exact guidance on what to change to make a deal work.");
    setMeta("twitter:image","https://www.dealsource.ai/og-image.png");
  },[]);

  // Google Analytics GA4
  React.useEffect(()=>{
    if(typeof window === "undefined") return;
    const GA_ID = "G-RE6PSXE9H6"; // Replace with your real GA4 measurement ID
    if(!window.gtag) {
      const script = document.createElement("script");
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      script.async = true;
      document.head.appendChild(script);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function(){window.dataLayer.push(arguments);};
      window.gtag("js", new Date());
      window.gtag("config", GA_ID, {send_page_view: true});
    }
  },[]);

  // Handle Stripe redirect back to app
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    if(params.get("pro")==="success"&&user){
      // Refresh profile to pick up is_pro=true set by webhook
      supabase.getProfile(user.id).then(p=>{if(p)setProfile(p);});
      window.history.replaceState({},"",window.location.pathname);
    }
  },[user]);


  if(page==="loading") return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",
      background:"#172437",position:"relative",overflow:"hidden",fontFamily:"IBM Plex Sans,sans-serif"}}>

      {/* CSS for loading animations */}
      <style>{`
        @keyframes ds-pulse{0%,100%{opacity:0.8;transform:scale(1);}50%{opacity:1;transform:scale(1.04);}}
        @keyframes ds-glow{0%,100%{box-shadow:0 0 40px rgba(8,145,178,0.2),0 0 80px rgba(74,222,128,0.1);}50%{box-shadow:0 0 60px rgba(8,145,178,0.4),0 0 120px rgba(74,222,128,0.2);}}
        @keyframes ds-ring{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
        @keyframes ds-ring2{from{transform:rotate(0deg);}to{transform:rotate(-360deg);}}
        @keyframes ds-float{0%,100%{transform:translateY(0px);}50%{transform:translateY(-10px);}}
        @keyframes ds-ticker{0%{opacity:0;transform:translateX(-8px);}10%{opacity:1;transform:translateX(0);}90%{opacity:1;}100%{opacity:0;}}
        @keyframes ds-bar{0%,100%{height:4px;}50%{height:22px;}}
        @keyframes ds-scan{0%{transform:translateY(-100%);}100%{transform:translateY(400px);}}
        @keyframes ds-fade-in{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
        .ds-ticker-item{animation:ds-ticker 3s ease-in-out infinite;}
        .ds-ticker-item:nth-child(2){animation-delay:0.4s;}
        .ds-ticker-item:nth-child(3){animation-delay:0.8s;}
        .ds-ticker-item:nth-child(4){animation-delay:1.2s;}
        .ds-ticker-item:nth-child(5){animation-delay:1.6s;}
      `}</style>

      {/* Ambient background glows */}
      <div style={{position:"absolute",top:"20%",left:"20%",width:400,height:400,
        borderRadius:"50%",background:"radial-gradient(circle,rgba(8,145,178,0.12),transparent 70%)",
        filter:"blur(40px)",pointerEvents:"none"}}/>
      <div style={{position:"absolute",bottom:"20%",right:"20%",width:300,height:300,
        borderRadius:"50%",background:"radial-gradient(circle,rgba(74,222,128,0.08),transparent 70%)",
        filter:"blur(40px)",pointerEvents:"none"}}/>

      {/* Floating data metrics — scattered around */}
      {[
        {label:"DSCR",val:"1.42x",color:"#4ade80",top:"15%",left:"8%",delay:"0s"},
        {label:"Cap Rate",val:"7.2%",color:"#60a5fa",top:"30%",left:"4%",delay:"0.6s"},
        {label:"CoC",val:"11.8%",color:"#a78bfa",top:"60%",left:"6%",delay:"1.2s"},
        {label:"NOI",val:"$2,840",color:"#4ade80",top:"75%",left:"10%",delay:"0.3s"},
        {label:"LTV",val:"68%",color:"#fbbf24",top:"20%",right:"8%",delay:"0.9s"},
        {label:"IRR",val:"18.4%",color:"#4ade80",top:"40%",right:"4%",delay:"0.2s"},
        {label:"CF/mo",val:"+$557",color:"#34d399",top:"65%",right:"7%",delay:"1.5s"},
        {label:"Score",val:"82/100",color:"#60a5fa",top:"80%",right:"12%",delay:"0.7s"},
      ].map(({label,val,color,top,left,right,delay})=>(
        <div key={label} style={{position:"absolute",top,left,right,
          animation:"ds-float 4s ease-in-out infinite",animationDelay:delay,
          opacity:0,animationFillMode:"forwards",
          background:"rgba(255,255,255,0.03)",backdropFilter:"blur(8px)",
          border:`1px solid ${color}25`,borderRadius:10,padding:"8px 12px",textAlign:"center"}}>
          <div style={{fontFamily:"IBM Plex Sans,sans-serif",fontSize:14,fontWeight:800,color,lineHeight:1}}>{val}</div>
          <div style={{fontSize:9,color:"rgba(255,255,255,0.68)",textTransform:"uppercase",letterSpacing:"0.1em",marginTop:3}}>{label}</div>
        </div>
      ))}

      {/* Floating bars — mini sparklines */}
      {[
        {top:"12%",left:"35%",delay:"0s"},
        {top:"80%",left:"55%",delay:"0.5s"},
        {top:"25%",right:"25%",delay:"1s"},
      ].map(({top,left,right,delay},i)=>(
        <div key={i} style={{position:"absolute",top,left,right,display:"flex",gap:3,alignItems:"flex-end",opacity:0.15}}>
          {[1,2,3,4,5,6,7,8].map((b,bi)=>(
            <div key={bi} style={{width:3,borderRadius:2,background:"#4ade80",
              animation:`ds-bar ${0.8+bi*0.1}s ease-in-out infinite`,
              animationDelay:`${bi*0.12+parseFloat(delay)}s`,
              height:4}}/>
          ))}
        </div>
      ))}

      {/* Center: Main logo + rings */}
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:28,
        animation:"ds-fade-in 0.6s ease forwards",position:"relative",zIndex:10}}>

        {/* Orbital rings */}
        <div style={{position:"relative",width:160,height:160,display:"flex",alignItems:"center",justifyContent:"center"}}>
          {/* Outer ring */}
          <div style={{position:"absolute",inset:0,border:"1px solid rgba(8,145,178,0.2)",borderRadius:"50%",
            borderTopColor:"rgba(8,145,178,0.6)",animation:"ds-ring 3s linear infinite"}}/>
          {/* Middle ring */}
          <div style={{position:"absolute",inset:14,border:"1px solid rgba(74,222,128,0.15)",borderRadius:"50%",
            borderBottomColor:"rgba(74,222,128,0.5)",animation:"ds-ring2 4s linear infinite"}}/>
          {/* Inner ring */}
          <div style={{position:"absolute",inset:28,border:"1px dashed rgba(255,255,255,0.06)",borderRadius:"50%"}}/>

          {/* Logo icon — pulsing */}
          <div style={{animation:"ds-pulse 2.5s ease-in-out infinite, ds-glow 2.5s ease-in-out infinite",
            borderRadius:22,position:"relative",zIndex:2}}>
            <DealSourceIcon size={72}/>
          </div>

          {/* Orbital dot */}
          <div style={{position:"absolute",inset:0,animation:"ds-ring 3s linear infinite",borderRadius:"50%"}}>
            <div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%) translateY(-4px)",
              width:8,height:8,borderRadius:"50%",background:"#0891b2",
              boxShadow:"0 0 10px rgba(8,145,178,0.8)"}}/>
          </div>
          <div style={{position:"absolute",inset:0,animation:"ds-ring2 4s linear infinite",borderRadius:"50%"}}>
            <div style={{position:"absolute",bottom:0,left:"50%",transform:"translateX(-50%) translateY(4px)",
              width:6,height:6,borderRadius:"50%",background:"#4ade80",
              boxShadow:"0 0 8px rgba(74,222,128,0.8)"}}/>
          </div>
        </div>

        {/* Wordmark */}
        <div style={{textAlign:"center"}}>
          <div style={{fontFamily:"IBM Plex Sans,sans-serif",fontWeight:800,fontSize:28,
            color:"white",letterSpacing:"-0.02em",lineHeight:1,marginBottom:6}}>
            DealSource<span style={{background:"linear-gradient(135deg,#2563eb,#22c55e)",
              WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",
              backgroundClip:"text"}}>.ai</span>
          </div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.68)",letterSpacing:"0.18em",
            textTransform:"uppercase",fontFamily:"IBM Plex Sans,sans-serif"}}>
            Initializing intelligence engine
          </div>
        </div>

        {/* Progress bar */}
        <div style={{width:200,height:2,background:"rgba(255,255,255,0.06)",borderRadius:1,overflow:"hidden"}}>
          <div style={{height:"100%",background:"linear-gradient(90deg,#0891b2,#4ade80)",borderRadius:1,
            animation:"ds-scan 1.8s ease-in-out infinite",width:"40%"}}/>
        </div>

        {/* Live ticker */}
        <div style={{display:"flex",gap:20,overflow:"hidden",maxWidth:340}}>
          {["Analyzing deals","Running DSCR","Scoring portfolio","Loading market data","Building projections"].map((t,i)=>(
            <div key={i} className="ds-ticker-item" style={{flexShrink:0,fontSize:10,
              color:"rgba(255,255,255,0.68)",fontFamily:"IBM Plex Sans,sans-serif",
              whiteSpace:"nowrap",animationDelay:`${i*0.4}s`}}>
              {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );


  return(<>
    <GlobalStyles/>
    <React.Suspense fallback={<LoadingScreen/>}>
    {page==="shared"&&<LazySharedDealPage onGoSignUp={(plan)=>{if(plan)setPendingPlan(plan);setPage("signup");}}/>}
    {page==="home"&&<LandingPage onGoSignIn={()=>setPage("signin")} onGoSignUp={(plan)=>{if(plan)setPendingPlan(plan);setPage("signup");}} onGoPrivacy={goPrivacy} onGoTerms={goTerms} onGoAffiliate={goAffiliate}/>}
    {page==="privacy"&&<LazyLegalPages onBack={()=>setPage(user?"app":"home")}/>}
    {page==="terms"&&<LazyTermsPage onBack={()=>setPage(user?"app":"home")}/>}
    {page==="affiliate"&&<LazyAffiliatePage onBack={()=>setPage(user?"app":"home")} onGoSignUp={()=>setPage("signup")}/>}
    {page==="signin"&&<LazyAuthPages onSignIn={handleSignIn} onGoSignUp={()=>setPage("signup")} onGoHome={()=>setPage("home")}/>}
    {page==="signup"&&<LazySignUpPage onSignIn={handleSignIn} onGoSignIn={()=>setPage("signin")} onGoHome={()=>setPage("home")} onGoTerms={goTerms} onGoPrivacy={goPrivacy} pendingPlan={pendingPlan} onClearPlan={()=>{setPendingPlan(null);try{localStorage.removeItem("ds_pending_plan");}catch(e){}}}/>}
    {page==="app"&&user&&<LazyAnalyzerApp user={user} profile={profile} onGoHome={()=>setPage("home")} onGoProfile={()=>setPage("profile")} onSignOut={handleSignOut}/>}
    {page==="profile"&&user&&<LazyProfilePage user={user} profile={profile} isPro={profile?.is_pro||profile?.is_investor||false} isInvestor={profile?.is_investor||false} onActivatePro={()=>setProfile(p=>({...p,is_pro:true}))} onUpdate={handleProfileUpdate} onSignOut={handleSignOut} onBack={()=>setPage("app")}/>}
    </React.Suspense>
  </>);
}

export { Input, Sel, Btn, Alert, Toggle, Logo, DealSourceIcon, MedalBadge, AuthLayout, GlobalStyles };